import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  LayoutDashboard, ListChecks, Users, Building2, Share2, Crown, ShieldCheck,
  FileBarChart2, History, Settings, LogOut, Search, Bell, Plus, Filter,
  ChevronDown, ChevronRight, X, Edit3, Save, AlertTriangle,
  Calendar, User, ArrowRight, Eye, MessageSquare, Send,
  CheckCircle2, Clock, Lock, Mail, Download, Upload,
  GanttChart, Network, FileText, MoreVertical, Copy, RefreshCw, Loader2, Link as LinkIcon, GitMerge,
  ArrowUp, ArrowDown, LayoutGrid
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, LineChart, Line
} from 'recharts';

// ============================================================
// CONSTANTS — lookups that match dbo.departments / dbo.functions / dbo.tiers
// ============================================================
const DEPARTMENTS = ['Corporate','TRS','Finance','Infrastructure','Safety & Operations','Commercial','IT','People','Engagement'];
const TIERS = ['Trust','PLC'];
const FUNCTIONS = [
  'Resources - Rolling Stock','Finance','Infrastructure','Safety','Operation of railway',
  'Commercial','Governance','Business Systems','Fundraising','Employment issues','Security'
];

// ============================================================
// IMPACT TAXONOMY (Phase 7 Batch C)
//
// Two cross-cutting structured tags on every risk, distinct from
// the responsible Department:
//   - NYMR Goals (5):  the charitable objects the risk pushes against
//   - Impacted Departments (8):  the functional areas the risk hits
//
// Both lists are mirrored server-side in dbo.goals and
// dbo.impacted_departments. The fallbacks below are used only if
// /api/lookups fails on first load.
// ============================================================
const NYMR_GOALS = ['Safety', 'Education', 'Public benefit', 'Citizenship', 'Conservation'];
const IMPACTED_DEPARTMENTS = [
  'Finance & Fundraising', 'Corporate Services', 'Business Systems',
  'Safety & Operations', 'Civil Engineering', 'Mechanical Engineering',
  'Volunteering', 'HR'
];

// RAG thresholds — keep in sync with api/src/functions/reports.ts impact-goal report.
// Goals: 0=Grey, 1=Green, 2=Amber, 3+=Red
// Depts: 0=Grey, 1=Green, 2-3=Amber, 4+=Red
const goalsRag = (count) => {
  const n = count || 0;
  if (n === 0) return 'grey';
  if (n === 1) return 'green';
  if (n === 2) return 'amber';
  return 'red';
};
const impactedDeptsRag = (count) => {
  const n = count || 0;
  if (n === 0) return 'grey';
  if (n === 1) return 'green';
  if (n <= 3)  return 'amber';
  return 'red';
};
const RAG_CHIP = {
  grey:  'bg-stone-200 text-stone-700 border-stone-300',
  green: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  amber: 'bg-amber-100 text-amber-800 border-amber-300',
  red:   'bg-red-100 text-red-700 border-red-400'
};
const RAG_DOT = {
  grey:  'bg-stone-300',
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red:   'bg-red-600'
};
const RAG_LABEL = { grey: 'Not assessed', green: 'Narrow', amber: 'Moderate', red: 'Broad' };

// Combined impact-rank used for sorting in reports / tables.
// Higher = more cross-cutting. Score 4 = both indicators Red.
const impactRank = (goalsCount, deptsCount) => {
  const g = goalsRag(goalsCount), d = impactedDeptsRag(deptsCount);
  const v = (rag) => (rag === 'red' ? 2 : rag === 'amber' ? 1 : 0);
  return v(g) + v(d);
};

// Static knowledge base — descriptor text from RR_scoring_System.xlsx.
// Each NYMR Goal maps to its charitable object's themes, and each theme
// has a 1..5 Impact-level descriptor describing what an impact at that
// level means for that goal. Shown in the risk panel as contextual context
// for whichever goals the risk is tagged against.
const GOAL_IMPACT_DESCRIPTORS = {
  'Safety': {
    object: 'Charitable object 5 — Safety',
    blurb: 'Health & safety of employees, volunteers, contractors and the public.',
    themes: [
      {
        theme: 'Employee Health & Safety',
        levels: {
          1: 'RIDDOR-reportable injury (7 days lost time).',
          2: 'Multiple RIDDOR-reportable 7-day injuries; multiple minor injuries; or chronic health condition (7-day lost time, RIDDOR Schedule 3).',
          3: 'Single major injury (RIDDOR Schedule 1 & 2). Multiple major injuries OR single reportable disease.',
          4: 'Single fatality caused by a non-systematic failure. Multiple major injuries OR multiple reportable diseases.',
          5: 'Workforce fatality(s) caused by a systematic failure in work practices.'
        }
      },
      {
        theme: 'Employee Engagement',
        levels: {
          1: 'Short-term loss of morale, with poor performance of non-critical activities.',
          2: 'Minor disengagement. Effectiveness/efficiency compromised in non-critical activities.',
          3: 'Some disengagement leading to compromised effectiveness/efficiency in some critical activities.',
          4: 'Major downturn in company-wide engagement leading to service failures within some critical activities.',
          5: 'Significant downturn in company-wide engagement. Serious failings across most services.'
        }
      },
      {
        theme: 'Accident to passengers or public',
        levels: {
          1: 'Minor injury to visitor, volunteer or staff member.',
          2: 'RIDDOR-reportable injury (7 days lost time) OR multiple minor injuries.',
          3: 'Single major injury (RIDDOR Schedule 1 and Schedule 2 Part 5).',
          4: 'Single fatality OR multiple major injuries.',
          5: 'Major train or station accident with multi-fatality potential.'
        }
      }
    ]
  },
  'Education': {
    object: 'Charitable object 1 — Education through the operation of a living museum',
    blurb: 'Reputation, user experience and finance impacts on the educational mission.',
    themes: [
      {
        theme: 'Reputation',
        levels: {
          1: 'Adverse local stakeholder reaction such as single complainants about noise or crossing misuse.',
          2: 'Adverse local or enthusiast press reports over a period. Localised stakeholder concern; multiple complaints about the same issue.',
          3: 'Significant local and/or enthusiast press and/or regional reports. National media interest creating public concern.',
          4: 'Extensive prolonged adverse national reporting and public disputes with key stakeholders. Possible threat to charitable status.',
          5: 'Extensive and prolonged negative reporting nationally or public disputes. Escalation to external bodies inevitable & impossible to contain in medium term. Potential significant consequences.'
        }
      },
      {
        theme: 'User Experience',
        levels: {
          1: 'Planned disruption or facilities temporarily unavailable.',
          2: 'Unplanned disruption or longer-term (3 days or more) facilities unavailable.',
          3: 'Unplanned disruption for up to a week.',
          4: 'Unplanned disruption for up to a month, or facilities for volunteers/users withdrawn long term.',
          5: 'All users experience prolonged and unplanned disruption. Volunteers no longer able to visit. Educational facilities withdrawn permanently.'
        }
      },
      {
        theme: 'Finance',
        levels: {
          1: 'Up to £1,000 financial impact (failure to meet planned targets).',
          2: 'Up to £50,000 financial impact.',
          3: 'Up to £500,000 financial impact.',
          4: 'Up to £1M financial impact.',
          5: '£1M to £5M+ financial impact.'
        }
      }
    ]
  },
  'Public benefit': {
    object: 'Charitable object 2 — Heritage preservation & operation for public benefit',
    blurb: 'Asset & service availability and public-facing reputation.',
    themes: [
      {
        theme: 'Public benefit',
        levels: {
          1: 'Some issues with fleet availability. More than planned diesel substitution. Some volunteer discontent.',
          2: 'Fleet issues endemic. Service impacts noticeable and affecting visitor numbers. Volunteers increasingly concerned.',
          3: 'Availability of fleet restricted. Railway operating hand-to-mouth. Increasing demands on volunteers. Some permanent staff laid off.',
          4: 'Service reduced to bare minimum. Visitor numbers declining fast. Future of charity in doubt.',
          5: 'Railway closed with staff laid off and assets dispersed. No opportunities for volunteers. Charity being wound up.'
        }
      },
      {
        theme: 'Reputation (heritage assets)',
        levels: {
          1: 'Complaints from stakeholders and/or funders about state of assets.',
          2: 'Stakeholders/funders require demonstrable improvements; possible legal notices. Some adverse local media coverage.',
          3: 'Adverse media coverage and pressure from owners. Some owners signalling withdrawal of assets, with potential consequential financial impacts.',
          4: 'Facing enforcement action. Strong and repeated adverse comment in local media and enthusiast press.',
          5: 'Facing prosecution for legal breach of contract or breach of planning laws — significant adverse reputational hit. No longer able to raise funds.'
        }
      }
    ]
  },
  'Citizenship': {
    object: 'Charitable object 3 — Citizenship, community development & individual wellbeing',
    blurb: 'Volunteer welfare, training and skills development.',
    themes: [
      {
        theme: 'Citizenship & wellbeing',
        levels: {
          1: 'Some adverse social media commentary. Managers identify isolated volunteer concerns.',
          2: 'High-profile individual(s) publicly leave the railway citing treatment as the primary cause. Little or no skills training on offer.',
          3: 'Training of staff, volunteers and outreach severely curtailed. Lack of training impacting ability to deliver planned train service / commercial offering. Some media commentary.',
          4: 'NYMR volunteers leaving in measurable numbers. Considerable adverse social media. Stakeholder concern; suggestions funding will be withheld pending improvements.',
          5: 'Volunteers leaving in significant numbers. Considerable adverse mainstream and social media. Interest from regulators and HRA over concern about safe running of the railway.'
        }
      }
    ]
  },
  'Conservation': {
    object: 'Charitable object 4 — Conservation of flora, fauna and species in the lineside estate',
    blurb: 'Environmental performance and biodiversity.',
    themes: [
      {
        theme: 'Environment',
        levels: {
          1: 'Environmental impact requiring notification to an environmental regulator.',
          2: 'Repeated environmental impacts of the same type or due to similar root causes — indicating systematic failure.',
          3: 'Single environmental impact causing a change in classification (e.g. of a watercourse, ground water or conservation area). Enforcement action for pollution or activity without necessary permissions.',
          4: 'Multiple environmental impacts from one location/work site causing classification changes. Enforcement action for pollution.',
          5: 'Impact causing very long-term environmental damage. Prosecution for environmental offence. Protests / lobbying on environmental performance.'
        }
      }
    ]
  }
};

const ROLE_DESCRIPTIONS = {
  admin: 'Full access. Can manage users, all risks, all reports.',
  risk_owner: 'Standard user. Edits risks they own/review. Tier visibility controlled by checkboxes.',
  readonly: 'Read-only access. Cannot edit anything.'
};

// ============================================================
// API HELPERS
// ============================================================
const API_BASE = '/api';

/** Common fetch wrapper — handles 401 by redirecting to AAD login,
 *  surfaces other errors to the caller. */
async function apiFetch(path, init = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {})
    },
    ...init
  });
  if (res.status === 401) {
    // SWA's responseOverrides should already redirect, but if for any reason
    // we get a JSON 401 from the API itself, send the user to login.
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
    throw new Error('Not authenticated');
  }
  if (!res.ok) {
    let detail;
    try { detail = (await res.json()).error; } catch { detail = res.statusText; }
    const err = new Error(detail || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// ============================================================
// SHAPE MAPPING — DB row ↔ prototype's compact `r.*` shape
// The prototype's UI was designed against short field names;
// the API returns full DB column names. These helpers translate
// in both directions so the prototype components stay untouched.
// ============================================================

const upnToName = (upn) => {
  if (!upn) return '';
  const local = String(upn).split('@')[0];
  if (!local) return upn;
  return local.split(/[._-]/).filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
};

const isoToYmd = (v) => {
  if (!v) return 'Ongoing';
  if (typeof v === 'string') return v.slice(0, 10);
  try { return new Date(v).toISOString().slice(0, 10); } catch { return 'Ongoing'; }
};

const dbToProto = (r) => ({
  id: r.id,
  serial: r.serial || '',
  fn: r.function_category || '',
  dept: r.department || '',
  desc: r.description || '',
  // Display-only owner/reviewer text. Prefer the resolved name from the
  // structured UPN (the canonical source). Fall back to legacy unstructured
  // text only when no structured owner exists, then to the raw UPN. This
  // ordering ensures Phase 3 import bleed-through (where unstructured fields
  // sometimes captured impact text) cannot override a valid structured owner.
  owner: upnToName(r.owner_upn) || r.unstructured_owner_text || r.owner_upn || '',
  reviewer: upnToName(r.reviewer_upn) || r.unstructured_reviewer_text || r.reviewer_upn || '',
  // Keep raw UPNs around — the "My Risks" filter and the save path need them.
  ownerUpn: r.owner_upn || null,
  reviewerUpn: r.reviewer_upn || null,
  unstructured_owner_text: r.unstructured_owner_text || null,
  unstructured_reviewer_text: r.unstructured_reviewer_text || null,
  L: r.likelihood ?? null,
  I: r.impact ?? null,
  s: r.score ?? (r.likelihood && r.impact ? r.likelihood * r.impact : null),
  rL: r.residual_likelihood ?? null,
  rI: r.residual_impact ?? null,
  rs: r.residual_score ?? (r.residual_likelihood && r.residual_impact ? r.residual_likelihood * r.residual_impact : null),
  ctrl: r.controls || '',
  gaps: r.gaps || '',
  td: isoToYmd(r.target_date),
  tStatus: r.target_status || (r.target_date ? 'date' : 'ongoing'),
  tier: r.tier || 'Department',
  is_archived: !!r.is_archived,
  source_register: r.source_register || null,
  source_serial: r.source_serial || null,
  // "Implementation of action" — who carries out the mitigation. Free text in
  // the DB (corporate register column S) so legacy role titles survive.
  impl: r.implementation_text || '',
  // The PERSON carrying the implementation (picked directly, or the holder of
  // the title above). null = nobody linked.
  implUpn: r.implementation_upn || null,
  implName: r.implementation_name || '',
  // Phase 7 Batch C — structured impact taxonomy from join tables
  goals: Array.isArray(r.goals) ? r.goals : [],
  impacted_departments: Array.isArray(r.impacted_departments) ? r.impacted_departments : [],
  created_at: r.created_at || null,
  updated_at: r.updated_at || null
});

/** Reverse mapping for POST/PUT bodies. We pass through the UPNs unchanged
 *  (changing them is a Phase 6 concern — we don't have an Entra picker yet).
 *  If the displayed `owner` text differs from the UPN-derived display, we
 *  treat it as updated unstructured text. */
const protoToDb = (p) => {
  const ownerDisplayFromUpn = upnToName(p.ownerUpn);
  const reviewerDisplayFromUpn = upnToName(p.reviewerUpn);
  const ownerEdited = p.owner && p.owner !== ownerDisplayFromUpn && p.owner !== p.ownerUpn;
  const reviewerEdited = p.reviewer && p.reviewer !== reviewerDisplayFromUpn && p.reviewer !== p.reviewerUpn;
  return {
    serial: p.serial,
    function_category: p.fn || null,
    department: p.dept,
    description: p.desc,
    owner_upn: p.ownerUpn || null,
    reviewer_upn: p.reviewerUpn || null,
    likelihood: p.L ?? null,
    impact: p.I ?? null,
    controls: p.ctrl || null,
    gaps: p.gaps || null,
    target_date: !p.td || p.td === 'Ongoing' ? null : p.td,
    target_status: p.tStatus || (p.td && p.td !== 'Ongoing' ? 'date' : 'ongoing'),
    residual_likelihood: p.rL ?? null,
    residual_impact: p.rI ?? null,
    tier: p.tier,
    implementation_text: (p.impl && String(p.impl).trim()) ? String(p.impl).trim().slice(0, 500) : null,
    // implUpn undefined = a TITLE was picked → let the server resolve the
    // title's holder; null = nobody; string = a person picked by name.
    ...(p.implUpn !== undefined ? { implementation_upn: p.implUpn || null } : {}),
    unstructured_owner_text: ownerEdited ? p.owner : (p.unstructured_owner_text || null),
    unstructured_reviewer_text: reviewerEdited ? p.reviewer : (p.unstructured_reviewer_text || null),
    // Phase 7 Batch C — structured impact taxonomy. The API replaces the
    // join rows wholesale with whatever array is sent (empty = clear).
    goals: Array.isArray(p.goals) ? p.goals : [],
    impacted_departments: Array.isArray(p.impacted_departments) ? p.impacted_departments : []
  };
};

/** Summarise a user's edits in plain English so the audit log says what
 *  actually changed (e.g. "Score 12→16. Residual L 3→2. Controls amended.")
 *  rather than the generic default. Compares two proto-shaped risks (before/after).
 *  Returns null if nothing significant changed. Used as the `outline` on PUT /api/risks. */
function summarizeRiskDiff(before, after) {
  if (!before || !after) return null;
  const parts = [];
  const wasL = before.L ?? null, wasI = before.I ?? null;
  const isL = after.L ?? null, isI = after.I ?? null;
  const wasS = (wasL && wasI) ? wasL * wasI : null;
  const isS = (isL && isI) ? isL * isI : null;
  if (wasS !== isS) parts.push(`Score ${wasS ?? '—'}→${isS ?? '—'}`);
  else if (wasL !== isL || wasI !== isI) parts.push(`L/I ${wasL ?? '—'}×${wasI ?? '—'}→${isL ?? '—'}×${isI ?? '—'}`);

  const wasRL = before.rL ?? null, wasRI = before.rI ?? null;
  const isRL = after.rL ?? null, isRI = after.rI ?? null;
  const wasRS = (wasRL && wasRI) ? wasRL * wasRI : null;
  const isRS = (isRL && isRI) ? isRL * isRI : null;
  if (wasRS !== isRS) parts.push(`Residual ${wasRS ?? '—'}→${isRS ?? '—'}`);
  else if (wasRL !== isRL || wasRI !== isRI) parts.push(`Residual L/I ${wasRL ?? '—'}×${wasRI ?? '—'}→${isRL ?? '—'}×${isRI ?? '—'}`);

  if ((before.desc || '') !== (after.desc || '')) parts.push('Description amended');
  if ((before.ctrl || '') !== (after.ctrl || '')) parts.push('Controls amended');
  if ((before.gaps || '') !== (after.gaps || '')) parts.push('Gaps amended');
  if ((before.fn || '') !== (after.fn || '')) parts.push(`Function ${before.fn || '—'}→${after.fn || '—'}`);
  if ((before.dept || '') !== (after.dept || '')) parts.push(`Department ${before.dept || '—'}→${after.dept || '—'}`);
  if ((before.tier || '') !== (after.tier || '')) parts.push(`Tier ${before.tier || '—'}→${after.tier || '—'}`);
  if ((before.ownerUpn || '') !== (after.ownerUpn || '')) parts.push(`Owner → ${after.ownerUpn || '—'}`);
  if ((before.reviewerUpn || '') !== (after.reviewerUpn || '')) parts.push(`Reviewer → ${after.reviewerUpn || '—'}`);
  if ((before.impl || '').trim() !== (after.impl || '').trim()) parts.push(`Implementation → ${(after.impl || '').trim() || '—'}`);
  if ((before.td || '') !== (after.td || '')) parts.push(`Target ${before.td || '—'}→${after.td || '—'}`);

  const beforeGoals = (before.goals || []).slice().sort().join(',');
  const afterGoals = (after.goals || []).slice().sort().join(',');
  if (beforeGoals !== afterGoals) parts.push('Goals tagged');

  const beforeDepts = (before.impacted_departments || []).slice().sort().join(',');
  const afterDepts = (after.impacted_departments || []).slice().sort().join(',');
  if (beforeDepts !== afterDepts) parts.push('Impacted depts tagged');

  if (parts.length === 0) return null;
  return parts.join('. ').slice(0, 500);
}

// ============================================================
// VISUAL HELPERS
// ============================================================
const scoreColor = (score) => {
  if (!score) return 'bg-stone-200 text-stone-700';
  if (score >= 16) return 'bg-red-600 text-white';
  if (score >= 10) return 'bg-orange-500 text-white';
  if (score >= 5) return 'bg-amber-300 text-stone-900';
  return 'bg-emerald-500 text-white';
};
const scoreLabel = (score) => {
  if (!score) return '—';
  if (score >= 16) return 'Critical';
  if (score >= 10) return 'High';
  if (score >= 5) return 'Medium';
  return 'Low';
};
const scoreBorder = (score) => {
  if (!score) return 'border-stone-300';
  if (score >= 16) return 'border-red-600';
  if (score >= 10) return 'border-orange-500';
  if (score >= 5) return 'border-amber-400';
  return 'border-emerald-500';
};
const isOverdue = (td) => {
  if (!td || td === 'Ongoing') return false;
  try { return new Date(td) < new Date(); } catch { return false; }
};
const fmtDate = (d) => {
  if (!d || d === 'Ongoing') return 'Ongoing';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};

// `myRisks` matches by UPN. Default mode is 'owner' (risks I am directly
// responsible for). 'all' adds risks where I am the reviewer too.
const myRisks = (risks, user, mode = 'owner') => {
  if (!user) return [];
  if (mode === 'all') {
    return risks.filter(r => r.ownerUpn === user.upn || r.reviewerUpn === user.upn);
  }
  return risks.filter(r => r.ownerUpn === user.upn);
};

// Risks where the user is the person implementing the action — picked
// directly on the form, or via an "Implementation of action" title they hold
// (dbo.risks.implementation_upn). Drives the "My Implementation of Action
// List" view and the dashboard pill of the same name.
const myImplementationRisks = (risks, user) => {
  if (!user?.upn) return [];
  const me = user.upn.toLowerCase();
  return risks.filter(r => (r.implUpn || '').toLowerCase() === me);
};

// Suggest the next sequential serial number from the existing register.
// Looks at the numeric portion of every existing serial (so '12A' counts as 12)
// and returns max+1 as a string. New risks default to this; users can still
// override (e.g. type '33A' for a sub-risk).
const nextSerial = (risks) => {
  const nums = risks
    .map(r => {
      const m = String(r.serial || '').match(/^\d+/);
      return m ? parseInt(m[0], 10) : NaN;
    })
    .filter(n => Number.isFinite(n));
  return String((nums.length ? Math.max(...nums) : 0) + 1);
};

// When admin mode is OFF, override the admin's role to a non-admin one so the
// UI gates apply. Server-side RLS still treats them as admin — this is a UX
// affordance for senior users to preview what their colleagues see.
//
// We PRESERVE the user's actual T/P/E flags so the preview matches what
// they'd see as a non-admin user with their existing tier breadth (e.g.
// Chris is admin + T+P+E — with admin off he should still see tier risks
// because his tier flags are real). To preview as a basic dept-only user,
// uncheck T/P/E in your own user record.
const applyAdminMode = (user, adminModeOn) => {
  if (!user) return user;
  if (user.role !== 'admin' || adminModeOn) return user;
  return {
    ...user,
    role: 'risk_owner',
    _adminModeOff: true
  };
};

// Mirror of server-side RLS for client-side preview. Phase 7 Batch E model:
// - admin sees everything
// - Department-tier risks: visible if user.department = risk.dept
//   OR user has any T/P/E flag (cross-dept access for senior users)
//   OR user is owner/reviewer
// - Trust-tier risks: visible if user.canSeeTrust OR owner/reviewer
// - PLC-tier risks: visible if user.canSeePlc OR owner/reviewer
// - Exec-tier risks: visible if user.canSeeExec OR owner/reviewer
const visibleRisks = (risks, user) => {
  if (!user) return [];
  if (user.role === 'admin') return risks;
  const ownReview = (r) => r.ownerUpn === user.upn || r.reviewerUpn === user.upn;
  const hasAnyTierFlag = !!user.canSeeTrust || !!user.canSeePlc || !!user.canSeeExec;
  return risks.filter(r => {
    if (ownReview(r)) return true;
    const tier = r.tier;
    if (tier === 'Department') return r.dept === user.department || hasAnyTierFlag;
    if (tier === 'Trust')      return !!user.canSeeTrust;
    if (tier === 'PLC')        return !!user.canSeePlc;
    if (tier === 'Exec')       return !!user.canSeeExec;
    return false;
  });
};

// ============================================================
// MAIN APP
// ============================================================
export default function RiskRegisterApp() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [risks, setRisks] = useState([]);
  const [amendments, setAmendments] = useState([]);
  const [directory, setDirectory] = useState([]);
  // Lookups (departments + functions) start from hardcoded fallbacks; the
  // API populates them on load. After admin add/rename, LookupManager fires
  // refreshLookups() to keep the dropdowns fresh without a full reload.
  const [lookupDepts, setLookupDepts] = useState(DEPARTMENTS);
  const [lookupFns, setLookupFns]     = useState(FUNCTIONS);
  // Phase 7 Batch C — impact taxonomy lookups, sourced from /api/lookups.
  const [lookupGoals, setLookupGoals]               = useState(NYMR_GOALS);
  const [lookupImpDepts, setLookupImpDepts]         = useState(IMPACTED_DEPARTMENTS);
  // "Implementation of action" titles in use: [{ title, risk_count, active_count, holder_upn, holder_name, is_person_name }]
  const [lookupImplTitles, setLookupImplTitles]     = useState([]);
  const [view, setView] = useState('dashboard');
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [cloneFrom, setCloneFrom] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ dept: 'all', score: 'all', residual: 'all', tier: 'all', goal: 'all', impactedDept: 'all', overdue: false, incomplete: false, name: '', myOnly: false });
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // ---- Phase 7 UI state ----
  // Admin Mode: when true (default), admins see/do everything. When false,
  // their role is downgraded to risk_owner client-side so they see what a
  // standard dept user would see. Pure UX — server still permits everything.
  const [adminModeOn, setAdminModeOn] = useState(true);
  // Multi-select dept filter for the "By Department" view. Empty set = all.
  const [deptFilter, setDeptFilter] = useState(new Set());
  // My Risks: 'owner' (default) shows only risks I own. 'all' adds reviewer.
  const [myRisksMode, setMyRisksMode] = useState('owner');
  // Notifications dropdown
  const [notifOpen, setNotifOpen] = useState(false);
  // Server-side notifications inbox (filtered to current user by API)
  const [apiNotifs, setApiNotifs] = useState([]);
  const loadNotifications = useCallback(async () => {
    try {
      const d = await apiFetch('/notifications');
      setApiNotifs(d.notifications || []);
    } catch (e) {
      // notifications are non-critical - never let them block the app
      // eslint-disable-next-line no-console
      console.warn('notification load failed:', e?.message);
    }
  }, []);
  const markNotifRead = useCallback(async (id) => {
    setApiNotifs(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    try {
      await apiFetch(`/notifications/${id}/read`, { method: 'POST' });
    } catch (e) {
      // best effort - reload to recover
      loadNotifications();
    }
  }, [loadNotifications]);
  const markAllNotifsRead = useCallback(async () => {
    setApiNotifs(prev => prev.map(n => n.read_at ? n : { ...n, read_at: new Date().toISOString() }));
    try {
      await apiFetch('/notifications/read-all', { method: 'POST' });
    } catch (e) {
      loadNotifications();
    }
  }, [loadNotifications]);
  // Load notifications on mount and refresh whenever risks reload
  useEffect(() => { loadNotifications(); }, [loadNotifications]);
  // Toast for feedback (sync, archive, etc.)
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, kind = 'info') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ---- Initial load: risks (active+archived), then amendments. ----
  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setLoadError(null);
    try {
      const risksData = await apiFetch('/risks?archived=1');
      setCurrentUser(risksData.user);
      setRisks(risksData.risks.map(dbToProto));
      // Active-user directory for owner/reviewer dropdowns. Best-effort.
      apiFetch('/directory').then(d => setDirectory(d.users || [])).catch(() => {});
      // Lookups (departments + functions). Best-effort — fall back to constants.
      apiFetch('/lookups').then(d => {
        if (d.departments?.length) setLookupDepts(d.departments.map(x => x.name));
        if (d.functions?.length)   setLookupFns(d.functions.map(x => x.name));
        if (d.goals?.length)       setLookupGoals(d.goals.map(x => x.name));
        if (d.impacted_departments?.length) setLookupImpDepts(d.impacted_departments.map(x => x.name));
        if (Array.isArray(d.implementation_titles)) setLookupImplTitles(d.implementation_titles);
      }).catch(() => {});
      // Audit pull is best-effort — not all roles can see entries; absence shouldn't break the app.
      try {
        const auditData = await apiFetch('/audit?limit=200');
        setAmendments(auditData.entries || []);
      } catch (auditErr) {
        // eslint-disable-next-line no-console
        console.warn('audit fetch failed:', auditErr.message);
      }
    } catch (err) {
      setLoadError(err.message || 'Failed to load risks.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Deep links: #risk-<id> — what the detail panel's "Copy link" button writes
  // and what notification emails point at. Opens the risk once risks are
  // loaded; each distinct hash is handled once so a later data refresh does
  // not re-open a panel the user has closed.
  const handledHash = useRef('');
  useEffect(() => {
    if (loading || !risks.length) return;
    const openFromHash = () => {
      const hash = window.location.hash || '';
      if (hash === handledHash.current) return;
      const m = /^#risk-(\d+)$/.exec(hash);
      if (!m) return;
      const target = risks.find(r => r.id === Number(m[1]));
      if (!target) return;
      handledHash.current = hash;
      setSelectedRisk(target);
      setEditMode(false);
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, [loading, risks]);

  // ---- Mutations: write through API, then refresh local state. ----
  const saveRisk = useCallback(async (updated, original) => {
    setSaving(true);
    try {
      const body = protoToDb(updated);
      const summary = summarizeRiskDiff(original, updated);
      if (summary) body.outline = summary;
      const result = await apiFetch(`/risks/${updated.id}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      const next = result.risk ? dbToProto(result.risk) : updated;
      setRisks(prev => prev.map(r => r.id === next.id ? next : r));
      setSelectedRisk(next);
      setEditMode(false);
      // Refresh audit silently so the change shows up.
      apiFetch('/audit?limit=200').then(d => setAmendments(d.entries || [])).catch(() => {});
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, []);

  const createRisk = useCallback(async (draft) => {
    setSaving(true);
    try {
      const body = protoToDb(draft);
      const result = await apiFetch('/risks', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      if (result.risk) {
        const created = dbToProto(result.risk);
        setRisks(prev => [created, ...prev]);
        setSelectedRisk(created);
      }
      setShowAdd(false);
      apiFetch('/audit?limit=200').then(d => setAmendments(d.entries || [])).catch(() => {});
    } catch (err) {
      alert(`Create failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, []);

  // Merge — see db/22_merge_risks.sql + api/src/functions/risksMerge.ts.
  // Caller passes the target risk id and an array of source ids; the
  // server validates everything, max's L/I/rL/rI, unions goals/depts,
  // appends a "MERGED DATA {datetime}" status update, archives sources.
  const mergeRisksApi = useCallback(async (targetId, sourceIds) => {
    setSaving(true);
    try {
      await apiFetch(`/risks/${targetId}/merge`, {
        method: 'POST',
        body: JSON.stringify({ source_ids: sourceIds })
      });
      // Refresh both risks (target now has new scores) and the journal
      await loadAll(true);
      // Reselect the target so the user sees the updated detail
      const fresh = (await apiFetch('/risks')).risks?.find?.(r => r.id === targetId);
      if (fresh) setSelectedRisk(dbToProto(fresh));
      setMergeTarget(null);
      setToast({ kind: 'success', msg: `Merged ${sourceIds.length} risk(s).` });
      setTimeout(() => setToast(null), 4000);
    } catch (err) {
      setToast({ kind: 'error', msg: `Merge failed: ${err.message}` });
      setTimeout(() => setToast(null), 6000);
    } finally {
      setSaving(false);
    }
  }, [loadAll]);

  // Archive / restore. The API endpoints already exist (Phase 4):
  // POST /api/risks/{id}/archive  and  POST /api/risks/{id}/restore.
  // Archived risks are filtered out of all active views and the dashboard
  // stats; they remain visible under the "Archive" sidebar entry.
  const archiveRisk = useCallback(async (id) => {
    setSaving(true);
    try {
      const result = await apiFetch(`/risks/${id}/archive`, { method: 'POST' });
      const next = result.risk ? dbToProto(result.risk) : null;
      if (next) {
        setRisks(prev => prev.map(r => r.id === next.id ? next : r));
        setSelectedRisk(next);
      }
      setEditMode(false);
      showToast('Risk archived. It will not appear in active scoring.', 'success');
      apiFetch('/audit?limit=200').then(d => setAmendments(d.entries || [])).catch(() => {});
    } catch (err) {
      showToast(`Archive failed: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  }, [showToast]);

  const restoreRisk = useCallback(async (id) => {
    setSaving(true);
    try {
      const result = await apiFetch(`/risks/${id}/restore`, { method: 'POST' });
      const next = result.risk ? dbToProto(result.risk) : null;
      if (next) {
        setRisks(prev => prev.map(r => r.id === next.id ? next : r));
        setSelectedRisk(next);
      }
      showToast('Risk restored from archive.', 'success');
      apiFetch('/audit?limit=200').then(d => setAmendments(d.entries || [])).catch(() => {});
    } catch (err) {
      showToast(`Restore failed: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  }, [showToast]);

  const refreshLookups = useCallback(async () => {
    try {
      const d = await apiFetch('/lookups');
      if (d.departments?.length) setLookupDepts(d.departments.map(x => x.name));
      if (d.functions?.length)   setLookupFns(d.functions.map(x => x.name));
      if (d.goals?.length)       setLookupGoals(d.goals.map(x => x.name));
      if (d.impacted_departments?.length) setLookupImpDepts(d.impacted_departments.map(x => x.name));
      if (Array.isArray(d.implementation_titles)) setLookupImplTitles(d.implementation_titles);
    } catch (_) { /* swallow — dropdowns keep last-known list */ }
  }, []);

  // Distinct "Implementation of action" values already in use across every
  // risk (archived included) — the "roles / titles" group of the
  // ImplementationPicker dropdown. Case-insensitive de-dupe, first spelling wins.
  const implTitles = useMemo(() => {
    const seen = new Map();
    for (const r of risks) {
      const v = (r.impl || '').trim();
      if (!v) continue;
      const k = v.toLowerCase();
      if (!seen.has(k)) seen.set(k, v);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
  }, [risks]);
  // Prefer the server's list (has counts + holders); fall back to the local
  // distinct values if /api/lookups has not answered.
  const implTitleRows = useMemo(() => (
    lookupImplTitles.length
      ? lookupImplTitles
      : implTitles.map(t => ({ title: t, risk_count: null, active_count: null, holder_upn: null, holder_name: null, is_person_name: false }))
  ), [lookupImplTitles, implTitles]);

  if (loading) return <SplashScreen />;
  if (loadError) return <ErrorScreen message={loadError} onRetry={loadAll} />;
  if (!currentUser) return <ErrorScreen message="No user context returned by the API." onRetry={loadAll} />;

  const effectiveUser = applyAdminMode(currentUser, adminModeOn);
  // Apply role-based visibility client-side. When admin mode is ON, this is
  // a no-op (admin sees everything anyway). When admin mode is OFF, this
  // restricts every risk-derived view to what a risk_owner would see.
  const scopedRisks = visibleRisks(risks, effectiveUser);
  // Archive view tier-gate: standard users see only their own archived risks.
  // admin / canSeeTrust / canSeePlc / canSeeExec see all archived they have
  // visibility on (which for those roles is everything).
  const canSeeAllArchived =
    effectiveUser.role === 'admin' || effectiveUser.canSeeTrust ||
    effectiveUser.canSeePlc || effectiveUser.canSeeExec;
  const archiveScope = canSeeAllArchived
    ? scopedRisks.filter(r => r.is_archived)
    : scopedRisks.filter(r => r.is_archived && (r.ownerUpn === effectiveUser.upn || r.reviewerUpn === effectiveUser.upn));

  return (
    <div className="min-h-screen bg-stone-50 flex font-sans antialiased">
      <Sidebar
        view={view}
        setView={(next) => {
          // Sidebar nav clicks are 'fresh nav' intent: reset filters so the
          // landing view isn't carrying state from a previous click-through
          // (e.g. clicking the High tile on Dashboard left score=high then
          // navigating to My Risks via sidebar). Dashboard tiles and other
          // intentional filter-then-navigate flows call setView directly.
          setFilters({ dept: 'all', score: 'all', residual: 'all', tier: 'all', goal: 'all', impactedDept: 'all', overdue: false, incomplete: false, name: '', myOnly: false });
          setView(next);
        }}
        navToFilteredView={(viewKey, filterPatch) => {
          // Sidebar click-throughs that should LAND with a specific filter
          // already applied (e.g. the Critical Risks alert box at the bottom).
          // Resets all other filters so the user lands with only the filter
          // they clicked for.
          setFilters({ dept: 'all', score: 'all', residual: 'all', tier: 'all', goal: 'all', impactedDept: 'all', overdue: false, incomplete: false, name: '', myOnly: false, ...filterPatch });
          setView(viewKey);
        }}
        currentUser={effectiveUser}
        realUser={currentUser}
        adminModeOn={adminModeOn}
        setAdminModeOn={setAdminModeOn}
        myRisksMode={myRisksMode}
        risks={scopedRisks}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          view={view}
          setShowAdd={setShowAdd}
          search={search}
          setSearch={setSearch}
          risks={scopedRisks}
          refreshing={refreshing}
          onRefresh={() => loadAll(true)}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          apiNotifs={apiNotifs}
          onMarkNotifRead={markNotifRead}
          onMarkAllNotifsRead={markAllNotifsRead}
          setSelectedRisk={setSelectedRisk}
          setView={setView}
          currentUser={effectiveUser}
        />
        <main className="flex-1 overflow-auto">
          {view === 'dashboard' && <DashboardView risks={scopedRisks} setView={setView} setSelectedRisk={setSelectedRisk} user={effectiveUser} setFilters={setFilters} />}
          {view === 'my-risks' && (
            <RisksTableView
              risks={myRisks(scopedRisks, effectiveUser, myRisksMode).filter(r => !r.is_archived)}
              title="My Risks"
              subtitle={myRisksMode === 'owner' ? 'Risks where you are the owner' : 'Risks where you are owner or reviewer'}
              setSelectedRisk={setSelectedRisk}
              search={search}
              filters={filters}
              setFilters={setFilters}
              myRisksMode={myRisksMode}
              setMyRisksMode={setMyRisksMode}
            />
          )}
          {view === 'my-implementation' && (
            <RisksTableView
              risks={myImplementationRisks(scopedRisks.filter(r => !r.is_archived), effectiveUser)}
              title="My Implementation of Action List"
              subtitle="Risks where you are the person implementing the action — picked directly, or through an Implementation of Action title you hold"
              setSelectedRisk={setSelectedRisk}
              search={search}
              filters={filters}
              setFilters={setFilters}
              departments={lookupDepts}
              currentUser={effectiveUser}
            />
          )}
          {view === 'all-risks' && <RisksTableView risks={scopedRisks.filter(r => !r.is_archived)} title="All Risks" subtitle={effectiveUser._adminModeOff ? 'Risks visible as a standard dept user (Admin Mode off)' : 'All risks visible to your role'} setSelectedRisk={setSelectedRisk} search={search} filters={filters} setFilters={setFilters} departments={lookupDepts}/>}
          {view === 'archive' && <RisksTableView risks={archiveScope} title="Archive" subtitle={canSeeAllArchived ? 'Archived risks (read-only) — these do not contribute to scores' : 'Your archived risks (read-only)'} setSelectedRisk={setSelectedRisk} search={search} filters={filters} setFilters={setFilters} departments={lookupDepts}/>}
          {view === 'department' && <DepartmentView risks={scopedRisks.filter(r => !r.is_archived)} setSelectedRisk={setSelectedRisk} deptFilter={deptFilter} setDeptFilter={setDeptFilter} />}
          {view === 'shared' && <SharedView risks={scopedRisks.filter(r => !r.is_archived)} setSelectedRisk={setSelectedRisk} user={effectiveUser} />}
          {view === 'trust' && <RisksTableView risks={scopedRisks.filter(r => !r.is_archived && r.tier === 'Trust')} title="Trust Risk Register" subtitle="Charity-level strategic risks owned by the Trust Board" setSelectedRisk={setSelectedRisk} search={search} filters={filters} setFilters={setFilters} departments={lookupDepts} currentUser={effectiveUser} tierMode/>}
          {view === 'plc' && <RisksTableView risks={scopedRisks.filter(r => !r.is_archived && r.tier === 'PLC')} title="PLC Risk Register" subtitle="Operating-company risks managed by the PLC Board and CLT" setSelectedRisk={setSelectedRisk} search={search} filters={filters} setFilters={setFilters} departments={lookupDepts} currentUser={effectiveUser} tierMode/>}
          {view === 'matrix' && <MatrixView risks={scopedRisks.filter(r => !r.is_archived)} setSelectedRisk={setSelectedRisk} user={effectiveUser} />}
          {view === 'reports' && <ReportsView risks={scopedRisks.filter(r => !r.is_archived)} user={effectiveUser} departments={lookupDepts} />}
          {view === 'audit' && <AuditView />}
          {view === 'admin' && <AdminView showToast={showToast} onLookupChange={refreshLookups} onUsersChanged={() => loadAll(true)} directory={directory} />}
        </main>
      </div>

      {selectedRisk && (
        <RiskDetailPanel
          risk={selectedRisk}
          editMode={editMode}
          setEditMode={setEditMode}
          onClose={() => { setSelectedRisk(null); setEditMode(false); }}
          onSave={saveRisk}
          onArchive={archiveRisk}
          onRestore={restoreRisk}
          saving={saving}
          canEdit={effectiveUser.role !== 'readonly'}
          directory={directory}
          departments={lookupDepts}
          functions={lookupFns}
          goalsList={lookupGoals}
          deptsList={lookupImpDepts}
          implTitles={implTitleRows}
          onClone={(source) => { setCloneFrom(source); setShowAdd(true); setSelectedRisk(null); setEditMode(false); }}
          onMerge={(target) => { setMergeTarget(target); }}
        />
      )}

      {mergeTarget && (
        <MergeRisksModal
          target={mergeTarget}
          allRisks={risks}
          currentUser={effectiveUser}
          onClose={() => setMergeTarget(null)}
          onMerge={mergeRisksApi}
          saving={saving}
        />
      )}

      {showAdd && (
        <AddRiskModal
          cloneFrom={cloneFrom}
          onClose={() => { setShowAdd(false); setCloneFrom(null); }}
          onCreate={async (d) => { await createRisk(d); setCloneFrom(null); }}
          currentUser={effectiveUser}
          existingRisks={risks}
          saving={saving}
          directory={directory}
          departments={lookupDepts}
          functions={lookupFns}
          goalsList={lookupGoals}
          deptsList={lookupImpDepts}
          implTitles={implTitleRows}
        />
      )}

      {toast && (
        <div
          role="status"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-4 py-3 rounded-md shadow-lg text-sm flex items-center gap-2 ${
            toast.kind === 'error' ? 'bg-red-600 text-white'
              : toast.kind === 'success' ? 'bg-emerald-600 text-white'
                : 'bg-stone-900 text-stone-50'
          }`}
        >
          {toast.kind === 'success' && <CheckCircle2 className="w-4 h-4"/>}
          {toast.kind === 'error' && <AlertTriangle className="w-4 h-4"/>}
          <span>{toast.msg}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SPLASH / ERROR SCREENS
// ============================================================
function SplashScreen() {
  return (
    <div className="min-h-screen bg-stone-900 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-md bg-gradient-to-br from-amber-600 to-red-700 flex items-center justify-center shadow-lg shadow-amber-900/40">
            <ShieldCheck className="w-7 h-7 text-stone-50"/>
          </div>
        </div>
        <h1 className="font-serif text-2xl text-stone-50 mb-2">NYMR Risk Register</h1>
        <div className="flex items-center justify-center gap-2 text-stone-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin"/>
          <span>Loading risks…</span>
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ message, onRetry }) {
  const isAuth = /401|auth|sign in/i.test(message);
  return (
    <div className="min-h-screen bg-stone-900 flex items-center justify-center p-6">
      <div className="bg-stone-800/60 backdrop-blur border border-stone-700 rounded-lg p-8 max-w-md text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4"/>
        <h1 className="font-serif text-xl text-stone-50 mb-2">Could not load risks</h1>
        <p className="text-stone-300 text-sm mb-5">{message}</p>
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={onRetry}
            className="bg-stone-50 hover:bg-white text-stone-900 text-sm font-medium py-2 px-4 rounded flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4"/> Retry
          </button>
          {isAuth && (
            <a
              href={`/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(window.location.pathname)}`}
              className="bg-amber-600 hover:bg-amber-500 text-stone-50 text-sm font-medium py-2 px-4 rounded"
            >
              Sign in
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SIDEBAR
// ============================================================
function Sidebar({ view, setView, navToFilteredView, currentUser, realUser, adminModeOn, setAdminModeOn, myRisksMode = 'owner', risks }) {
  const myCount = myRisks(risks.filter(r => !r.is_archived), currentUser, myRisksMode).length;
  const activeRisks = risks.filter(r => !r.is_archived);
  const myImplCount = myImplementationRisks(activeRisks, currentUser).length;
  const archivedCount = risks.filter(r => r.is_archived).length;
  const criticalCount = activeRisks.filter(r => (r.s || 0) >= 16).length;
  const role = currentUser?.role;
  const isReallyAdmin = realUser?.role === 'admin';

  const navSections = [
    {label: 'Overview', items: [
      {key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard'},
      {key: 'matrix', icon: GanttChart, label: 'Risk Matrix'}
    ]},
    {label: 'Risks', items: [
      {key: 'my-risks', icon: User, label: 'My Risks', badge: myCount},
      {key: 'my-implementation', icon: CheckCircle2, label: 'My Imp Actions', badge: myImplCount},
      {key: 'all-risks', icon: ListChecks, label: 'All Risks', badge: activeRisks.length},
      {key: 'department', icon: Building2, label: 'By Department'},
      {key: 'shared', icon: Share2, label: 'Shared Risks'},
      ...(archivedCount ? [{key: 'archive', icon: Lock, label: 'Archive', badge: archivedCount}] : [])
    ]},
    {label: 'Tier Registers', items: [
      {key: 'trust', icon: Crown, label: 'Trust', visible: role === 'admin' || !!currentUser?.canSeeTrust},
      {key: 'plc',   icon: Network, label: 'PLC',  visible: role === 'admin' || !!currentUser?.canSeePlc}
    ]},
    {label: 'Insights', items: [
      {key: 'reports', icon: FileBarChart2, label: 'Reports'},
      {key: 'audit', icon: History, label: 'Audit Log', visible: role === 'admin' || !!currentUser?.canSeeTrust || !!currentUser?.canSeePlc || !!currentUser?.canSeeExec}
    ]},
    {label: 'Admin', items: [
      {key: 'admin', icon: Settings, label: 'Users & Roles', visible: role === 'admin'}
    ]}
  ];

  return (
    <aside className="w-64 bg-stone-900 text-stone-200 flex flex-col flex-shrink-0">
      <div className="px-5 pt-6 pb-5 border-b border-stone-800">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-8 h-8 rounded bg-gradient-to-br from-amber-600 to-red-700 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-stone-50"/>
          </div>
          <div>
            <div className="font-serif text-lg leading-tight">NYMR</div>
            <div className="text-[10px] text-stone-400 tracking-widest uppercase leading-tight">Risk Register</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {navSections.map(section => {
          const items = section.items.filter(i => i.visible !== false);
          if (!items.length) return null;
          return (
            <div key={section.label} className="mb-5">
              <div className="px-2 text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 font-medium">{section.label}</div>
              {items.map(item => {
                const Icon = item.icon;
                const active = view === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => setView(item.key)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors ${
                      active ? 'bg-amber-900/30 text-amber-200 border-l-2 border-amber-500 -ml-px pl-3' : 'text-stone-300 hover:bg-stone-800 hover:text-stone-100 border-l-2 border-transparent -ml-px pl-3'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0"/>
                    {/* Sidebar stays w-64: long labels wrap onto a second line instead of widening it. */}
                    <span className="flex-1 min-w-0 text-left leading-snug">{item.label}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-amber-900/50 text-amber-200' : 'bg-stone-800 text-stone-400'}`}>{item.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}

        {criticalCount > 0 && (
          <button
            onClick={() => navToFilteredView?.('all-risks', { score: 'critical' })}
            className="mt-auto w-full text-left p-3 rounded bg-red-950/40 border border-red-900/60 mx-1 hover:bg-red-900/50 hover:border-red-700/70 transition-colors group"
            title="Click to view the filtered list of critical risks"
          >
            <div className="flex items-center gap-2 text-red-400 mb-1">
              <AlertTriangle className="w-4 h-4"/>
              <span className="text-xs font-medium">{criticalCount} Critical Risk{criticalCount > 1 ? 's' : ''}</span>
              <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"/>
            </div>
            <div className="text-[10px] text-red-300/70 leading-relaxed">Score 16-25 · click to view filtered list</div>
          </button>
        )}
      </nav>

      <div className="border-t border-stone-800 p-3">
        {isReallyAdmin && (
          <div className="mb-2 px-2 py-2 rounded bg-stone-800/60 border border-stone-700">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-widest text-stone-400 font-medium">Admin Mode</div>
              <button
                onClick={() => setAdminModeOn(v => !v)}
                role="switch"
                aria-checked={adminModeOn}
                className={`relative w-9 h-5 rounded-full transition-colors ${adminModeOn ? 'bg-amber-600' : 'bg-stone-600'}`}
                title={adminModeOn ? 'Admin Mode is ON — you see and can edit everything' : 'Admin Mode is OFF — you see what a standard dept user sees'}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${adminModeOn ? 'translate-x-4' : 'translate-x-0.5'}`}/>
              </button>
            </div>
            <div className="text-[10px] text-stone-400 leading-snug">
              {adminModeOn
                ? 'You are acting as admin. All actions logged.'
                : <>Acting as <span className="text-amber-300">risk_owner</span>. Toggle on to regain admin powers.</>}
            </div>
          </div>
        )}
        <div className="px-2 py-2 rounded bg-stone-800/60">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-700 to-stone-600 flex items-center justify-center text-stone-50 text-xs font-medium">
              {(currentUser.displayName || currentUser.upn).split(/[ .]/).filter(Boolean).map(n => n[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-stone-200 truncate">{currentUser.displayName || currentUser.upn}</div>
              <div className="text-[10px] text-stone-500 truncate">
                {currentUser.role}{currentUser._adminModeOff && <span className="text-amber-400"> · admin off</span>}
                {currentUser.department ? ` · ${currentUser.department}` : ''}
              </div>
            </div>
            <a href="/logout" title="Sign out" className="p-1.5 hover:bg-stone-700 rounded">
              <LogOut className="w-3.5 h-3.5 text-stone-400"/>
            </a>
          </div>
        </div>
        {currentUser.source === 'dev-bypass' && (
          <div className="mt-2 px-2 py-1.5 rounded bg-amber-950/40 border border-amber-900/50 text-[10px] text-amber-300 leading-relaxed">
            ⚠ Dev auth bypass active — server is honouring x-dev-* headers. Phase 6 turns this off.
          </div>
        )}
      </div>
    </aside>
  );
}

// ============================================================
// TOP BAR
// ============================================================
function TopBar({ view, setShowAdd, search, setSearch, risks, refreshing, onRefresh, notifOpen, setNotifOpen, apiNotifs = [], onMarkNotifRead, onMarkAllNotifsRead, setSelectedRisk, setView, currentUser }) {
  const titles = {
    'dashboard': 'Dashboard', 'my-risks': 'My Risks', 'all-risks': 'All Risks',
    'department': 'Risks by Department', 'shared': 'Shared Risks', 'archive': 'Archived Risks',
    'trust': 'Trust Risk Register', 'plc': 'PLC Risk Register', 'exec': 'Exec Risk Register',
    'matrix': 'Risk Matrix', 'reports': 'Reports', 'audit': 'Audit Log', 'admin': 'Users & Roles'
  };
  const active = risks.filter(r => !r.is_archived);
  // Notifications scoped to MY risks only (where I am owner or reviewer).
  const myActive = currentUser
    ? active.filter(r => r.ownerUpn === currentUser.upn || r.reviewerUpn === currentUser.upn)
    : [];
  const overdueRisks = myActive.filter(r => isOverdue(r.td));
  const criticalRisks = myActive.filter(r => (r.s || 0) >= 16);
  const unreadApiNotifs = apiNotifs.filter(n => !n.read_at);
  const notifCount = overdueRisks.length + criticalRisks.length + unreadApiNotifs.length;

  // Close dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const onClick = (e) => {
      if (!e.target.closest('[data-notif-root]')) setNotifOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [notifOpen, setNotifOpen]);

  return (
    <div className="bg-white border-b border-stone-200 px-6 py-3 flex items-center gap-4 flex-shrink-0">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500">NYMR / Risk Register</div>
        <h1 className="font-serif text-xl text-stone-900 leading-tight">{titles[view] || 'Dashboard'}</h1>
      </div>
      <div className="flex-1"/>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"/>
        <input
          type="text"
          placeholder="Search risks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 pr-3 py-1.5 text-sm bg-stone-50 border border-stone-200 rounded-md w-64 focus:outline-none focus:border-amber-600 focus:bg-white"
        />
      </div>
      <button
        onClick={onRefresh}
        className="p-2 hover:bg-stone-100 rounded-md disabled:opacity-50"
        disabled={refreshing}
        title="Refresh"
      >
        <RefreshCw className={`w-4 h-4 text-stone-600 ${refreshing ? 'animate-spin' : ''}`}/>
      </button>

      <div className="relative" data-notif-root>
        <button
          onClick={() => setNotifOpen(v => !v)}
          className={`relative p-2 rounded-md ${notifOpen ? 'bg-stone-100' : 'hover:bg-stone-100'}`}
          title={`${notifCount} notification${notifCount !== 1 ? 's' : ''}`}
        >
          <Bell className="w-4 h-4 text-stone-600"/>
          {notifCount > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[1rem] h-4 px-1 bg-red-500 text-white text-[9px] font-semibold rounded-full flex items-center justify-center">
              {notifCount > 9 ? '9+' : notifCount}
            </span>
          )}
        </button>
        {notifOpen && (
          <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-stone-200 rounded-lg shadow-xl z-50">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center">
              <div>
                <div className="font-serif text-sm text-stone-900">Notifications</div>
                <div className="text-[11px] text-stone-500">{notifCount} item{notifCount !== 1 ? 's' : ''} need attention</div>
              </div>
              {notifCount > 0 && <button onClick={() => { setNotifOpen(false); setView('all-risks'); }} className="ml-auto text-[11px] text-amber-700 hover:text-amber-800 font-medium">View all</button>}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {unreadApiNotifs.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-[10px] uppercase tracking-widest text-amber-800 font-semibold flex items-center">
                    <span>Mentions ({unreadApiNotifs.length})</span>
                    {onMarkAllNotifsRead && unreadApiNotifs.length > 1 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onMarkAllNotifsRead(); }}
                        className="ml-auto text-[10px] text-amber-700 hover:text-amber-900 normal-case tracking-normal font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  {unreadApiNotifs.slice(0, 8).map(n => {
                    const r = (risks || []).find(x => x.id === n.risk_id);
                    return (
                      <button
                        key={`api-${n.id}`}
                        onClick={() => {
                          if (r) setSelectedRisk(r);
                          onMarkNotifRead?.(n.id);
                          setNotifOpen(false);
                        }}
                        className="w-full px-4 py-2.5 text-left hover:bg-stone-50 border-b border-stone-100 flex items-start gap-2.5"
                      >
                        <Bell className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"/>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {n.risk_serial && <code className="text-[10px] font-mono bg-stone-100 px-1 rounded">#{n.risk_serial}</code>}
                            <span className="text-[10px] text-stone-500">{n.sender_name || n.sender_upn || 'NYMR Risk Register'}</span>
                            <span className="text-[10px] text-stone-400">·</span>
                            <span className="text-[10px] text-stone-400">{new Date(n.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                          </div>
                          <div className="text-xs text-stone-700 leading-snug">{n.message}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {notifCount === 0 && (
                <div className="px-4 py-8 text-center text-stone-500 text-sm">
                  <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-500"/>
                  Nothing urgent.
                </div>
              )}
              {criticalRisks.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-red-50 border-b border-red-100 text-[10px] uppercase tracking-widest text-red-700 font-semibold">Critical Risks ({criticalRisks.length})</div>
                  {criticalRisks.slice(0, 5).map(r => (
                    <button
                      key={`crit-${r.id}`}
                      onClick={() => { setSelectedRisk(r); setNotifOpen(false); }}
                      className="w-full px-4 py-2.5 text-left hover:bg-stone-50 border-b border-stone-100 flex items-start gap-2.5"
                    >
                      <div className={`w-7 h-7 rounded ${scoreColor(r.s)} flex items-center justify-center font-bold text-xs flex-shrink-0`}>{r.s}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <code className="text-[10px] font-mono bg-stone-100 px-1 rounded">#{r.serial}</code>
                          <span className="text-[10px] text-stone-500">{r.dept}</span>
                        </div>
                        <div className="text-xs text-stone-700 line-clamp-2 leading-snug">{r.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {overdueRisks.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-[10px] uppercase tracking-widest text-amber-800 font-semibold">Overdue Actions ({overdueRisks.length})</div>
                  {overdueRisks.slice(0, 5).map(r => (
                    <button
                      key={`ovd-${r.id}`}
                      onClick={() => { setSelectedRisk(r); setNotifOpen(false); }}
                      className="w-full px-4 py-2.5 text-left hover:bg-stone-50 border-b border-stone-100 flex items-start gap-2.5"
                    >
                      <Clock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"/>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <code className="text-[10px] font-mono bg-stone-100 px-1 rounded">#{r.serial}</code>
                          <span className="text-[10px] text-amber-700">Due {fmtDate(r.td)}</span>
                        </div>
                        <div className="text-xs text-stone-700 line-clamp-2 leading-snug">{r.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={() => setShowAdd(true)}
        className="bg-stone-900 hover:bg-stone-800 text-stone-50 text-sm px-3 py-1.5 rounded-md flex items-center gap-1.5 font-medium"
      >
        <Plus className="w-4 h-4"/>
        New Risk
      </button>
    </div>
  );
}

// ============================================================
// DASHBOARD VIEW
// ============================================================
function DashboardView({ risks, setView, setSelectedRisk, user, setFilters }) {
  // Phase 7 Batch E item 4: scope pill at the top of the dashboard.
  // Default 'mine' so each user lands on what's most relevant to them.
  const [dashScope, setDashScope] = useState('mine');
  // Tier multi-select (May 2026): independent of scope — filters Trust/PLC.
  // At least one must be selected; toggling the last one off is a no-op.
  const [tierSet, setTierSet] = useState(() => new Set(['Trust', 'PLC']));
  // Score multi-select (May 2026): controls whether the top-line cards and
  // pie chart bucket risks by their initial L×I, residual rL×rI, or the
  // worst of the two when both selected. Default Residual = post-mitigation
  // (the most relevant real-world reading).
  const [scoreSet, setScoreSet] = useState(() => new Set(['Residual']));
  const canSeeTiers = user && (user.role === 'admin' || user.canSeeTrust || user.canSeePlc || user.canSeeExec);
  const allActive = useMemo(() => risks.filter(r => !r.is_archived), [risks]);
  const active = useMemo(() => {
    let result = allActive.filter(r => tierSet.has(r.tier));
    if (!user) return result;
    if (dashScope === 'mine') return result.filter(r => r.ownerUpn === user.upn || r.reviewerUpn === user.upn);
    if (dashScope === 'implementation') return myImplementationRisks(result, user);
    if (dashScope === 'department') return result.filter(r => r.dept === user.department);
    return result;
  }, [allActive, user, dashScope, tierSet]);
  const scopeCounts = useMemo(() => {
    const tierFiltered = allActive.filter(r => tierSet.has(r.tier));
    return {
      mine: user ? tierFiltered.filter(r => r.ownerUpn === user.upn || r.reviewerUpn === user.upn).length : 0,
      implementation: myImplementationRisks(tierFiltered, user).length,
      department: user ? tierFiltered.filter(r => r.dept === user.department).length : 0,
      all: tierFiltered.length,
      Trust: allActive.filter(r => r.tier === 'Trust').length,
      PLC:   allActive.filter(r => r.tier === 'PLC').length,
    };
  }, [allActive, user, tierSet]);
  const DashScopePill = ({ value, label, count }) => {
    const activeP = dashScope === value;
    return (
      <button
        onClick={() => setDashScope(value)}
        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
          activeP
            ? 'bg-stone-900 text-stone-50 border-stone-900'
            : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
        }`}
      >
        {label} <span className={activeP ? 'opacity-70' : 'text-stone-400'}>({count})</span>
      </button>
    );
  };
  const DashTierPill = ({ tier }) => {
    const on = tierSet.has(tier);
    const count = scopeCounts[tier];
    const toggle = () => {
      setTierSet(prev => {
        const next = new Set(prev);
        if (next.has(tier)) {
          if (next.size === 1) return prev; // never empty — keep the last one
          next.delete(tier);
        } else {
          next.add(tier);
        }
        return next;
      });
    };
    return (
      <button
        onClick={toggle}
        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
          on ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
        }`}
      >
        {tier} <span className={on ? 'opacity-70' : 'text-stone-400'}>({count})</span>
      </button>
    );
  };
  const DashScorePill = ({ scoreType }) => {
    const on = scoreSet.has(scoreType);
    const toggle = () => {
      setScoreSet(prev => {
        const next = new Set(prev);
        if (next.has(scoreType)) {
          if (next.size === 1) return prev; // never empty
          next.delete(scoreType);
        } else {
          next.add(scoreType);
        }
        return next;
      });
    };
    return (
      <button
        onClick={toggle}
        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
          on ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
        }`}
        title={scoreType === 'Initial' ? 'Use the pre-mitigation score (L × I)' : 'Use the post-mitigation score (rL × rI)'}
      >
        {scoreType}
      </button>
    );
  };
  // When both score types are selected, click-throughs use the residual
  // filter (the more conservative real-world reading). Initial-only → score.
  const scoreFilterKey = scoreSet.has('Residual') ? 'residual' : 'score';

  // Phase 7 Batch E hotfix #3: dashboard click-throughs preserve scope.
  // Counts on the dashboard tiles (and Concentrations/Overdue blocks) are
  // computed against the dashScope-filtered set, but the destination All
  // Risks view uses the user's full visibility set. Without this helper,
  // clicking 'High' under 'My Risks' scope showed every visible high risk
  // (~80) instead of just the user's owned/reviewed high risks (~4).
  // Mapping: mine -> my-risks (already owner/reviewer filtered);
  // department -> all-risks + dept=user.department; company/tiers/all
  // -> all-risks (too broad to express in single-select pills, so the
  // landing count is approximate for those scopes).
  const navWithScope = (filterUpdates = {}) => {
    const baseFilter = { dept: 'all', score: 'all', residual: 'all', tier: 'all', goal: 'all', impactedDept: 'all', overdue: false, incomplete: false };
    if (dashScope === 'mine') {
      setFilters?.({ ...baseFilter, ...filterUpdates });
      setView('my-risks');
    } else if (dashScope === 'implementation') {
      setFilters?.({ ...baseFilter, ...filterUpdates });
      setView('my-implementation');
    } else if (dashScope === 'department' && user?.department) {
      setFilters?.({ ...baseFilter, dept: user.department, ...filterUpdates });
      setView('all-risks');
    } else {
      setFilters?.({ ...baseFilter, ...filterUpdates });
      setView('all-risks');
    }
  };
  const stats = useMemo(() => {
    // Each risk is bucketed by the WORST of its selected scores.
    // Residual default → buckets reflect post-mitigation reality.
    // Initial only → buckets reflect pre-mitigation severity.
    // Both → each risk goes in the bucket of its higher score (no double-counting).
    const bucketScoreFor = (r) => {
      let best = 0;
      if (scoreSet.has('Initial'))  best = Math.max(best, r.s  || 0);
      if (scoreSet.has('Residual')) best = Math.max(best, r.rs || 0);
      return best;
    };
    const total = active.length;
    let critical = 0, high = 0, medium = 0, low = 0;
    active.forEach(r => {
      const s = bucketScoreFor(r);
      if (s >= 16) critical++;
      else if (s >= 10) high++;
      else if (s >= 5) medium++;
      else if (s > 0) low++;
    });
    const overdue = active.filter(r => isOverdue(r.td)).length;
    return { total, critical, high, medium, low, overdue };
  }, [active, scoreSet]);

  const byDept = useMemo(() => {
    const m = {};
    active.forEach(r => { if (r.dept) m[r.dept] = (m[r.dept] || 0) + 1; });
    return Object.entries(m).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [active]);

  const scoreDistribution = useMemo(() => [
    { name: 'Critical (16-25)', value: stats.critical, color: '#dc2626' },
    { name: 'High (10-15)', value: stats.high, color: '#f97316' },
    { name: 'Medium (5-9)', value: stats.medium, color: '#fbbf24' },
    { name: 'Low (1-4)', value: stats.low, color: '#10b981' }
  ], [stats]);

  const overdueRisks = useMemo(() => active.filter(r => isOverdue(r.td)).slice(0, 6), [active]);

  // Risks Requiring Attention (item #4):
  // - rs >= 16 (critical residual)
  // - missing initial score
  // - missing residual score
  // - s >= 10 with no controls written down (high initial, no mitigation)
  // - rs >= 10 (high residual)
  // Each risk gets a single "why" tag — the most severe applicable category wins.
  const attentionList = useMemo(() => {
    const tagFor = (r) => {
      if ((r.rs || 0) >= 16) return { bucket: 5, label: 'Critical residual',       color: 'bg-red-100 text-red-800 border-red-300' };
      if (!r.s || r.s === 0)  return { bucket: 4, label: 'No initial score',        color: 'bg-purple-100 text-purple-800 border-purple-300' };
      if (!r.rs || r.rs === 0)return { bucket: 4, label: 'No residual score',       color: 'bg-purple-100 text-purple-800 border-purple-300' };
      if ((r.s || 0) >= 10 && !(r.ctrl && r.ctrl.trim())) return { bucket: 4, label: 'High risk — no mitigation', color: 'bg-orange-100 text-orange-800 border-orange-300' };
      if ((r.rs || 0) >= 10)  return { bucket: 3, label: 'High residual',           color: 'bg-orange-100 text-orange-800 border-orange-300' };
      return null;
    };
    return active
      .map(r => ({ r, tag: tagFor(r) }))
      .filter(x => x.tag)
      .sort((a, b) => b.tag.bucket - a.tag.bucket
                   || (b.r.rs || 0) - (a.r.rs || 0)
                   || (b.r.s  || 0) - (a.r.s  || 0))
      .slice(0, 8);
  }, [active]);

  // Trend uses the last 6 months of updates from the dataset itself.
  // Falls back to a flat line for the current month total if there's not enough history.
  // Mitigation effectiveness: distribution of risks by severity bucket
  // BEFORE mitigation (initial L*I) vs AFTER (residual rL*rI). Tells the
  // story of how many risks have moved down the severity ladder once
  // controls are applied. Uses live data only - no fake history.
  const mitigationData = useMemo(() => {
    const bucket = (s) => {
      if (!s) return null;
      if (s >= 16) return 'Critical';
      if (s >= 10) return 'High';
      if (s >= 5) return 'Medium';
      return 'Low';
    };
    const counts = { Critical: { initial: 0, residual: 0 }, High: { initial: 0, residual: 0 }, Medium: { initial: 0, residual: 0 }, Low: { initial: 0, residual: 0 } };
    active.forEach(r => {
      const bi = bucket(r.s);
      const br = bucket(r.rs);
      if (bi) counts[bi].initial += 1;
      if (br) counts[br].residual += 1;
    });
    return ['Critical', 'High', 'Medium', 'Low'].map(name => ({
      name,
      Initial: counts[name].initial,
      Residual: counts[name].residual
    }));
  }, [active]);

  // Headline numbers under the chart
  const mitigationStats = useMemo(() => {
    const totInit = active.reduce((s, r) => s + (r.s || 0), 0);
    const totRes = active.reduce((s, r) => s + (r.rs || 0), 0);
    const pctReduction = totInit > 0 ? Math.round(((totInit - totRes) / totInit) * 100) : 0;
    return { totInit, totRes, pctReduction };
  }, [active]);

  // Concentrations of Concern: count elevated-residual risks (>=10) per
  // NYMR Goal and per Impacted Department. Critical (>=16) and High
  // (10-15) are tracked separately so the chart can render a stacked bar.
  // Risks with no goals/depts tagged simply contribute nothing - they
  // can't be plotted against an axis they aren't on.
  const redConcentrations = useMemo(() => {
    const tally = (axisItems, getTags) => axisItems.map(name => {
      let critical = 0, high = 0;
      active.forEach(r => {
        const rs = r.rs || 0;
        if (rs < 10) return;
        const tags = getTags(r);
        if (!tags.includes(name)) return;
        if (rs >= 16) critical += 1; else high += 1;
      });
      return { name, critical, high, total: critical + high };
    }).sort((a, b) => b.total - a.total);

    const byGoal = tally(NYMR_GOALS, r => r.goals || []);
    const byDept = tally(IMPACTED_DEPARTMENTS, r => r.impacted_departments || []);
    const totalRed = active.filter(r => (r.rs || 0) >= 10).length;
    const totalCritical = active.filter(r => (r.rs || 0) >= 16).length;
    return { byGoal, byDept, totalRed, totalCritical };
  }, [active]);

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-lg border border-stone-200 px-4 py-3 flex items-center gap-2 flex-wrap">
        <div className="text-xs text-stone-500 flex items-center gap-1.5 mr-1"><Filter className="w-3.5 h-3.5"/>Show</div>
        <DashScopePill value="mine" label="My Risks" count={scopeCounts.mine}/>
        <DashScopePill value="implementation" label="My Implementation of Action" count={scopeCounts.implementation}/>
        <DashScopePill value="department" label="Department" count={scopeCounts.department}/>
        <DashScopePill value="all" label="All" count={scopeCounts.all}/>
        {canSeeTiers && (user?.canSeeTrust || user?.role === 'admin') && (user?.canSeePlc || user?.role === 'admin') && (
          <>
            <div className="w-px h-5 bg-stone-200 mx-1"/>
            <DashTierPill tier="Trust"/>
            <DashTierPill tier="PLC"/>
          </>
        )}
        <div className="w-px h-5 bg-stone-200 mx-1"/>
        <span className="text-xs text-stone-500 mr-0.5">Score</span>
        <DashScorePill scoreType="Initial"/>
        <DashScorePill scoreType="Residual"/>
      </div>
      <div className="grid grid-cols-6 gap-3">
        <button
          onClick={() => navWithScope({ score: 'all', overdue: false })}
          className="bg-white rounded-lg border border-stone-200 p-4 text-left hover:border-stone-400 hover:shadow-sm transition-all group focus:outline-none focus:ring-2 focus:ring-amber-500"
          title="View all active risks"
        >
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 flex items-center gap-1">Active Risks <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"/></div>
          <div className="font-serif text-3xl text-stone-900">{stats.total}</div>
          <div className="text-xs text-stone-500 mt-1">Visible to {user.displayName || user.upn}</div>
        </button>
        <button
          onClick={() => navWithScope({ [scoreFilterKey]: 'critical', overdue: false })}
          className="bg-white rounded-lg border border-red-200 p-4 relative overflow-hidden text-left hover:border-red-400 hover:shadow-sm transition-all group focus:outline-none focus:ring-2 focus:ring-red-500"
          title="View critical risks (score 16+)"
        >
          <div className="absolute top-0 right-0 w-16 h-16 bg-red-50 rounded-full -mr-8 -mt-8"/>
          <div className="text-[10px] uppercase tracking-widest text-red-700 mb-1 relative flex items-center gap-1">Critical <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"/></div>
          <div className="font-serif text-3xl text-red-600 relative">{stats.critical}</div>
          <div className="text-xs text-red-500/80 mt-1 relative">{scoreSet.size === 2 ? 'Worst ' : scoreSet.has('Residual') ? 'Residual ' : 'Initial '}16+</div>
        </button>
        <button
          onClick={() => navWithScope({ [scoreFilterKey]: 'high', overdue: false })}
          className="bg-white rounded-lg border border-orange-200 p-4 text-left hover:border-orange-400 hover:shadow-sm transition-all group focus:outline-none focus:ring-2 focus:ring-orange-500"
          title="View high risks (score 10-15)"
        >
          <div className="text-[10px] uppercase tracking-widest text-orange-700 mb-1 flex items-center gap-1">High <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"/></div>
          <div className="font-serif text-3xl text-orange-600">{stats.high}</div>
          <div className="text-xs text-orange-500/80 mt-1">{scoreSet.size === 2 ? 'Worst ' : scoreSet.has('Residual') ? 'Residual ' : 'Initial '}10-15</div>
        </button>
        <button
          onClick={() => navWithScope({ [scoreFilterKey]: 'medium', overdue: false })}
          className="bg-white rounded-lg border border-amber-200 p-4 text-left hover:border-amber-400 hover:shadow-sm transition-all group focus:outline-none focus:ring-2 focus:ring-amber-500"
          title="View medium risks (score 5-9)"
        >
          <div className="text-[10px] uppercase tracking-widest text-amber-800 mb-1 flex items-center gap-1">Medium <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"/></div>
          <div className="font-serif text-3xl text-amber-600">{stats.medium}</div>
          <div className="text-xs text-amber-700/80 mt-1">{scoreSet.size === 2 ? 'Worst ' : scoreSet.has('Residual') ? 'Residual ' : 'Initial '}5-9</div>
        </button>
        <button
          onClick={() => navWithScope({ [scoreFilterKey]: 'low', overdue: false })}
          className="bg-white rounded-lg border border-emerald-200 p-4 text-left hover:border-emerald-400 hover:shadow-sm transition-all group focus:outline-none focus:ring-2 focus:ring-emerald-500"
          title="View low risks (score 1-4)"
        >
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1 flex items-center gap-1">Low <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"/></div>
          <div className="font-serif text-3xl text-emerald-600">{stats.low}</div>
          <div className="text-xs text-emerald-500/80 mt-1">{scoreSet.size === 2 ? 'Worst ' : scoreSet.has('Residual') ? 'Residual ' : 'Initial '}1-4</div>
        </button>
        <button
          onClick={() => navWithScope({ score: 'all', overdue: true })}
          className="bg-stone-900 rounded-lg p-4 text-stone-50 text-left hover:bg-stone-800 hover:shadow-sm transition-all group focus:outline-none focus:ring-2 focus:ring-amber-500"
          title="View all overdue actions"
        >
          <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1 flex items-center gap-1">Overdue Actions <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"/></div>
          <div className="font-serif text-3xl text-amber-400">{stats.overdue}</div>
          <div className="text-xs text-stone-400 mt-1">Past target date</div>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 bg-white rounded-lg border border-stone-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-serif text-xl text-stone-900">Risks Requiring Attention</h2>
              <div className="text-xs text-stone-500">Critical residuals, missing scores, or high risk without mitigation</div>
            </div>
            <button onClick={() => navWithScope()} className="text-xs text-amber-700 hover:text-amber-800 font-medium flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3"/>
            </button>
          </div>
          <div className="space-y-2">
            {attentionList.length === 0 && (
              <div className="text-sm text-stone-500 py-4 text-center flex flex-col items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-500"/>
                Nothing requires attention right now.
              </div>
            )}
            {attentionList.map(({ r, tag }) => (
              <button
                key={r.id}
                onClick={() => setSelectedRisk(r)}
                className={`w-full text-left p-3 rounded border-l-4 ${scoreBorder(r.s)} bg-stone-50 hover:bg-stone-100 transition-colors flex items-center gap-3`}
              >
                <div className={`w-12 h-12 rounded ${scoreColor(r.s)} flex flex-col items-center justify-center font-bold flex-shrink-0`}>
                  <div className="text-lg leading-none">{r.s || '—'}</div>
                  <div className="text-[8px] uppercase tracking-wider opacity-80">{scoreLabel(r.s)}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <code className="text-[10px] font-mono bg-stone-200 px-1.5 py-0.5 rounded text-stone-700">#{r.serial}</code>
                    <span className="text-[10px] uppercase tracking-wider text-stone-500">{r.dept}</span>
                    <span className="text-[10px] text-stone-400">·</span>
                    <span className="text-[10px] text-stone-500">{r.tier}</span>
                    <span className={`text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded border ${tag.color}`}>{tag.label}</span>
                  </div>
                  <div className="text-sm text-stone-800 line-clamp-2 leading-snug">{r.desc}</div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-stone-500">
                    <span className="flex items-center gap-1"><User className="w-3 h-3"/>{r.owner || '—'}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>{fmtDate(r.td)}</span>
                    <span className="text-stone-400">·</span>
                    <span>Residual {r.rs ?? '—'}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0"/>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-stone-200 p-5">
          <h2 className="font-serif text-lg text-stone-900 mb-1">Risk Distribution</h2>
          <div className="text-xs text-stone-500 mb-3">By severity score</div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={scoreDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                {scoreDistribution.map((entry, i) => <Cell key={i} fill={entry.color}/>)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '4px', border: '1px solid #e7e5e4' }}/>
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {scoreDistribution.map(s => (
              <div key={s.name} className="flex items-center text-xs">
                <div className="w-2.5 h-2.5 rounded-sm mr-2" style={{ backgroundColor: s.color }}/>
                <span className="flex-1 text-stone-700">{s.name}</span>
                <span className="font-semibold text-stone-900">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 bg-white rounded-lg border border-stone-200 p-5">
          <h2 className="font-serif text-lg text-stone-900 mb-1">Risks by Department</h2>
          <div className="text-xs text-stone-500 mb-3">Count of active risks per departmental register</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byDept} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#e7e5e4"/>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#78716c' }} angle={-25} textAnchor="end" height={60}/>
              <YAxis tick={{ fontSize: 10, fill: '#78716c' }}/>
              <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '4px', border: '1px solid #e7e5e4' }}/>
              <Bar dataKey="value" fill="#b45309" radius={[2, 2, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg border border-stone-200 p-5">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h2 className="font-serif text-lg text-stone-900">Mitigation Effectiveness</h2>
              <div className="text-xs text-stone-500">Risks per severity bucket: before vs after controls</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Score reduction</div>
              <div className={`font-serif text-2xl leading-none ${mitigationStats.pctReduction >= 30 ? 'text-emerald-600' : mitigationStats.pctReduction >= 10 ? 'text-amber-600' : 'text-stone-700'}`}>
                {mitigationStats.pctReduction}%
              </div>
              <div className="text-[10px] text-stone-500 mt-0.5">{mitigationStats.totInit} <ArrowRight className="w-2.5 h-2.5 inline -mt-0.5"/> {mitigationStats.totRes}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mitigationData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#e7e5e4"/>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#78716c' }}/>
              <YAxis tick={{ fontSize: 10, fill: '#78716c' }} allowDecimals={false}/>
              <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '4px', border: '1px solid #e7e5e4' }}/>
              <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={10}/>
              <Bar dataKey="Initial" fill="#9a3412" radius={[2, 2, 0, 0]}/>
              <Bar dataKey="Residual" fill="#10b981" radius={[2, 2, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {(redConcentrations.totalRed > 0) && (
        <div className="bg-white rounded-lg border border-stone-200 p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="font-serif text-xl text-stone-900">Concentrations of Concern</h2>
              <div className="text-xs text-stone-500">Where elevated-residual risks (residual 10+) cluster across NYMR Goals and Impacted Departments. Click any row to drill in.</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-red-700">Elevated risks</div>
              <div className="font-serif text-3xl text-red-700 leading-none">{redConcentrations.totalRed}</div>
              <div className="text-[10px] text-stone-500 mt-0.5">{redConcentrations.totalCritical} critical &middot; {redConcentrations.totalRed - redConcentrations.totalCritical} high</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <ConcentrationColumn
              label="By NYMR Goal"
              rows={redConcentrations.byGoal}
              maxOverall={Math.max(1, ...redConcentrations.byGoal.map(r => r.total), ...redConcentrations.byDept.map(r => r.total))}
              onRowClick={(name) => navWithScope({ residual: 'elevated', goal: name })}
            />
            <ConcentrationColumn
              label="By Impacted Department"
              rows={redConcentrations.byDept}
              maxOverall={Math.max(1, ...redConcentrations.byGoal.map(r => r.total), ...redConcentrations.byDept.map(r => r.total))}
              onRowClick={(name) => navWithScope({ residual: 'elevated', impactedDept: name })}
            />
          </div>
          <div className="mt-4 pt-4 border-t border-stone-100 flex items-center gap-4 text-[10px] uppercase tracking-widest text-stone-500">
            <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-red-600"/>Critical (16+)</div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-orange-500"/>High (10-15)</div>
            <div className="flex items-center gap-1.5 ml-auto text-stone-400 normal-case tracking-normal italic">Click any row to see the relevant risks.</div>
          </div>
        </div>
      )}

      {overdueRisks.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-amber-700"/>
            <h2 className="font-serif text-lg text-stone-900">Overdue Actions</h2>
            <div className="text-xs text-amber-700/80">{overdueRisks.length} risk{overdueRisks.length > 1 ? 's' : ''} past target date</div>
            <button
              onClick={() => navWithScope({ overdue: true })}
              className="ml-auto text-xs text-amber-800 hover:text-amber-900 font-medium flex items-center gap-1"
            >
              View all overdue <ArrowRight className="w-3 h-3"/>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {overdueRisks.map(r => (
              <button
                key={r.id}
                onClick={() => setSelectedRisk(r)}
                className="text-left bg-white p-3 rounded border border-amber-200 hover:border-amber-400 transition-colors flex items-center gap-3"
              >
                <div className={`w-9 h-9 rounded ${scoreColor(r.s)} flex items-center justify-center font-bold text-sm flex-shrink-0`}>{r.s || '—'}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-[10px] font-mono bg-stone-100 px-1 rounded">#{r.serial}</code>
                    <span className="text-[10px] text-amber-700">Due {fmtDate(r.td)}</span>
                  </div>
                  <div className="text-xs text-stone-700 truncate">{r.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// RISKS TABLE VIEW
// ============================================================
function RisksTableView({ risks, title, subtitle, setSelectedRisk, search, filters, setFilters, myRisksMode, setMyRisksMode, departments = DEPARTMENTS, currentUser, tierMode }) {
  const filtered = useMemo(() => {
    const q = search?.toLowerCase() || '';
    return risks.filter(r => {
      if (q && !`${r.serial} ${r.desc} ${r.owner} ${r.fn} ${r.impl || ''}`.toLowerCase().includes(q)) return false;
      if (filters.dept !== 'all' && r.dept !== filters.dept) return false;
      if (filters.score === 'critical' && (r.s || 0) < 16) return false;
      if (filters.score === 'high' && ((r.s || 0) < 10 || (r.s || 0) >= 16)) return false;
      if (filters.score === 'medium' && ((r.s || 0) < 5 || (r.s || 0) >= 10)) return false;
      if (filters.score === 'low' && ((r.s || 0) === 0 || (r.s || 0) >= 5)) return false;
      if (filters.residual === 'critical' && (r.rs || 0) < 16) return false;
      if (filters.residual === 'high' && ((r.rs || 0) < 10 || (r.rs || 0) >= 16)) return false;
      if (filters.residual === 'medium' && ((r.rs || 0) < 5 || (r.rs || 0) >= 10)) return false;
      if (filters.residual === 'low' && ((r.rs || 0) === 0 || (r.rs || 0) >= 5)) return false;
      if (filters.residual === 'elevated' && (r.rs || 0) < 10) return false;
      if (filters.tier !== 'all' && r.tier !== filters.tier) return false;
      if (filters.goal && filters.goal !== 'all' && !(r.goals || []).includes(filters.goal)) return false;
      if (filters.impactedDept && filters.impactedDept !== 'all' && !(r.impacted_departments || []).includes(filters.impactedDept)) return false;
      if (filters.overdue && !isOverdue(r.td)) return false;
      if (filters.incomplete && !(!r.s || r.s === 0 || !r.rs || r.rs === 0)) return false;
      if (filters.name) {
        const nq = filters.name.toLowerCase();
        if (!`${r.owner || ''} ${r.reviewer || ''} ${r.impl || ''}`.toLowerCase().includes(nq)) return false;
      }
      if (filters.myOnly && currentUser && !(r.ownerUpn === currentUser.upn || r.reviewerUpn === currentUser.upn)) return false;
      return true;
    });
  }, [risks, search, filters, currentUser]);

  const exportCsv = () => {
    const cols = ['serial', 'fn', 'dept', 'desc', 'owner', 'reviewer', 'impl', 'L', 'I', 's', 'rL', 'rI', 'rs', 'td', 'tier'];
    const header = cols.join(',');
    const rows = filtered.map(r => cols.map(c => {
      const v = r[c] ?? '';
      return /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    }).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nymr-risks-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex items-start gap-4">
        <div className="flex-1">
          <h2 className="font-serif text-2xl text-stone-900">{title}</h2>
          <div className="text-sm text-stone-500">{subtitle} · {filtered.length} of {risks.length} risk{risks.length !== 1 ? 's' : ''}</div>
        </div>
        {setMyRisksMode && (
          <div className="flex items-center gap-1 bg-stone-100 rounded-md p-1">
            <button
              onClick={() => setMyRisksMode('owner')}
              className={`text-xs px-3 py-1 rounded ${myRisksMode === 'owner' ? 'bg-white text-stone-900 shadow-sm font-medium' : 'text-stone-600 hover:text-stone-900'}`}
            >
              Owner only
            </button>
            <button
              onClick={() => setMyRisksMode('all')}
              className={`text-xs px-3 py-1 rounded ${myRisksMode === 'all' ? 'bg-white text-stone-900 shadow-sm font-medium' : 'text-stone-600 hover:text-stone-900'}`}
            >
              Owner + Reviewer
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-stone-200 mb-4 px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="text-xs text-stone-500 flex items-center gap-1.5"><Filter className="w-3.5 h-3.5"/>Filters</div>
        <FilterPill label="Department" value={filters.dept} options={[{ value: 'all', label: 'All Departments' }, ...departments.map(d => ({ value: d, label: d }))]} onChange={(v) => setFilters({ ...filters, dept: v })}/>
        <FilterPill label="Score" value={filters.score} options={[{ value: 'all', label: 'All Scores' }, { value: 'critical', label: 'Critical (16+)' }, { value: 'high', label: 'High (10-15)' }, { value: 'medium', label: 'Medium (5-9)' }, { value: 'low', label: 'Low (1-4)' }]} onChange={(v) => setFilters({ ...filters, score: v })}/>
        <FilterPill label="Residual" value={filters.residual} options={[{ value: 'all', label: 'All Residual' }, { value: 'elevated', label: 'Elevated (10+)' }, { value: 'critical', label: 'Critical (16+)' }, { value: 'high', label: 'High (10-15)' }, { value: 'medium', label: 'Medium (5-9)' }, { value: 'low', label: 'Low (1-4)' }]} onChange={(v) => setFilters({ ...filters, residual: v })}/>
        <FilterPill label="Tier" value={filters.tier} options={[{ value: 'all', label: 'All Tiers' }, ...TIERS.map(t => ({ value: t, label: t }))]} onChange={(v) => setFilters({ ...filters, tier: v })}/>
        <FilterPill label="Goal" value={filters.goal || 'all'} options={[{ value: 'all', label: 'All Goals' }, ...NYMR_GOALS.map(g => ({ value: g, label: g }))]} onChange={(v) => setFilters({ ...filters, goal: v })}/>
        <FilterPill label="Impacted" value={filters.impactedDept || 'all'} options={[{ value: 'all', label: 'All Impacted Depts' }, ...IMPACTED_DEPARTMENTS.map(d => ({ value: d, label: d }))]} onChange={(v) => setFilters({ ...filters, impactedDept: v })}/>
        <button
          onClick={() => setFilters({ ...filters, overdue: !filters.overdue })}
          className={`text-xs px-3 py-1 rounded-full border transition-colors flex items-center gap-1 ${
            filters.overdue
              ? 'bg-amber-600 text-white border-amber-600'
              : 'bg-stone-100 hover:bg-stone-200 text-stone-700 border-stone-200'
          }`}
        >
          <Clock className="w-3 h-3"/>Overdue only
          {filters.overdue && <X className="w-3 h-3 ml-0.5"/>}
        </button>
        {setMyRisksMode && (
          <button
            onClick={() => setFilters({ ...filters, incomplete: !filters.incomplete })}
            className={`text-xs px-3 py-1 rounded-full border transition-colors flex items-center gap-1 ${
              filters.incomplete
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-stone-100 hover:bg-stone-200 text-stone-700 border-stone-200'
            }`}
            title="Show only risks where the initial score or residual score has not been completed"
          >
            <AlertTriangle className="w-3 h-3"/>Incomplete
            {filters.incomplete && <X className="w-3 h-3 ml-0.5"/>}
          </button>
        )}
        {currentUser && (
          <button
            onClick={() => setFilters({ ...filters, myOnly: !filters.myOnly })}
            className={`text-xs px-3 py-1 rounded-full border transition-colors flex items-center gap-1 ${
              filters.myOnly
                ? 'bg-amber-700 text-white border-amber-700'
                : 'bg-stone-100 hover:bg-stone-200 text-stone-700 border-stone-200'
            }`}
            title="Show only risks where you are owner or reviewer"
          >
            <User className="w-3 h-3"/>My Risks only
            {filters.myOnly && <X className="w-3 h-3 ml-0.5"/>}
          </button>
        )}
        <div className="relative flex items-center">
          <Search className="absolute left-2 w-3 h-3 text-stone-400 pointer-events-none"/>
          <input
            type="text"
            value={filters.name || ''}
            onChange={e => setFilters({ ...filters, name: e.target.value })}
            placeholder="By name…"
            className="text-xs bg-stone-100 hover:bg-stone-200 border border-stone-200 rounded-full pl-7 pr-2 py-1 w-32 focus:outline-none focus:bg-white focus:border-stone-400 placeholder:text-stone-500"
            title="Filter by owner or reviewer name"
          />
        </div>
        <button onClick={() => setFilters({ dept: 'all', score: 'all', residual: 'all', tier: 'all', goal: 'all', impactedDept: 'all', overdue: false, incomplete: false, name: '', myOnly: false })} className="text-xs text-stone-500 hover:text-stone-700 ml-auto">Clear all</button>
        <button onClick={exportCsv} className="text-xs text-stone-700 hover:text-stone-900 flex items-center gap-1"><Download className="w-3.5 h-3.5"/>Export CSV</button>
      </div>

      <RisksTable risks={filtered} setSelectedRisk={setSelectedRisk} tierMode={tierMode}/>
    </div>
  );
}

function FilterPill({ label, value, options, onChange }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs bg-stone-100 hover:bg-stone-200 border border-stone-200 rounded-full pl-3 pr-7 py-1 appearance-none cursor-pointer font-medium text-stone-700"
      >
        {options.map(o => <option key={o.value} value={o.value}>{label}: {o.label}</option>)}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-stone-500 pointer-events-none"/>
    </div>
  );
}

function RisksTable({ risks, setSelectedRisk, tierMode }) {
  const [sortBy, setSortBy] = useState('serial');
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    if (!tierMode) return risks;
    const cmp = (a, b) => {
      let res = 0;
      if (sortBy === 'serial') {
        const pa = (a.serial || '').toString();
        const pb = (b.serial || '').toString();
        const na = parseInt(pa.match(/^\d+/)?.[0] || '0', 10);
        const nb = parseInt(pb.match(/^\d+/)?.[0] || '0', 10);
        res = na !== nb ? na - nb : pa.localeCompare(pb);
      } else if (sortBy === 'td') {
        // 'Ongoing' / null / invalid go to the end
        const parse = (v) => {
          if (!v || v === 'Ongoing') return Number.POSITIVE_INFINITY;
          const t = new Date(v).getTime();
          return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
        };
        res = parse(a.td) - parse(b.td);
      }
      return sortDir === 'asc' ? res : -res;
    };
    return [...risks].sort(cmp);
  }, [risks, tierMode, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortArrow = ({ col }) => {
    if (sortBy !== col) return <ArrowUp className="w-3 h-3 opacity-20"/>;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-stone-700"/> : <ArrowDown className="w-3 h-3 text-stone-700"/>;
  };

  if (!risks.length) return (
    <div className="bg-white rounded-lg border border-stone-200 p-12 text-center text-stone-500 text-sm">No risks match the current filters.</div>
  );
  return (
    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 border-b border-stone-200">
          <tr>
            {tierMode ? (
              <th
                className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-16 cursor-pointer select-none hover:bg-stone-100"
                onClick={() => toggleSort('serial')}
                title="Sort by serial number"
              >
                <span className="inline-flex items-center gap-1">Serial <SortArrow col="serial"/></span>
              </th>
            ) : (
              <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-16">Serial</th>
            )}
            <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2">Risk</th>
            <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-32">Owner</th>
            <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-36" title="Implementation of action — who carries out the mitigation">Implementation</th>
            <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-28">Department</th>
            <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-12">L</th>
            <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-12">I</th>
            <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-16">Score</th>
            {tierMode && <th className="text-center text-[10px] uppercase tracking-widest text-emerald-700 font-medium px-3 py-2 w-12" title="Residual Likelihood">rL</th>}
            {tierMode && <th className="text-center text-[10px] uppercase tracking-widest text-emerald-700 font-medium px-3 py-2 w-12" title="Residual Impact">rI</th>}
            <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-16">Resid.</th>
            <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-16" title="Goals impacted / Departments impacted">Impact</th>
            {tierMode ? (
              <th
                className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-24 cursor-pointer select-none hover:bg-stone-100"
                onClick={() => toggleSort('td')}
                title="Sort by target date"
              >
                <span className="inline-flex items-center gap-1">Target <SortArrow col="td"/></span>
              </th>
            ) : (
              <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-24">Target</th>
            )}
            <th className="w-8"/>
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => (
            <tr
              key={r.id}
              onClick={() => setSelectedRisk(r)}
              className="border-b border-stone-100 hover:bg-amber-50/40 cursor-pointer group"
            >
              <td className="px-3 py-3"><code className="text-[11px] font-mono bg-stone-100 px-1.5 py-0.5 rounded text-stone-700">{r.serial}</code></td>
              <td className="px-3 py-3"><div className="text-stone-800 line-clamp-2 leading-snug max-w-xl">{r.desc}</div><div className="text-[10px] text-stone-500 mt-0.5">{r.fn}</div></td>
              <td className="px-3 py-3 text-stone-700 text-xs">{r.owner || '—'}</td>
              <td className="px-3 py-3 text-stone-700 text-xs">
                {r.impl || '—'}
                {r.implUpn && r.implName && r.implName !== r.impl && <div className="text-[10px] text-stone-500">{r.implName}</div>}
              </td>
              <td className="px-3 py-3"><span className="text-[10px] uppercase tracking-wider text-stone-600 bg-stone-100 px-1.5 py-0.5 rounded">{r.dept}</span></td>
              <td className="px-3 py-3 text-center text-stone-700">{r.L || '—'}</td>
              <td className="px-3 py-3 text-center text-stone-700">{r.I || '—'}</td>
              <td className="px-3 py-3 text-center"><span className={`inline-block min-w-[2rem] px-2 py-0.5 rounded text-xs font-semibold ${scoreColor(r.s)}`}>{r.s || '—'}</span></td>
              {tierMode && <td className="px-3 py-3 text-center text-emerald-800">{r.rL || '—'}</td>}
              {tierMode && <td className="px-3 py-3 text-center text-emerald-800">{r.rI || '—'}</td>}
              <td className="px-3 py-3 text-center"><span className={`inline-flex items-center justify-center gap-0.5 min-w-[2rem] px-2 py-0.5 rounded text-xs font-semibold ${scoreColor(r.rs)}`} title={r.rs && r.s && r.rs < r.s ? `Reduced from ${r.s} by mitigation` : undefined}>{r.rs || '—'}{r.rs && r.s && r.rs < r.s ? <span className="opacity-70 text-[10px]">↓</span> : null}</span></td>
              <td className="px-3 py-3 text-center"><div className="inline-flex"><ImpactRagDots goalsCount={(r.goals||[]).length} deptsCount={(r.impacted_departments||[]).length}/></div></td>
              <td className="px-3 py-3 text-xs">{r.tStatus === 'ongoing_mitigated' ? <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold tracking-wider">Mitigated</span> : isOverdue(r.td) ? <span className="text-red-600 font-medium">{fmtDate(r.td)}</span> : <span className="text-stone-600">{fmtDate(r.td)}</span>}</td>
              <td className="px-3 py-3"><ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-amber-700"/></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// DEPARTMENT VIEW
// ============================================================
function DepartmentView({ risks, setSelectedRisk, deptFilter, setDeptFilter }) {
  // All distinct departments seen in the data, sorted by count desc.
  const allDepts = useMemo(() => {
    const m = {};
    risks.forEach(r => { m[r.dept || '(no department)'] = (m[r.dept || '(no department)'] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [risks]);

  // When deptFilter is empty (Set size 0) we treat it as "show all".
  const showAll = !deptFilter || deptFilter.size === 0;
  const visible = useMemo(() => {
    if (showAll) return risks;
    return risks.filter(r => deptFilter.has(r.dept || '(no department)'));
  }, [risks, deptFilter, showAll]);

  const grouped = useMemo(() => {
    const m = {};
    visible.forEach(r => { (m[r.dept || '(no department)'] = m[r.dept || '(no department)'] || []).push(r); });
    return Object.fromEntries(Object.entries(m).sort((a, b) => b[1].length - a[1].length));
  }, [visible]);

  const toggleDept = (d) => {
    const next = new Set(deptFilter);
    if (next.has(d)) next.delete(d); else next.add(d);
    setDeptFilter(next);
  };

  return (
    <div className="p-6 space-y-4">
      <div className="mb-2">
        <h2 className="font-serif text-2xl text-stone-900">Risks by Department</h2>
        <div className="text-sm text-stone-500">Click departments to add or remove them from the view. Showing {visible.length} of {risks.length} risk{risks.length !== 1 ? 's' : ''}.</div>
      </div>

      {/* Cumulative dept selector */}
      <div className="bg-white rounded-lg border border-stone-200 px-4 py-3 flex items-center gap-2 flex-wrap">
        <div className="text-xs text-stone-500 flex items-center gap-1.5 mr-1"><Filter className="w-3.5 h-3.5"/>Departments</div>
        <button
          onClick={() => setDeptFilter(new Set())}
          className={`text-xs px-3 py-1 rounded-full border transition-colors ${
            showAll
              ? 'bg-stone-900 text-stone-50 border-stone-900'
              : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
          }`}
        >
          All ({risks.length})
        </button>
        {allDepts.map(([d, count]) => {
          const selected = !showAll && deptFilter.has(d);
          return (
            <button
              key={d}
              onClick={() => toggleDept(d)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                selected
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
              }`}
            >
              {d} <span className={selected ? 'opacity-80' : 'text-stone-400'}>({count})</span>
            </button>
          );
        })}
        {!showAll && (
          <button onClick={() => setDeptFilter(new Set())} className="text-xs text-stone-500 hover:text-stone-800 ml-auto flex items-center gap-1">
            <X className="w-3 h-3"/>Clear
          </button>
        )}
      </div>

      {Object.entries(grouped).length === 0 && (
        <div className="bg-white rounded-lg border border-stone-200 p-12 text-center text-stone-500 text-sm">
          No risks visible. Pick at least one department or click “All”.
        </div>
      )}
      {Object.entries(grouped).map(([dept, deptRisks]) => {
        const total = deptRisks.length;
        const critical = deptRisks.filter(r => (r.s || 0) >= 16).length;
        const high = deptRisks.filter(r => (r.s || 0) >= 10 && (r.s || 0) < 16).length;
        const totalScore = deptRisks.reduce((s, r) => s + (r.s || 0), 0);
        return (
          <div key={dept || '_blank'} className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-stone-200 flex items-center bg-stone-50">
              <div>
                <h3 className="font-serif text-lg text-stone-900">{dept || '(no department)'}</h3>
                <div className="text-xs text-stone-500">{total} risk{total !== 1 ? 's' : ''} · total score {totalScore}</div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {critical > 0 && <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-red-100 text-red-700 font-semibold">{critical} Critical</span>}
                {high > 0 && <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold">{high} High</span>}
              </div>
            </div>
            <div className="divide-y divide-stone-100">
              {deptRisks.map(r => (
                <button
                  key={r.id}
                  onClick={() => setSelectedRisk(r)}
                  className="w-full px-5 py-3 flex items-center gap-4 hover:bg-amber-50/40 text-left"
                >
                  <div className={`w-10 h-10 rounded ${scoreColor(r.s)} flex items-center justify-center font-bold flex-shrink-0`}>{r.s || '—'}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <code className="text-[10px] font-mono bg-stone-100 px-1.5 py-0.5 rounded text-stone-700">#{r.serial}</code>
                      <span className="text-[10px] uppercase tracking-wider text-stone-500">{r.fn}</span>
                    </div>
                    <div className="text-sm text-stone-800 line-clamp-1">{r.desc}</div>
                  </div>
                  <div className="text-xs text-stone-600 flex-shrink-0 hidden md:block">
                    <div>{r.owner || '—'}</div>
                    <div className="text-[10px] text-stone-500">{fmtDate(r.td)}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0"/>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// SHARED RISKS VIEW
// ============================================================
function SharedView({ risks, setSelectedRisk, user }) {
  // Phase 7 rule: shared risks = risks I'm involved in where there are TWO
  // distinct stakeholders. So either:
  //   - I'm the owner AND there's a reviewer, OR
  //   - I'm the reviewer (the owner is someone else by definition).
  // Other people's owner/reviewer pairings are not my concern.
  const shared = useMemo(() => {
    if (!user) return [];
    return risks.filter(r => {
      const iAmOwner = r.ownerUpn === user.upn;
      const iAmReviewer = r.reviewerUpn === user.upn;
      const hasReviewer = !!r.reviewerUpn;
      const hasOwner = !!r.ownerUpn;
      const distinct = r.ownerUpn !== r.reviewerUpn;
      if (iAmOwner && hasReviewer && distinct) return true;
      if (iAmReviewer && hasOwner && distinct) return true;
      return false;
    });
  }, [risks, user]);

  return (
    <div className="p-6">
      <div className="mb-4">
        <h2 className="font-serif text-2xl text-stone-900">Shared Risks</h2>
        <div className="text-sm text-stone-500">Risks where you are owner with a reviewer set, or where someone else owns and you review.</div>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-start gap-3">
        <Share2 className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5"/>
        <div className="text-sm text-stone-800">
          Use this view to keep track of risks where the responsibility is split with a colleague — either as a peer-review pairing on something you own, or because you're the reviewer on someone else's risk.
        </div>
      </div>
      <RisksTable risks={shared} setSelectedRisk={setSelectedRisk}/>
    </div>
  );
}

// ============================================================
// TIER VIEW
// ============================================================
function TierView({ tier, risks, setSelectedRisk }) {
  const tierRisks = risks.filter(r => r.tier === tier);
  const desc = {
    Trust: 'Charity-level strategic risks owned by the Trust Board. Highest-impact items spanning the organisation.',
    PLC: 'Operating-company risks managed by the PLC Board and CLT.',
    Exec: 'Risks owned by individual executive directors as part of the executive register.'
  };
  const Icon = tier === 'Trust' ? Crown : tier === 'PLC' ? Network : ShieldCheck;
  return (
    <div className="p-6">
      <div className="mb-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded bg-stone-900 text-amber-400 flex items-center justify-center"><Icon className="w-5 h-5"/></div>
        <div>
          <h2 className="font-serif text-2xl text-stone-900">{tier} Risk Register</h2>
          <div className="text-sm text-stone-500">{desc[tier]} · {tierRisks.length} risk{tierRisks.length !== 1 ? 's' : ''}</div>
        </div>
      </div>
      <RisksTable risks={tierRisks} setSelectedRisk={setSelectedRisk}/>
    </div>
  );
}

// ============================================================
// MATRIX VIEW
// ============================================================
function MatrixView({ risks, setSelectedRisk, user }) {
  const [expanded, setExpanded] = useState(false);
  const canSeeTiers = user && (user.role === 'admin' || user.canSeeTrust || user.canSeePlc || user.canSeeExec);
  const [scope, setScope] = useState('mine');
  // Tier multi-select (May 2026): independent of scope — filters Trust/PLC.
  // At least one must be selected; toggling the last one off is a no-op.
  const [tierSet, setTierSet] = useState(() => new Set(['Trust', 'PLC']));
  // Score multi-select (May 2026): controls which position(s) chips appear at.
  // Initial → plot at L×I. Residual → plot at rL×rI. Both → plot at both;
  // if positions match (no migration), one chip with neutral styling.
  const [scoreSet, setScoreSet] = useState(() => new Set(['Residual']));

  // Apply scope + tier filter. Note: upstream visibleRisks() already
  // strips tiers the user can't see, so tier pills only narrow what's
  // already visible.
  const scopedForMatrix = useMemo(() => {
    let result = risks.filter(r => tierSet.has(r.tier));
    if (!user) return result;
    if (scope === 'mine') return result.filter(r => r.ownerUpn === user.upn || r.reviewerUpn === user.upn);
    if (scope === 'department') return result.filter(r => r.dept === user.department);
    return result;
  }, [risks, user, scope, tierSet]);

  const grid = useMemo(() => {
    const g = {};
    for (let l = 1; l <= 5; l++) for (let i = 1; i <= 5; i++) g[`${l}-${i}`] = [];
    scopedForMatrix.forEach(r => {
      // Track which positions this risk should be plotted at and what type
      // each placement represents. If both score types lead to the same
      // position, merge into a single 'both' placement.
      const placements = new Map(); // key -> Set of types
      if (scoreSet.has('Initial') && r.L && r.I) {
        const k = `${r.L}-${r.I}`;
        if (!placements.has(k)) placements.set(k, new Set());
        placements.get(k).add('initial');
      }
      if (scoreSet.has('Residual') && r.rL && r.rI) {
        const k = `${r.rL}-${r.rI}`;
        if (!placements.has(k)) placements.set(k, new Set());
        placements.get(k).add('residual');
      }
      placements.forEach((types, key) => {
        g[key].push({ ...r, _scoreTypes: Array.from(types) });
      });
    });
    return g;
  }, [scopedForMatrix, scoreSet]);

  const cellColor = (l, i) => {
    const s = l * i;
    if (s >= 16) return 'bg-red-100 hover:bg-red-200 border-red-300';
    if (s >= 10) return 'bg-orange-100 hover:bg-orange-200 border-orange-300';
    if (s >= 5) return 'bg-amber-100 hover:bg-amber-200 border-amber-300';
    return 'bg-emerald-100 hover:bg-emerald-200 border-emerald-300';
  };

  const likelihoodLabels = ['Remote (1)', 'Unlikely (2)', 'Possible (3)', 'Expected (4)', 'Recurring (5)'];
  const impactLabels = ['Negligible', 'Minor', 'Moderate', 'Major', 'Catastrophic'];

  const cellHeight = expanded ? 'min-h-[12rem]' : 'h-24';
  const chipClass = expanded
    ? 'text-xs font-mono bg-white hover:bg-amber-50 border border-stone-300 hover:border-amber-400 px-2 py-1 rounded text-stone-800 inline-flex items-center gap-1'
    : 'text-[9px] font-mono bg-white/80 hover:bg-white border border-stone-300 px-1 py-0.5 rounded text-stone-700';

  const ScopePill = ({ value, label, count }) => {
    const active = scope === value;
    return (
      <button
        onClick={() => setScope(value)}
        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
          active
            ? 'bg-stone-900 text-stone-50 border-stone-900'
            : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
        }`}
      >
        {label} <span className={active ? 'opacity-70' : 'text-stone-400'}>({count})</span>
      </button>
    );
  };
  const TierPill = ({ tier }) => {
    const on = tierSet.has(tier);
    const count = counts[tier];
    const toggle = () => {
      setTierSet(prev => {
        const next = new Set(prev);
        if (next.has(tier)) {
          if (next.size === 1) return prev; // never empty
          next.delete(tier);
        } else {
          next.add(tier);
        }
        return next;
      });
    };
    return (
      <button
        onClick={toggle}
        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
          on ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
        }`}
      >
        {tier} <span className={on ? 'opacity-70' : 'text-stone-400'}>({count})</span>
      </button>
    );
  };
  const ScorePill = ({ scoreType }) => {
    const on = scoreSet.has(scoreType);
    const toggle = () => {
      setScoreSet(prev => {
        const next = new Set(prev);
        if (next.has(scoreType)) {
          if (next.size === 1) return prev; // never empty
          next.delete(scoreType);
        } else {
          next.add(scoreType);
        }
        return next;
      });
    };
    return (
      <button
        onClick={toggle}
        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
          on ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
        }`}
        title={scoreType === 'Initial' ? 'Plot risks at their initial L × I position' : 'Plot risks at their residual rL × rI position'}
      >
        {scoreType}
      </button>
    );
  };

  // Pre-compute counts so the pill labels are honest. Note 'mine' /
  // 'department' / 'all' all respect tier multi-select; Trust/PLC counts
  // are global so the pill always shows total available in each tier.
  const counts = useMemo(() => {
    const tierFiltered = risks.filter(r => tierSet.has(r.tier));
    return {
      mine: user ? tierFiltered.filter(r => r.ownerUpn === user.upn || r.reviewerUpn === user.upn).length : 0,
      department: user ? tierFiltered.filter(r => r.dept === user.department).length : 0,
      all: tierFiltered.length,
      Trust: risks.filter(r => r.tier === 'Trust').length,
      PLC:   risks.filter(r => r.tier === 'PLC').length,
    };
  }, [risks, user, tierSet]);

  return (
    <div className="p-6">
      <div className="mb-4 flex items-start gap-4">
        <div className="flex-1">
          <h2 className="font-serif text-2xl text-stone-900">Risk Matrix</h2>
          <div className="text-sm text-stone-500">5 × 5 likelihood/impact grid · click any serial to open the risk</div>
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 border ${
            expanded ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
          }`}
        >
          {expanded ? <ChevronRight className="w-3.5 h-3.5 rotate-90"/> : <ChevronDown className="w-3.5 h-3.5 -rotate-90"/>}
          {expanded ? 'Compact view' : 'Expand view'}
        </button>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 px-4 py-3 mb-4 flex items-center gap-2 flex-wrap">
        <div className="text-xs text-stone-500 flex items-center gap-1.5 mr-1"><Filter className="w-3.5 h-3.5"/>Show</div>
        <ScopePill value="mine" label="My Risks" count={counts.mine}/>
        <ScopePill value="department" label="Department" count={counts.department}/>
        <ScopePill value="all" label="All" count={counts.all}/>
        {canSeeTiers && (user?.canSeeTrust || user?.role === 'admin') && (user?.canSeePlc || user?.role === 'admin') && (
          <>
            <div className="w-px h-5 bg-stone-200 mx-1"/>
            <TierPill tier="Trust"/>
            <TierPill tier="PLC"/>
          </>
        )}
        <div className="w-px h-5 bg-stone-200 mx-1"/>
        <span className="text-xs text-stone-500 mr-0.5">Score</span>
        <ScorePill scoreType="Initial"/>
        <ScorePill scoreType="Residual"/>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 p-6">
        <div className="flex gap-3">
          <div className="flex flex-col justify-around items-center pr-2 pt-8 pb-8" style={{ minWidth: '2rem' }}>
            <div className="font-serif text-sm text-stone-700 transform -rotate-90 whitespace-nowrap origin-center">Likelihood →</div>
          </div>
          <div className="flex-1">
            <table className="w-full border-separate border-spacing-1">
              <thead>
                <tr>
                  <th className="w-20"/>
                  {impactLabels.map((label, i) => (
                    <th key={i} className="text-[10px] uppercase tracking-wider text-stone-500 font-medium pb-2">{label}<div className="text-[10px] text-stone-400 font-normal">(Impact {i + 1})</div></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[5, 4, 3, 2, 1].map(l => (
                  <tr key={l}>
                    <td className="text-right pr-2 text-[10px] uppercase tracking-wider text-stone-500 font-medium align-middle">{likelihoodLabels[l - 1]}</td>
                    {[1, 2, 3, 4, 5].map(i => {
                      const cellRisks = grid[`${l}-${i}`];
                      return (
                        <td key={i} className={`border ${cellColor(l, i)} rounded ${cellHeight} align-top p-2 transition-colors`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] text-stone-600 font-mono">Score {l * i}</span>
                            {cellRisks.length > 0 && <span className="text-[10px] text-stone-600 font-semibold">{cellRisks.length}</span>}
                          </div>
                          <div className={`flex flex-wrap ${expanded ? 'gap-1.5' : 'gap-0.5'}`}>
                            {(expanded ? cellRisks : cellRisks.slice(0, 6)).map((r, idx) => {
                              // When both score types are selected, color-code
                              // the chip by which position it represents.
                              const showTypeColor = scoreSet.size === 2;
                              const types = r._scoreTypes || [];
                              const typeClass = !showTypeColor ? ''
                                : types.length === 2 ? ''
                                : types[0] === 'initial'
                                  ? '!bg-amber-50 !border-amber-300 hover:!bg-amber-100'
                                  : '!bg-emerald-50 !border-emerald-300 hover:!bg-emerald-100';
                              return (
                                <button
                                  key={`${r.id}-${types.join(',')}-${idx}`}
                                  onClick={() => setSelectedRisk(r)}
                                  className={`${chipClass} ${typeClass}`}
                                  title={`${r.serial} · ${r.dept} · ${r.desc}${showTypeColor && types.length === 1 ? ` · ${types[0]} position` : ''}`}
                                >
                                  <span className="font-semibold">#{r.serial}</span>
                                  {expanded && <span className="text-[10px] text-stone-500 max-w-[10rem] truncate font-sans">{r.desc}</span>}
                                </button>
                              );
                            })}
                            {!expanded && cellRisks.length > 6 && <span className="text-[9px] text-stone-600 px-1">+{cellRisks.length - 6}</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-center text-[10px] uppercase tracking-wider text-stone-500 font-medium mt-3">Impact →</div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-stone-200 flex items-center gap-6 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">Severity</div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-emerald-500"/><span className="text-xs text-stone-700">Low (1-4)</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-amber-400"/><span className="text-xs text-stone-700">Medium (5-9)</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-orange-500"/><span className="text-xs text-stone-700">High (10-15)</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-600"/><span className="text-xs text-stone-700">Critical (16-25)</span></div>
          {scoreSet.size === 2 && (
            <>
              <div className="w-px h-5 bg-stone-200"/>
              <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-amber-50 border border-amber-300"/><span className="text-xs text-stone-700">Initial position</span></div>
              <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-emerald-50 border border-emerald-300"/><span className="text-xs text-stone-700">Residual position</span></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// REPORTS VIEW — pulls from /api/reports/{type} on demand
// ============================================================
function ReportsView({ risks, user, departments = DEPARTMENTS }) {
  const [selectedReport, setSelectedReport] = useState('high-risks-board');
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [registerBusy, setRegisterBusy] = useState(false);

  // The Corporate Risk Register is the Trust-tier register (board-level
  // material). RLS scopes Trust rows to admins and Trust-flagged users, so the
  // download button matches: admin or Trust visibility.
  const canDownloadRegister =
    !!user && (user.role === 'admin' || user.canSeeTrust);

  // Streams the byte-for-byte Excel replica from the API and saves it. Kept as
  // a raw fetch (not apiFetch) because the response is a binary blob, not JSON.
  const downloadRegister = async () => {
    setRegisterBusy(true);
    try {
      const res = await fetch(`${API_BASE}/export/corporate-register`, { credentials: 'include' });
      if (res.status === 401) {
        window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
        return;
      }
      if (!res.ok) {
        let detail;
        try { detail = (await res.json()).error; } catch { detail = res.statusText; }
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NYMR-Corporate-Risk-Register.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not download the Corporate Risk Register: ' + err.message);
    } finally {
      setRegisterBusy(false);
    }
  };

  // Reports are ordered by what people open most often: "My Risks" first
  // because that's the daily working view, the operational reports next,
  // department / cross-cutting, then the board packs, with the
  // Executive Summary as the highest-level synthesis at the bottom.
  const reports = [
    { key: 'high-risks-board', label: 'High Risks Board Report', desc: 'Critical / High / Overdue · multi-select tier & dept · PDF export', icon: AlertTriangle, apiType: null },
    { key: 'personal',     label: 'My Risks',              desc: 'Where you are owner or reviewer', icon: User,         apiType: 'personal' },
    { key: 'overdue',      label: 'Overdue Actions',       desc: 'Past target date',                icon: Clock,        apiType: 'overdue' },
    { key: 'changes',      label: 'Recent Changes',        desc: 'Updated in the last 30 days',     icon: History,      apiType: 'changes' },
    { key: 'department',   label: 'Department Detail',     desc: 'Pick a department to drill in',   icon: Building2,    apiType: 'department' },
    { key: 'impact-goal',  label: 'Impact & Goal Rankings',desc: 'Risks with broad Goals/Depts impact', icon: Share2,    apiType: 'impact-goal' },
    { key: 'plc',          label: 'PLC Board Pack',        desc: 'PLC-tier active risks',           icon: Network,      apiType: 'plc' },
    { key: 'trust',        label: 'Trust Board Pack',      desc: 'Trust-tier active risks',         icon: Crown,        apiType: 'trust' }
  ];

  useEffect(() => {
    const def = reports.find(r => r.key === selectedReport);
    if (!def) return;
    // Local-only reports (apiType=null) skip the backend round-trip and feed
    // the already-RLS-scoped, already-proto risks prop straight into reportData.
    // Used by the High Risks Board Report so tier/dept filters are instant.
    if (def.apiType === null) {
      setLoadingReport(false);
      setReportError(null);
      setReportData({
        __isLocal: true,
        risks,
        description: 'Critical (16+) and overdue risks across your visibility'
      });
      return;
    }
    let cancelled = false;
    setLoadingReport(true);
    setReportError(null);
    apiFetch(`/reports/${def.apiType}`)
      .then(data => { if (!cancelled) setReportData(data); })
      .catch(err => { if (!cancelled) setReportError(err.message); })
      .finally(() => { if (!cancelled) setLoadingReport(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, risks]);

  return (
    <div className="p-6 grid grid-cols-12 gap-6">
      <div className="col-span-3">
        <h2 className="font-serif text-xl text-stone-900 mb-1">Reports</h2>
        <div className="text-xs text-stone-500 mb-4">Pre-built reports for governance meetings</div>
        {canDownloadRegister && (
          <button
            onClick={downloadRegister}
            disabled={registerBusy}
            title="Download the full NYMR Corporate Risk Register as a formatted Excel workbook"
            className="w-full mb-4 text-left p-3 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60 transition-colors flex items-start gap-2"
          >
            {registerBusy
              ? <Loader2 className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-700 animate-spin"/>
              : <Download className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-700"/>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-emerald-900">{registerBusy ? 'Preparing…' : 'Corporate Risk Register (Excel)'}</div>
              <div className="text-[11px] text-emerald-700">Full formatted workbook · all sheets</div>
            </div>
          </button>
        )}
        <div className="space-y-1">
          {reports.map(r => {
            const Icon = r.icon;
            const active = selectedReport === r.key;
            return (
              <button
                key={r.key}
                onClick={() => setSelectedReport(r.key)}
                className={`w-full text-left p-3 rounded transition-colors flex items-start gap-2 ${active ? 'bg-stone-900 text-stone-50' : 'bg-white border border-stone-200 hover:border-stone-300'}`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${active ? 'text-amber-400' : 'text-stone-500'}`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{r.label}</div>
                  <div className={`text-[11px] ${active ? 'text-stone-400' : 'text-stone-500'}`}>{r.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="col-span-9">
        <ReportContent
          report={reports.find(r => r.key === selectedReport)}
          data={reportData}
          loading={loadingReport}
          error={reportError}
          user={user}
          departments={departments}
        />
      </div>
    </div>
  );
}

// Severity bucket for one risk score.
function sevBucket(score) {
  const s = score || 0;
  if (s >= 16) return 'critical';
  if (s >= 10) return 'high';
  if (s >= 5)  return 'medium';
  return 'low';
}

// True for users who legitimately see across the organisation in the
// standalone Overdue Actions report (Item #4 in the Phase 7 Batch C spec).
// risk_owner / readonly users are scoped to their own owned/reviewed risks
// in that report; everyone else (admin, or a user with any tier flag) sees
// the full visible set.
function isElevatedForReports(user) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Boolean(user.canSeeTrust || user.canSeePlc || user.canSeeExec);
}

function ReportContent({ report, data, loading, error, user, departments = DEPARTMENTS }) {
  // Severity toggles — Critical + High default ON, Medium + Low default OFF.
  const [sev, setSev] = useState({ critical: true, high: true, medium: false, low: false });
  // Overdue toggle — when on, the bottom of the report appends an "Overdue
  // Actions" section. Default ON per Phase 7 Batch C spec.
  const [showOverdue, setShowOverdue] = useState(true);
  // Department pill selection (only used by the Department Detail report).
  // Default to the user's own department on first render; admins can pick All.
  const [selectedDept, setSelectedDept] = useState(null);
  // Phase 7 Batch E item 1: Impact & Goal Rankings filter pills.
  // Multi-select; empty Set = no filter on that dimension. Reset whenever
  // the active report changes so switching reports doesn't carry filters.
  const [selectedGoals, setSelectedGoals] = useState(() => new Set());
  const [selectedImpDepts, setSelectedImpDepts] = useState(() => new Set());
  useEffect(() => {
    setSelectedGoals(new Set());
    setSelectedImpDepts(new Set());
  }, [report?.key]);
  useEffect(() => {
    if (selectedDept === null) {
      setSelectedDept(user?.department || 'all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.department]);

  if (!report) return null;

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-stone-200 p-12 flex items-center justify-center text-stone-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2"/>Loading report…
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6 text-center text-red-700 text-sm">
        Couldn't load report: {error}
      </div>
    );
  }
  if (!data) return null;

  // High Risks Board Report has its own layout (tier+dept pills, petrol gauge)
  // and uses already-proto risks fed via the local-only path above, so dispatch
  // out of the standard report layout entirely before the dbToProto re-mapping.
  if (report.key === 'high-risks-board') {
    return <HighRisksBoardReport risks={data.risks || []} user={user} departments={departments} />;
  }

  // ----- Apply structural filters that are independent of severity. -----
  let scopedRisks = (data.risks || []).map(dbToProto);

  // Department Detail: narrow to the chosen department (single-select pills).
  if (report.key === 'department' && selectedDept && selectedDept !== 'all') {
    scopedRisks = scopedRisks.filter(r => r.dept === selectedDept);
  }

  // Overdue standalone report: non-elevated users see only their own risks.
  if (report.key === 'overdue' && !isElevatedForReports(user)) {
    scopedRisks = scopedRisks.filter(r => r.ownerUpn === user.upn || r.reviewerUpn === user.upn);
  }

  // Impact & Goal Rankings: multi-select pill filtering on Goals and Impacted Depts.
  // Within each dimension: ANY-of (selected goal -> show risk if it has any of those goals).
  // Across dimensions: AND (must satisfy both Goals filter AND Depts filter).
  if (report.key === 'impact-goal') {
    if (selectedGoals.size > 0) {
      scopedRisks = scopedRisks.filter(r => (r.goals || []).some(g => selectedGoals.has(g)));
    }
    if (selectedImpDepts.size > 0) {
      scopedRisks = scopedRisks.filter(r => (r.impacted_departments || []).some(d => selectedImpDepts.has(d)));
    }
  }

  // ----- Severity filter applied on top. -----
  const filteredRisks = scopedRisks.filter(r => sev[sevBucket(r.s)]);

  // ----- Overdue items within the scoped set, regardless of severity. -----
  const overdueRisks = scopedRisks.filter(r => isOverdue(r.td));

  // ----- Banner stats (severity counts in the un-filtered scoped set, so
  //       toggling pills doesn't make the headline numbers move). -----
  const stats = {
    total: scopedRisks.length,
    critical: scopedRisks.filter(r => (r.s || 0) >= 16).length,
    high: scopedRisks.filter(r => (r.s || 0) >= 10 && (r.s || 0) < 16).length,
    medium: scopedRisks.filter(r => (r.s || 0) >= 5 && (r.s || 0) < 10).length,
    low: scopedRisks.filter(r => (r.s || 0) < 5).length,
    overdue: overdueRisks.length
  };

  const showDeptPills = report.key === 'department';
  const showImpactGoalPills = report.key === 'impact-goal';
  const toggleGoal = (g) => setSelectedGoals(prev => {
    const n = new Set(prev);
    if (n.has(g)) n.delete(g); else n.add(g);
    return n;
  });
  const toggleImpDept = (d) => setSelectedImpDepts(prev => {
    const n = new Set(prev);
    if (n.has(d)) n.delete(d); else n.add(d);
    return n;
  });

  return (
    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-stone-200 flex items-center bg-stone-50">
        <div>
          <h3 className="font-serif text-xl text-stone-900">{report.label}</h3>
          <div className="text-xs text-stone-500">
            {data.description} · Generated {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} · {stats.total} risks{showDeptPills && selectedDept && selectedDept !== 'all' ? ` in ${selectedDept}` : ''}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => window.print()} className="text-xs px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5"><FileText className="w-3.5 h-3.5"/>Print</button>
        </div>
      </div>

      {/* ---- Department pill selector (Department Detail report only) ---- */}
      {showDeptPills && (
        <div className="px-6 py-3 border-b border-stone-200 bg-white flex items-center gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mr-1">Department</div>
          <button
            onClick={() => setSelectedDept('all')}
            className={`text-xs px-2.5 py-1 rounded-full border transition ${selectedDept === 'all' ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
          >
            All
          </button>
          {departments.map(d => (
            <button
              key={d}
              onClick={() => setSelectedDept(d)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${selectedDept === d ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
            >
              {d}
            </button>
          ))}
        </div>
      )}

      {/* ---- Impact & Goal Rankings filter pills (multi-select) ---- */}
      {showImpactGoalPills && (
        <>
          <div className="px-6 py-3 border-b border-stone-200 bg-white flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mr-1">NYMR Goal</div>
            {NYMR_GOALS.map(g => {
              const sel = selectedGoals.has(g);
              return (
                <button
                  key={g}
                  onClick={() => toggleGoal(g)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${sel ? 'bg-amber-700 text-white border-amber-700' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
                >
                  {g}
                </button>
              );
            })}
            {selectedGoals.size > 0 && (
              <button onClick={() => setSelectedGoals(new Set())} className="text-[10px] text-stone-500 hover:text-stone-700 ml-1">Clear</button>
            )}
          </div>
          <div className="px-6 py-3 border-b border-stone-200 bg-white flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mr-1">Impacted Dept</div>
            {IMPACTED_DEPARTMENTS.map(d => {
              const sel = selectedImpDepts.has(d);
              return (
                <button
                  key={d}
                  onClick={() => toggleImpDept(d)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${sel ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
                >
                  {d}
                </button>
              );
            })}
            {selectedImpDepts.size > 0 && (
              <button onClick={() => setSelectedImpDepts(new Set())} className="text-[10px] text-stone-500 hover:text-stone-700 ml-1">Clear</button>
            )}
          </div>
        </>
      )}

      {/* ---- Severity + Overdue toggle row ---- */}
      <div className="px-6 py-3 border-b border-stone-200 bg-white flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mr-1">Show</div>
        <SeverityToggle label="Critical" count={stats.critical} active={sev.critical} dotClass="bg-red-600"
          onClick={() => setSev(s => ({ ...s, critical: !s.critical }))}/>
        <SeverityToggle label="High" count={stats.high} active={sev.high} dotClass="bg-orange-500"
          onClick={() => setSev(s => ({ ...s, high: !s.high }))}/>
        <SeverityToggle label="Medium" count={stats.medium} active={sev.medium} dotClass="bg-amber-400"
          onClick={() => setSev(s => ({ ...s, medium: !s.medium }))}/>
        <SeverityToggle label="Low" count={stats.low} active={sev.low} dotClass="bg-emerald-500"
          onClick={() => setSev(s => ({ ...s, low: !s.low }))}/>
        <div className="w-px h-5 bg-stone-200 mx-1"/>
        <button
          onClick={() => setShowOverdue(o => !o)}
          className={`text-xs px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${showOverdue ? 'bg-amber-50 text-amber-800 border-amber-300' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'}`}
        >
          <Clock className="w-3 h-3"/>
          Overdue section
          <span className={`text-[10px] font-mono ${showOverdue ? 'text-amber-700' : 'text-stone-400'}`}>{stats.overdue}</span>
        </button>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="border-l-2 border-stone-300 pl-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Total Risks</div>
            <div className="font-serif text-2xl text-stone-900">{stats.total}</div>
          </div>
          <div className="border-l-2 border-red-300 pl-3">
            <div className="text-[10px] uppercase tracking-widest text-red-700">Critical</div>
            <div className="font-serif text-2xl text-red-700">{stats.critical}</div>
          </div>
          <div className="border-l-2 border-orange-300 pl-3">
            <div className="text-[10px] uppercase tracking-widest text-orange-700">High</div>
            <div className="font-serif text-2xl text-orange-700">{stats.high}</div>
          </div>
          <div className="border-l-2 border-amber-400 pl-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-800">Overdue</div>
            <div className="font-serif text-2xl text-amber-700">{stats.overdue}</div>
          </div>
        </div>

        <div className="text-stone-700 mb-4 leading-relaxed">
          <p className="mb-2">
            This report covers <strong>{stats.total}</strong> active risks visible to {user.displayName || user.upn} (role: <code className="text-xs bg-stone-100 px-1 rounded">{user.role}</code>).
            Of these, <strong>{stats.critical}</strong> are scored critical (16+) and require immediate executive attention.
            {stats.high > 0 && <> {stats.high} risks are rated high (10-15) and should be reviewed at the next governance meeting.</>}
            {stats.overdue > 0 && <> <strong>{stats.overdue}</strong> risk{stats.overdue === 1 ? ' is' : 's are'} past target date.</>}
          </p>
        </div>

        {/* ---- Main risk list (severity-filtered) ---- */}
        <div className="space-y-2">
          {scopedRisks.length === 0 && (
            <div className="text-sm text-stone-500 text-center py-8">No risks match this report under your access level.</div>
          )}
          {scopedRisks.length > 0 && filteredRisks.length === 0 && (
            <div className="text-sm text-stone-500 text-center py-8">All {scopedRisks.length} risks are filtered out by the severity toggles.</div>
          )}
          {filteredRisks.map(r => <ReportRiskRow key={r.id} risk={r}/>)}
        </div>

        {/* ---- Overdue Actions section (Item #4 — appended to every report) ---- */}
        {showOverdue && overdueRisks.length > 0 && (
          <div className="mt-6 pt-6 border-t-2 border-dashed border-amber-300">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-amber-700"/>
              <h4 className="font-serif text-lg text-stone-900">Overdue Actions</h4>
              <span className="text-xs text-amber-700/80">{overdueRisks.length} risk{overdueRisks.length === 1 ? '' : 's'} past target date in this report's scope</span>
            </div>
            <div className="space-y-2">
              {overdueRisks.map(r => <ReportRiskRow key={`od-${r.id}`} risk={r} showDays/>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SeverityToggle({ label, count, active, dotClass, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${active ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'}`}
    >
      <span className={`w-2 h-2 rounded-full ${dotClass} ${active ? '' : 'opacity-40'}`}/>
      {label}
      <span className={`text-[10px] font-mono ${active ? 'text-stone-300' : 'text-stone-400'}`}>{count}</span>
    </button>
  );
}

function ReportRiskRow({ risk, showDays }) {
  const r = risk;
  const overdue = isOverdue(r.td);
  const goalsCount = (r.goals || []).length;
  const deptsCount = (r.impacted_departments || []).length;
  // Border colour reflects the WORST of initial / residual so the visual
  // weight matches the more concerning reading.
  const worstScore = Math.max(r.s || 0, r.rs || 0);
  return (
    <div className="border-l-4 pl-3 py-1" style={{ borderColor: worstScore >= 16 ? '#dc2626' : worstScore >= 10 ? '#f97316' : worstScore >= 5 ? '#fbbf24' : '#10b981' }}>
      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
        <code className="text-[10px] font-mono bg-stone-100 px-1.5 py-0.5 rounded">#{r.serial}</code>
        <span className={`text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded ${scoreColor(r.s)}`} title="Initial score (pre-mitigation)">Initial {r.s || '—'} · {scoreLabel(r.s)}</span>
        <span className={`text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded ${scoreColor(r.rs)}`} title="Residual score (post-mitigation)">Residual {r.rs || '—'} · {scoreLabel(r.rs)}</span>
        <span className="text-[10px] uppercase text-stone-500">{r.dept}</span>
        {r.tier && <span className="text-[10px] uppercase text-stone-400">· {r.tier}</span>}
        {(goalsCount > 0 || deptsCount > 0) && (
          <ImpactRagBadges goalsCount={goalsCount} deptsCount={deptsCount} size="sm"/>
        )}
        {overdue && <span className="text-[10px] uppercase font-semibold tracking-wider bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Overdue</span>}
      </div>
      <div className="text-sm text-stone-800 leading-snug">{r.desc}</div>
      <div className="text-xs text-stone-500 mt-1 flex gap-3 flex-wrap">
        <span>Owner: {r.owner || '—'}</span>
        <span>Target: {fmtDate(r.td)}</span>
        {goalsCount > 0 && <span>Goals: {(r.goals || []).join(', ')}</span>}
        {deptsCount > 0 && <span>Depts: {(r.impacted_departments || []).join(', ')}</span>}
        {showDays && r.td && r.td !== 'Ongoing' && overdue && (
          <span className="text-amber-700">{Math.ceil((new Date() - new Date(r.td)) / (1000 * 60 * 60 * 24))} days overdue</span>
        )}
      </div>
    </div>
  );
}

// ============================================================
// HIGH RISKS BOARD REPORT — Critical (16+) and overdue risks,
// with tier and department filter pills, and a petrol gauge
// summarising the volume of high-priority items in scope.
// Fed local proto risks (already RLS-scoped, already non-archived)
// from ReportsView so all filtering is instant client-side.
// ============================================================
function HighRisksBoardReport({ risks: protoRisks, user, departments }) {
  // Multi-select pill state. Empty Set = no filter on that dimension
  // ("All" effectively selected). Clicking "All" clears the set.
  const [tierSet, setTierSet] = useState(() => new Set());
  const [deptSet, setDeptSet] = useState(() => new Set());

  // Severity + overdue toggles. Defaults: only Critical Residual + Overdue
  // are on. The board's headline view = "things still bad after mitigation
  // OR past their target date". Critical Initial / High are off-by-default
  // but available for exploration.
  const [showCriticalInit, setShowCriticalInit] = useState(false);
  const [showCriticalRes, setShowCriticalRes] = useState(true);
  const [showHigh, setShowHigh] = useState(false);
  const [showOverdue, setShowOverdue] = useState(true);

  // Status Updates pill (Item 4): when on, pulls the latest
  // risk_status_updates entry per visible risk and renders it inline below.
  const [showUpdates, setShowUpdates] = useState(false);
  const [updatesByRiskId, setUpdatesByRiskId] = useState({}); // { [riskId]: latestUpdateOrNull, undefined = not yet loaded }
  const [loadingUpdates, setLoadingUpdates] = useState(false);

  const toggleTier = (t) => setTierSet(prev => {
    const n = new Set(prev);
    if (n.has(t)) n.delete(t); else n.add(t);
    return n;
  });
  const toggleDept = (d) => setDeptSet(prev => {
    const n = new Set(prev);
    if (n.has(d)) n.delete(d); else n.add(d);
    return n;
  });
  const clearTiers = () => setTierSet(new Set());
  const clearDepts = () => setDeptSet(new Set());

  // Apply tier + dept filters (within-dim OR, across-dim AND).
  const scoped = useMemo(() => {
    let r = protoRisks;
    if (tierSet.size > 0) r = r.filter(x => tierSet.has(x.tier));
    if (deptSet.size > 0) r = r.filter(x => deptSet.has(x.dept));
    return r;
  }, [protoRisks, tierSet, deptSet]);

  const criticalInit = useMemo(() => scoped.filter(r => (r.s || 0) >= 16), [scoped]);
  const criticalRes = useMemo(() => scoped.filter(r => (r.rs || 0) >= 16), [scoped]);
  const high = useMemo(() => scoped.filter(r => (r.s || 0) >= 10 && (r.s || 0) < 16), [scoped]);
  const overdueRisks = useMemo(() => scoped.filter(r => isOverdue(r.td)), [scoped]);

  // Combined unique list: union of the enabled buckets (Critical Initial /
  // Critical Residual / High / Overdue). De-duped by id, sorted score desc.
  const combined = useMemo(() => {
    const m = new Map();
    if (showCriticalInit) criticalInit.forEach(r => m.set(r.id, r));
    if (showCriticalRes) criticalRes.forEach(r => { if (!m.has(r.id)) m.set(r.id, r); });
    if (showHigh) high.forEach(r => { if (!m.has(r.id)) m.set(r.id, r); });
    if (showOverdue) overdueRisks.forEach(r => { if (!m.has(r.id)) m.set(r.id, r); });
    return Array.from(m.values()).sort((a, b) => (b.s || 0) - (a.s || 0));
  }, [criticalInit, criticalRes, high, overdueRisks, showCriticalInit, showCriticalRes, showHigh, showOverdue]);

  // Fetch latest status update for each risk in the combined list when the
  // Status Updates pill is on. Skip risks already in the cache. Errors per
  // risk are caught individually so one bad row doesn't hide the rest.
  useEffect(() => {
    if (!showUpdates) return;
    const ids = combined.map(r => r.id).filter(id => !(id in updatesByRiskId));
    if (ids.length === 0) return;
    let cancelled = false;
    setLoadingUpdates(true);
    Promise.all(
      ids.map(id =>
        apiFetch(`/risks/${id}/status-updates`)
          .then(d => ({ id, latest: (d.updates && d.updates[0]) || null }))
          .catch(() => ({ id, latest: null }))
      )
    ).then(results => {
      if (cancelled) return;
      setUpdatesByRiskId(prev => {
        const next = { ...prev };
        results.forEach(({ id, latest }) => { next[id] = latest; });
        return next;
      });
      setLoadingUpdates(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpdates, combined]);

  const gaugeMax = 30;

  const tierLabel = tierSet.size === 0 ? 'All tiers' : Array.from(tierSet).join(', ');
  const deptLabel = deptSet.size === 0 ? 'All departments' : Array.from(deptSet).join(', ');
  const showLabels = [showCriticalInit && 'Critical Initial', showCriticalRes && 'Critical Residual', showHigh && 'High', showOverdue && 'Overdue'].filter(Boolean);

  return (
    <>
      {/* Print stylesheet — isolates the report container so window.print()
          / Save as PDF produces a clean, single-document output. */}
      <style>{`
        .hrbr-print-only { display: none; }
        @media print {
          @page { size: A4 portrait; margin: 14mm 12mm; }
          body * { visibility: hidden !important; }
          #high-risks-board-print, #high-risks-board-print * { visibility: visible !important; }
          #high-risks-board-print {
            position: absolute !important;
            top: 0; left: 0;
            width: 100%;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: white !important;
          }
          .hrbr-no-print { display: none !important; }
          .hrbr-print-only { display: block !important; }
          .hrbr-risk-row { page-break-inside: avoid; break-inside: avoid; }
          .hrbr-print-block { page-break-inside: avoid; }
        }
      `}</style>

      <div id="high-risks-board-print" className="bg-white rounded-lg border border-stone-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-stone-200 flex items-center bg-stone-50">
        <div>
          <h3 className="font-serif text-xl text-stone-900">High Risks Board Report</h3>
          <div className="text-xs text-stone-500">
            Generated {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} · {combined.length} item{combined.length === 1 ? '' : 's'} in scope
          </div>
          {/* Print-only scope banner so the PDF is self-describing */}
          <div className="hrbr-print-only text-[11px] text-stone-700 mt-1 leading-snug">
            <strong>North Yorkshire Moors Railway</strong> — Risk Register board report.
            Tiers: {tierLabel}. Departments: {deptLabel}. Showing: {showLabels.length ? showLabels.join(', ') : '— nothing (all bucket toggles off)'}.
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2 hrbr-no-print">
          <button
            onClick={() => window.print()}
            className="text-xs px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5"
            title="Opens print dialog — choose Save as PDF to export"
          >
            <Download className="w-3.5 h-3.5"/>Export PDF
          </button>
        </div>
      </div>

      {/* Tier filter pills (multi-select) */}
      <div className="hrbr-no-print px-6 py-3 border-b border-stone-200 bg-white flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mr-1">Tier</div>
        <button
          onClick={clearTiers}
          className={`text-xs px-2.5 py-1 rounded-full border transition ${tierSet.size === 0 ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
        >
          All
        </button>
        {['Trust', 'PLC'].map(t => {
          const sel = tierSet.has(t);
          return (
            <button
              key={t}
              onClick={() => toggleTier(t)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${sel ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
            >
              {t}
            </button>
          );
        })}
      </div>

      {/* Department filter pills (multi-select) */}
      <div className="hrbr-no-print px-6 py-3 border-b border-stone-200 bg-white flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mr-1">Department</div>
        <button
          onClick={clearDepts}
          className={`text-xs px-2.5 py-1 rounded-full border transition ${deptSet.size === 0 ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
        >
          All
        </button>
        {departments.map(d => {
          const sel = deptSet.has(d);
          return (
            <button
              key={d}
              onClick={() => toggleDept(d)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${sel ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
            >
              {d}
            </button>
          );
        })}
      </div>

      {/* Severity + overdue + status-updates toggle row */}
      <div className="hrbr-no-print px-6 py-3 border-b border-stone-200 bg-white flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mr-1">Show</div>
        <SeverityToggle label="Critical Initial" count={criticalInit.length} active={showCriticalInit} dotClass="bg-red-600"
          onClick={() => setShowCriticalInit(s => !s)}/>
        <SeverityToggle label="Critical Residual" count={criticalRes.length} active={showCriticalRes} dotClass="bg-red-700"
          onClick={() => setShowCriticalRes(s => !s)}/>
        <SeverityToggle label="High" count={high.length} active={showHigh} dotClass="bg-orange-500"
          onClick={() => setShowHigh(s => !s)}/>
        <div className="w-px h-5 bg-stone-200 mx-1"/>
        <button
          onClick={() => setShowOverdue(o => !o)}
          className={`text-xs px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${showOverdue ? 'bg-amber-50 text-amber-800 border-amber-300' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'}`}
        >
          <Clock className="w-3 h-3"/>
          Overdue
          <span className={`text-[10px] font-mono ${showOverdue ? 'text-amber-700' : 'text-stone-400'}`}>{overdueRisks.length}</span>
        </button>
        <div className="w-px h-5 bg-stone-200 mx-1"/>
        <button
          onClick={() => setShowUpdates(u => !u)}
          className={`text-xs px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${showUpdates ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'}`}
          title="Show the latest status update under each risk"
        >
          <MessageSquare className="w-3 h-3"/>
          Status updates
          {loadingUpdates && <Loader2 className="w-3 h-3 animate-spin"/>}
        </button>
      </div>

      <div className="p-6">
        {/* Petrol gauge + stat cards */}
        <div className="hrbr-print-block grid grid-cols-12 gap-6 mb-6 items-center">
          <div className="col-span-5 flex items-center justify-center">
            <PetrolGauge value={combined.length} max={gaugeMax} label="High-Priority Risks"/>
          </div>
          <div className="col-span-7 grid grid-cols-2 gap-4">
            <div className="border-l-2 border-red-300 pl-3">
              <div className="text-[10px] uppercase tracking-widest text-red-700">Critical Residual (16+)</div>
              <div className="font-serif text-2xl text-red-700">{criticalRes.length}</div>
            </div>
            <div className="border-l-2 border-red-200 pl-3">
              <div className="text-[10px] uppercase tracking-widest text-red-700/70">Critical Initial (16+)</div>
              <div className="font-serif text-2xl text-red-700/70">{criticalInit.length}</div>
            </div>
            <div className="border-l-2 border-orange-300 pl-3">
              <div className="text-[10px] uppercase tracking-widest text-orange-700">High (10–15)</div>
              <div className="font-serif text-2xl text-orange-700">{high.length}</div>
            </div>
            <div className="border-l-2 border-amber-400 pl-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-800">Overdue</div>
              <div className="font-serif text-2xl text-amber-700">{overdueRisks.length}</div>
            </div>
          </div>
        </div>

        <div className="text-stone-700 mb-4 leading-relaxed text-sm">
          <p>
            This report shows <strong>{combined.length}</strong> risk{combined.length === 1 ? '' : 's'} matching:&nbsp;
            {showLabels.length === 0 ? <em>nothing — all severity / overdue toggles are off</em> : <strong>{showLabels.join(' + ')}</strong>}
            .&nbsp;Tiers: {tierLabel}. Departments: {deptLabel}.
          </p>
        </div>

        <div className="space-y-2">
          {combined.length === 0 ? (
            <div className="text-sm text-stone-500 text-center py-8">No risks match the current filter combination.</div>
          ) : (
            combined.map(r => {
              const u = updatesByRiskId[r.id];
              return (
                <div key={r.id} className="hrbr-risk-row">
                  <ReportRiskRow risk={r} showDays/>
                  {showUpdates && (
                    <div className="ml-3 mt-1 mb-2 pl-3 border-l border-stone-200 text-xs text-stone-600">
                      {u === undefined && <span className="text-stone-400 italic">Loading update…</span>}
                      {u === null && <span className="text-stone-400 italic">No status updates posted.</span>}
                      {u && (
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-0.5">
                            Latest update · {new Date(u.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            {u.author_name ? ` · ${u.author_name}` : ''}
                          </div>
                          <div className="text-stone-700 leading-snug whitespace-pre-wrap">{u.content}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      </div>
    </>
  );
}

// ============================================================
// PETROL GAUGE — semi-circle SVG gauge with green/amber/orange/red
// zones and a needle. Domain 0..max maps to math-angle 180°..0°
// (left to right across the top of the arc).
// ============================================================
function PetrolGauge({ value, max, label }) {
  const safeMax = Math.max(1, max || 1);
  const v = Math.max(0, Math.min(safeMax, value || 0));
  const cx = 100, cy = 100, r = 80;

  // Generate an SVG arc from startMathDeg to endMathDeg, where math angles
  // use 0° = right, 90° = top, 180° = left. SVG y is flipped (down=positive),
  // hence (cy - r*sin). For the top-half gauge we use sweep-flag=0.
  const arcPath = (startMathDeg, endMathDeg) => {
    const sRad = startMathDeg * Math.PI / 180;
    const eRad = endMathDeg * Math.PI / 180;
    const x1 = cx + r * Math.cos(sRad);
    const y1 = cy - r * Math.sin(sRad);
    const x2 = cx + r * Math.cos(eRad);
    const y2 = cy - r * Math.sin(eRad);
    return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
  };

  // Needle: 0 → left (180°), max → right (0°).
  const needleMathDeg = 180 - (v / safeMax) * 180;
  const needleRad = needleMathDeg * Math.PI / 180;
  const needleLen = r - 10;
  const needleX = cx + needleLen * Math.cos(needleRad);
  const needleY = cy - needleLen * Math.sin(needleRad);

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 130" width="240" height="156">
        {/* 4 equal coloured zones across the 180° arc */}
        <path d={arcPath(180, 135)} stroke="#10b981" strokeWidth="14" fill="none"/>
        <path d={arcPath(135, 90)}  stroke="#fbbf24" strokeWidth="14" fill="none"/>
        <path d={arcPath(90, 45)}   stroke="#f97316" strokeWidth="14" fill="none"/>
        <path d={arcPath(45, 0)}    stroke="#dc2626" strokeWidth="14" fill="none"/>
        {/* Needle */}
        <line x1={cx} y1={cy} x2={needleX.toFixed(2)} y2={needleY.toFixed(2)} stroke="#1c1917" strokeWidth="3" strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r="6" fill="#1c1917"/>
        {/* Tick labels */}
        <text x="20" y="118" fontSize="9" fill="#78716c" textAnchor="middle">0</text>
        <text x="100" y="14" fontSize="9" fill="#78716c" textAnchor="middle">{Math.round(safeMax / 2)}</text>
        <text x="180" y="118" fontSize="9" fill="#78716c" textAnchor="middle">{safeMax}+</text>
      </svg>
      <div className="-mt-3 text-center">
        <div className="font-serif text-3xl text-stone-900">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      </div>
    </div>
  );
}

// ============================================================
// AUDIT VIEW
// ============================================================
function AuditView() {
  const PAGE_SIZE = 200;
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [serialFilter, setSerialFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('ManualChange');
  const [userFilter, setUserFilter] = useState('');

  // Build audit URL with offset + optional change_type query param.
  // typeFilter === 'all' means no server-side filter (return all rows).
  const buildAuditUrl = (offset) => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (typeFilter !== 'all') params.set('change_type', typeFilter);
    return `/audit?${params.toString()}`;
  };

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(buildAuditUrl(0));
      const got = data.entries || [];
      setEntries(got);
      setHasMore(got.length === PAGE_SIZE);
    } catch (e) {
      setError(e.message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
    // buildAuditUrl reads typeFilter so it must be in the dep list to
    // re-fetch when the user clicks a different pill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const data = await apiFetch(buildAuditUrl(entries.length));
      const got = data.entries || [];
      setEntries(prev => [...prev, ...got]);
      setHasMore(got.length === PAGE_SIZE);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  // Distinct change types in loaded set, for the filter pills.
  // The server-side filter scopes results, so this set will shrink when
  // typeFilter is restrictive. The pill row uses CORE_PILL_TYPES below as
  // the canonical list and only adds extras from distinctTypes that aren't
  // already in the core set.
  const distinctTypes = Array.from(new Set(entries.map(a => a.change_type))).sort();
  const CORE_PILL_TYPES = ['ManualChange', 'Change', 'UserChange', 'LookupChange'];
  const extraTypes = distinctTypes.filter(t => t && !CORE_PILL_TYPES.includes(t));
  const pillTypes = [...CORE_PILL_TYPES, ...extraTypes];

  // Client-side filter handles only serial + user. The change_type filter
  // is applied server-side via the change_type query param so pagination
  // pages through filtered results.
  const filtered = entries.filter(a => {
    if (serialFilter.trim()) {
      const q = serialFilter.trim().toLowerCase();
      if (!(a.risk_serial || '').toLowerCase().includes(q)) return false;
    }
    if (userFilter.trim()) {
      const q = userFilter.trim().toLowerCase();
      const userBlob = `${a.user_name || ''} ${a.user_upn || ''}`.toLowerCase();
      if (!userBlob.includes(q)) return false;
    }
    return true;
  });

  const typePillClass = (t) => {
    if (t === 'ManualChange') return 'bg-amber-600 text-white';
    if (t === 'New') return 'bg-emerald-100 text-emerald-700';
    if (t === 'Pending') return 'bg-amber-100 text-amber-700';
    if (t === 'Archive' || t === 'Restore') return 'bg-orange-100 text-orange-700';
    if (t === 'UserChange' || t === 'LookupChange') return 'bg-indigo-100 text-indigo-700';
    return 'bg-stone-100 text-stone-700';
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex items-end gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl text-stone-900">Audit Log</h2>
          <div className="text-sm text-stone-500">Immutable record of all changes · stored in append-only Azure SQL table (dbo.audit_log)</div>
        </div>
        <div className="text-sm text-stone-600 ml-auto">
          {loading ? 'Loading…' : `Showing ${filtered.length} of ${entries.length} loaded${hasMore ? '+' : ''}`}
        </div>
      </div>

      <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-stone-700 flex-shrink-0 mt-0.5"/>
        <div className="text-sm text-stone-700">
          <div className="font-medium mb-1">Tamper-evident audit trail</div>
          <div className="text-xs text-stone-600">Entries can only be appended via stored procedure. No user — including admins — can edit historic entries. This satisfies Charity Commission good-practice expectations for risk register integrity.</div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-stone-200 rounded-lg p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">Risk #</span>
          <input
            type="text"
            value={serialFilter}
            onChange={e => setSerialFilter(e.target.value)}
            placeholder="e.g. 12 or 33/43"
            className="text-sm px-2 py-1 border border-stone-200 rounded focus:outline-none focus:border-amber-600 w-32"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">User</span>
          <input
            type="text"
            value={userFilter}
            onChange={e => setUserFilter(e.target.value)}
            placeholder="name or initials"
            className="text-sm px-2 py-1 border border-stone-200 rounded focus:outline-none focus:border-amber-600 w-40"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mr-1">Type</span>
          <button
            type="button"
            onClick={() => setTypeFilter('all')}
            className={`text-[11px] uppercase font-semibold tracking-wider px-2 py-1 rounded-full border transition ${
              typeFilter === 'all' ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
            }`}
          >
            All
          </button>
          {pillTypes.map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTypeFilter(t)}
              className={`text-[11px] uppercase font-semibold tracking-wider px-2 py-1 rounded-full border transition ${
                typeFilter === t ? typePillClass(t) + ' border-current' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        {(serialFilter || userFilter || typeFilter !== 'ManualChange') && (
          <button
            type="button"
            onClick={() => { setSerialFilter(''); setUserFilter(''); setTypeFilter('ManualChange'); }}
            className="ml-auto text-xs text-stone-500 hover:text-stone-800"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3">{error}</div>
      )}

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-stone-500 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading audit entries…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-stone-500 text-sm">
            {entries.length === 0 ? 'No audit entries visible to your role.' : 'No entries match the current filters.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-32">When</th>
                <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-16">Risk</th>
                <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-24">Type</th>
                <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2">Outline</th>
                <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2">Action Taken</th>
                <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-40">User</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="border-b border-stone-100 hover:bg-stone-50 align-top">
                  <td className="px-3 py-2.5 text-stone-700 text-xs whitespace-nowrap">
                    {a.changed_at ? new Date(a.changed_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    {a.risk_serial ? <code className="text-[11px] font-mono bg-stone-100 px-1.5 py-0.5 rounded text-stone-700">#{a.risk_serial}</code> : <span className="text-stone-400 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${typePillClass(a.change_type)}`}>{a.change_type}</span>
                  </td>
                  <td className="px-3 py-2.5 text-stone-700 text-xs leading-snug">{a.outline}</td>
                  <td className="px-3 py-2.5 text-stone-600 text-xs leading-snug">{a.action_taken || '—'}</td>
                  <td className="px-3 py-2.5 text-stone-600 text-xs">{a.user_name || a.user_upn || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {hasMore && !loading && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="text-sm px-4 py-2 border border-stone-300 hover:border-stone-500 hover:bg-stone-50 rounded-md flex items-center gap-2 disabled:opacity-50"
          >
            {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <ChevronDown className="w-3.5 h-3.5"/>}
            {loadingMore ? 'Loading…' : `Load more (±${PAGE_SIZE} more)`}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ADMIN VIEW — pulls from /api/users (rich rows from dbo.users)
// ============================================================
function AdminView({ showToast, onLookupChange, onUsersChanged, directory = [] }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loadingState, setLoadingState] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const reload = useCallback(async () => {
    try {
      const fresh = await apiFetch('/users');
      setData(fresh);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingState(false);
    }
  }, []);

  // After a user mutation, refresh the users table AND ask the app shell to
  // re-pull risks + directory. Archive (reassigns owner/reviewer on every
  // active risk), UPN edits (cascade to risks) and pause/resume (directory
  // membership) all change data that other views were still showing stale —
  // e.g. an archived user's risks kept displaying their name until a manual
  // page reload.
  const afterUserMutation = useCallback(async () => {
    await reload();
    onUsersChanged?.();
  }, [reload, onUsersChanged]);

  useEffect(() => { reload(); }, [reload]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await reload();
      showToast?.('User list refreshed.', 'success');
    } finally {
      setRefreshing(false);
    }
  };

  const setStatus = async (upn, action) => {
    setPendingAction(upn);
    try {
      await apiFetch(`/users/${encodeURIComponent(upn)}/${action}`, { method: 'POST' });
      await afterUserMutation();
      showToast?.(`User ${action === 'pause' ? 'paused' : 'resumed'}.`, 'success');
    } catch (e) {
      showToast?.(`${action} failed: ${e.message}`, 'error');
    } finally {
      setPendingAction(null);
    }
  };

  if (loadingState && !data) return <div className="p-6 flex items-center text-stone-500"><Loader2 className="w-4 h-4 animate-spin mr-2"/>Loading users…</div>;
  if (error) return <div className="p-6 text-sm text-red-700">{error}</div>;
  if (!data) return null;

  const users = data.users || [];
  const departments = (data.departments || []).map(d => d.name);
  const roleNames = ['admin', 'risk_owner', 'readonly'];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-stone-900">Users & Roles</h2>
        <div className="text-sm text-stone-500">Manage who can sign in, what they can edit, and which tier registers they see. Visibility is enforced by Azure SQL Row-Level Security.</div>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 bg-stone-50 flex items-center">
          <h3 className="font-serif text-lg text-stone-900">Users ({users.length})</h3>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={handleRefresh} disabled={refreshing} className="text-xs px-3 py-1.5 bg-white border border-stone-200 hover:border-stone-300 rounded-md flex items-center gap-1.5 disabled:opacity-60">
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5"/>}
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <button onClick={() => setShowAddUser(true)} className="text-xs px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5"/>Add User
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-stone-200">
            <tr>
              <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-4 py-2">User</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-4 py-2">Department</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-4 py-2">Role</th>
              <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-4 py-2">Tiers</th>
              <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-4 py-2">Status</th>
              <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-4 py-2">Risks</th>
              <th className="text-right text-[10px] uppercase tracking-widest text-stone-500 font-medium px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const statusColor = u.status === 'active' ? 'bg-emerald-100 text-emerald-700'
                : u.status === 'paused' ? 'bg-amber-100 text-amber-700'
                : 'bg-stone-200 text-stone-600';
              const isPending = pendingAction === u.upn;
              return (
                <tr key={u.upn} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-700 to-stone-600 flex items-center justify-center text-stone-50 text-xs font-medium">
                        {((u.first_name || '?')[0] + (u.last_name || '?')[0]).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-stone-900 text-sm">{u.display_name || `${u.first_name} ${u.last_name}`}</div>
                        <div className="text-[11px] text-stone-500">{u.upn}</div>
                        {(u.implementation_titles || []).length > 0 && (
                          <div className="text-[10px] text-amber-800 mt-0.5" title={(u.implementation_titles || []).join(', ')}>
                            Implements: {(u.implementation_titles || []).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-wider text-stone-600 bg-stone-100 px-1.5 py-0.5 rounded">{u.department}</span></td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded ${u.role === 'admin' ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-stone-700'}`}>{u.role}</span></td>
                  <td className="px-4 py-3 text-center">
                    <div className="inline-flex items-center gap-1">
                      <TierBadge active={u.role === 'admin' || u.can_see_trust} label="T" title="Trust"/>
                      <TierBadge active={u.role === 'admin' || u.can_see_plc} label="P" title="PLC"/>
                      <TierBadge active={u.role === 'admin' || u.can_see_exec} label="E" title="Exec"/>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center"><span className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full ${statusColor}`}>{u.status}</span></td>
                  <td className="px-4 py-3 text-center text-stone-700">{u.risk_count}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setEditingUser(u)} disabled={isPending} className="text-xs px-2 py-1 hover:bg-stone-100 rounded text-stone-700 disabled:opacity-50" title="Edit user">Edit</button>
                      {u.status === 'active' && (
                        <button onClick={() => setStatus(u.upn, 'pause')} disabled={isPending} className="text-xs px-2 py-1 hover:bg-stone-100 rounded text-stone-600 disabled:opacity-50" title="Pause access (user cannot sign in)">
                          {isPending ? <Loader2 className="w-3 h-3 animate-spin"/> : 'Pause'}
                        </button>
                      )}
                      {u.status === 'paused' && (
                        <button onClick={() => setStatus(u.upn, 'resume')} disabled={isPending} className="text-xs px-2 py-1 hover:bg-emerald-50 rounded text-emerald-700 disabled:opacity-50" title="Resume access">
                          {isPending ? <Loader2 className="w-3 h-3 animate-spin"/> : 'Resume'}
                        </button>
                      )}
                      {u.status !== 'archived' && (
                        <button onClick={() => setArchiveTarget(u)} disabled={isPending} className="text-xs px-2 py-1 hover:bg-amber-50 rounded text-amber-700 disabled:opacity-50" title="Archive user (reassign risks)">Archive</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 p-5">
        <h3 className="font-serif text-lg text-stone-900 mb-1">Roles</h3>
        <div className="text-xs text-stone-500 mb-4">Roles control edit permissions only. Tier visibility (which registers a user can see) is now per-user via the checkboxes on Edit.</div>
        <table className="w-full text-sm">
          <thead className="border-b border-stone-200">
            <tr>
              <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2">Role</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2">Description</th>
            </tr>
          </thead>
          <tbody>
            {roleNames.map(name => (
              <tr key={name} className="border-b border-stone-100">
                <td className="px-3 py-2.5"><span className="text-xs uppercase tracking-wider font-semibold text-amber-800">{name}</span></td>
                <td className="px-3 py-2.5 text-stone-700 text-xs">{ROLE_DESCRIPTIONS[name] || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LookupManager showToast={showToast} onLookupChange={onLookupChange} directory={directory}/>

      {showAddUser && (
        <UserAddModal
          departments={departments}
          onClose={() => setShowAddUser(false)}
          onCreated={async () => { await afterUserMutation(); setShowAddUser(false); }}
          showToast={showToast}
        />
      )}
      {editingUser && (
        <UserEditModal
          user={editingUser}
          departments={departments}
          titles={data.titles || []}
          onClose={() => setEditingUser(null)}
          onSaved={async () => { await afterUserMutation(); setEditingUser(null); }}
          showToast={showToast}
        />
      )}
      {archiveTarget && (
        <UserArchiveModal
          user={archiveTarget}
          allUsers={users}
          onClose={() => setArchiveTarget(null)}
          onArchived={async () => { await afterUserMutation(); setArchiveTarget(null); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function TierBadge({ active, label, title }) {
  return (
    <span title={title} className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold ${active ? 'bg-stone-900 text-amber-300' : 'bg-stone-100 text-stone-400'}`}>{label}</span>
  );
}

function TierCheckbox({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="rounded"/>
      <span className="text-stone-700">{label}</span>
    </label>
  );
}

// Editable Lookups: Departments + Functions. Add and rename are wired to
// /api/departments and /api/functions. Rename of a department cascades to
// dbo.risks.department and dbo.users.department in a single transaction;
// rename of a function cascades to dbo.risks.function_category. Deletes are
// not exposed (DB convention is to set is_active=0 on out-of-use lookups).
function LookupManager({ showToast, onLookupChange, directory = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(null); // 'department' | 'function' | null
  const [renaming, setRenaming] = useState(null); // { kind, oldName }
  const [newName, setNewName] = useState('');
  const [working, setWorking] = useState(false);
  const [ownerBusy, setOwnerBusy] = useState(null); // impacted department name being saved

  // Owner of an Impacted Department = the person emailed when a risk is raised
  // (or edited) with that department selected. Saved immediately on change.
  const setImpactedOwner = async (name, upn) => {
    setOwnerBusy(name);
    try {
      await apiFetch(`/impacted-departments/${encodeURIComponent(name)}`, {
        method: 'PATCH',
        body: JSON.stringify({ owner_upn: upn || null })
      });
      showToast?.(upn ? `${name}: owner set — they will be notified of new risks impacting the department.` : `${name}: owner cleared.`, 'success');
      await reload();
    } catch (e) {
      showToast?.(`Owner update failed: ${e.message}`, 'error');
    } finally {
      setOwnerBusy(null);
    }
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const fresh = await apiFetch('/lookups');
      setData(fresh);
    } catch (e) {
      // Surface the failure instead of spinning forever (the old behaviour
      // when GET /api/lookups 500'd — see api/src/functions/lookups.ts).
      setLoadError(e.message || 'Failed to load lookups.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const submitAdd = async () => {
    if (!newName.trim()) return;
    setWorking(true);
    try {
      await apiFetch(`/${adding === 'department' ? 'departments' : 'functions'}`, {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim() })
      });
      showToast?.(`${adding === 'department' ? 'Department' : 'Function'} added.`, 'success');
      setAdding(null);
      setNewName('');
      await reload();
      onLookupChange?.();
    } catch (e) {
      showToast?.(`Add failed: ${e.message}`, 'error');
    } finally {
      setWorking(false);
    }
  };

  const submitRename = async () => {
    if (!newName.trim() || newName.trim() === renaming.oldName) {
      setRenaming(null); setNewName('');
      return;
    }
    setWorking(true);
    try {
      const route = renaming.kind === 'department' ? 'departments' : 'functions';
      await apiFetch(`/${route}/${encodeURIComponent(renaming.oldName)}`, {
        method: 'PATCH',
        body: JSON.stringify({ new_name: newName.trim() })
      });
      showToast?.(
        `${renaming.kind === 'department' ? 'Department' : 'Function'} renamed.`,
        'success'
      );
      setRenaming(null);
      setNewName('');
      await reload();
      onLookupChange?.();
    } catch (e) {
      showToast?.(`Rename failed: ${e.message}`, 'error');
    } finally {
      setWorking(false);
    }
  };

  if (loadError && !data) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-5 text-sm">
        <div className="flex items-start gap-2 text-red-700">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
          <div>
            <div className="font-medium">Lookups could not be loaded</div>
            <div className="text-xs text-red-600 mt-0.5">{loadError}</div>
          </div>
          <button onClick={reload} disabled={loading} className="ml-auto text-xs px-3 py-1.5 bg-white border border-stone-200 hover:border-stone-300 rounded-md flex items-center gap-1.5 text-stone-700 disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}/>Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="bg-white rounded-lg border border-stone-200 p-5 flex items-center text-stone-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2"/>Loading lookups…
      </div>
    );
  }

  const Column = ({ kind, title, items, secondaryCol }) => (
    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-200 bg-stone-50 flex items-center">
        <h4 className="font-serif text-base text-stone-900">{title}</h4>
        <button onClick={() => { setAdding(kind); setNewName(''); }} className="ml-auto text-xs px-2 py-1 bg-stone-900 text-stone-50 rounded flex items-center gap-1"><Plus className="w-3 h-3"/>Add</button>
      </div>
      <table className="w-full text-sm">
        <thead className="border-b border-stone-200">
          <tr>
            <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2">Name</th>
            <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-20">Risks</th>
            {secondaryCol && <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-20">Users</th>}
            <th className="w-16"/>
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr key={it.name} className="border-b border-stone-100 hover:bg-stone-50">
              <td className="px-3 py-2 text-stone-800 text-sm">{it.name}</td>
              <td className="px-3 py-2 text-center text-stone-700 text-xs">{it.risk_count}</td>
              {secondaryCol && <td className="px-3 py-2 text-center text-stone-700 text-xs">{it.user_count}</td>}
              <td className="px-3 py-2 text-right">
                <button
                  onClick={() => { setRenaming({ kind, oldName: it.name }); setNewName(it.name); }}
                  className="text-xs px-2 py-1 hover:bg-stone-100 rounded text-stone-700"
                  title="Rename (cascades to risks)"
                >
                  Rename
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={secondaryCol ? 4 : 3} className="px-3 py-6 text-center text-xs text-stone-500 italic">None.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div>
        <h3 className="font-serif text-lg text-stone-900 mb-1">Lookups</h3>
        <div className="text-xs text-stone-500 mb-4">Add new departments or functions. Renames cascade to all risks (and users, for departments). Existing values can’t be deleted; if a value falls out of use, simply rename it. Below, give each Impacted Department an owner so they are told when a risk lands on their area.</div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Column kind="department" title={`Departments (${data.departments.length})`} items={data.departments} secondaryCol/>
        <Column kind="function"   title={`Functions (${data.functions.length})`}     items={data.functions}/>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 bg-stone-50">
          <h4 className="font-serif text-base text-stone-900">Impacted departments — owners</h4>
          <div className="text-xs text-stone-500 mt-0.5">The owner receives an email and an in-app notification whenever a risk is raised, or edited, with their department selected under Impact Profile. Department names are fixed (they mirror the corporate scoring system); only the owner can be changed.</div>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-stone-200">
            <tr>
              <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2">Impacted department</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-96">Owner (notified)</th>
              <th className="text-center text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-20">Risks</th>
            </tr>
          </thead>
          <tbody>
            {(data.impacted_departments || []).map(d => (
              <tr key={d.name} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-3 py-2 text-stone-800 text-sm">
                  {d.name}
                  {d.description && <div className="text-[10px] text-stone-500 leading-snug">{d.description}</div>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <PersonPicker
                      value={d.owner_upn}
                      directory={directory}
                      placeholder="— No owner (nobody is notified) —"
                      onChange={({ upn }) => setImpactedOwner(d.name, upn)}
                    />
                    {ownerBusy === d.name && <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-500 flex-shrink-0"/>}
                  </div>
                </td>
                <td className="px-3 py-2 text-center text-stone-700 text-xs">{d.risk_count}</td>
              </tr>
            ))}
            {(data.impacted_departments || []).length === 0 && (
              <tr><td colSpan={3} className="px-3 py-6 text-center text-xs text-stone-500 italic">None.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={() => !working && setAdding(null)}>
          <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"/>
          <div onClick={e => e.stopPropagation()} className="relative w-full max-w-md bg-white rounded-lg shadow-2xl">
            <div className="px-6 py-4 border-b border-stone-200">
              <h3 className="font-serif text-xl text-stone-900">Add {adding === 'department' ? 'Department' : 'Function'}</h3>
              <div className="text-xs text-stone-500">It’ll be available immediately on the risk add/edit forms.</div>
            </div>
            <div className="p-6">
              <Field label="Name">
                <input value={newName} onChange={e => setNewName(e.target.value)} autoFocus className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/>
              </Field>
            </div>
            <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex justify-end gap-2">
              <button onClick={() => setAdding(null)} disabled={working} className="text-sm px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md disabled:opacity-50">Cancel</button>
              <button onClick={submitAdd} disabled={working || !newName.trim()} className="text-sm px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5 disabled:opacity-50">
                {working ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>}
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {renaming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={() => !working && setRenaming(null)}>
          <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"/>
          <div onClick={e => e.stopPropagation()} className="relative w-full max-w-md bg-white rounded-lg shadow-2xl">
            <div className="px-6 py-4 border-b border-stone-200">
              <h3 className="font-serif text-xl text-stone-900">Rename {renaming.kind === 'department' ? 'department' : 'function'}</h3>
              <div className="text-xs text-stone-500">Renaming “{renaming.oldName}” cascades to every risk{renaming.kind === 'department' ? ' and every user' : ''} that references it.</div>
            </div>
            <div className="p-6">
              <Field label="New name">
                <input value={newName} onChange={e => setNewName(e.target.value)} autoFocus className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/>
              </Field>
            </div>
            <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex justify-end gap-2">
              <button onClick={() => setRenaming(null)} disabled={working} className="text-sm px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md disabled:opacity-50">Cancel</button>
              <button onClick={submitRename} disabled={working || !newName.trim() || newName.trim() === renaming.oldName} className="text-sm px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5 disabled:opacity-50">
                {working ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---- User mgmt modals (wired to /api/users CRUD) ----

function UserAddModal({ departments, onClose, onCreated, showToast }) {
  const [form, setForm] = useState({
    upn: '',
    first_name: '',
    last_name: '',
    department: departments[0] || 'Corporate',
    role: 'risk_owner',
    can_see_trust: false,
    can_see_plc: false,
    can_see_exec: false
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.upn || !form.first_name || !form.last_name) {
      showToast?.('UPN, first name, and last name are all required.', 'error');
      return;
    }
    setSaving(true);
    try {
      await apiFetch('/users', { method: 'POST', body: JSON.stringify(form) });
      showToast?.(`User ${form.first_name} ${form.last_name} added.`, 'success');
      onCreated?.();
    } catch (e) {
      showToast?.(`Add failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"/>
      <div onClick={e => e.stopPropagation()} className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl">
        <div className="px-6 py-4 border-b border-stone-200">
          <h3 className="font-serif text-xl text-stone-900">Add User</h3>
          <div className="text-xs text-stone-500">User must already exist in Microsoft Entra ID. They'll also be auto-provisioned on first login if you skip this step.</div>
        </div>
        <div className="p-6 space-y-3">
          <Field label="UPN (email)"><input value={form.upn} onChange={e => setForm({ ...form, upn: e.target.value })} placeholder="firstname.surname@nymr.co.uk" className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name"><input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/></Field>
            <Field label="Last name"><input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department">
              <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Role">
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                {Object.keys(ROLE_DESCRIPTIONS).map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Tier register access">
            <div className="flex items-center gap-4 pt-1 flex-wrap">
              <TierCheckbox label="Trust" checked={form.can_see_trust} onChange={v => setForm({ ...form, can_see_trust: v })}/>
              <TierCheckbox label="PLC" checked={form.can_see_plc} onChange={v => setForm({ ...form, can_see_plc: v })}/>
              <TierCheckbox label="Exec" checked={form.can_see_exec} onChange={v => setForm({ ...form, can_see_exec: v })}/>
            </div>
            <div className="text-[10px] text-stone-500 mt-1">Department-tier access is implicit (matches the user's department).</div>
          </Field>
        </div>
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="text-sm px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="text-sm px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>}
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

function UserEditModal({ user, departments, titles = [], onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    upn: user.upn,
    new_upn: user.upn,
    first_name: user.first_name,
    last_name: user.last_name,
    department: user.department,
    role: user.role,
    can_see_trust: !!user.can_see_trust,
    can_see_plc: !!user.can_see_plc,
    can_see_exec: !!user.can_see_exec
  });
  // Implementation of Action titles this user holds (multi-select). Only titles
  // actually carried by a risk are offered; titles that are just another
  // user's name are excluded (those risks link to the person directly).
  const [heldTitles, setHeldTitles] = useState(() => [...(user.implementation_titles || [])]);
  const [titleFilter, setTitleFilter] = useState('');
  const [saving, setSaving] = useState(false);

  const titleRows = useMemo(() => {
    const held = new Set(heldTitles.map(t => t.toLowerCase()));
    const q = titleFilter.trim().toLowerCase();
    return titles
      .filter(t => t.title && !t.is_person_name)
      .filter(t => !q || t.title.toLowerCase().includes(q) || held.has(t.title.toLowerCase()))
      .sort((a, b) => {
        const ha = held.has(a.title.toLowerCase()) ? 0 : 1;
        const hb = held.has(b.title.toLowerCase()) ? 0 : 1;
        return ha - hb || (b.active_count || 0) - (a.active_count || 0) || a.title.localeCompare(b.title);
      });
  }, [titles, heldTitles, titleFilter]);
  const toggleTitle = (title) => setHeldTitles(prev => (
    prev.some(t => t.toLowerCase() === title.toLowerCase())
      ? prev.filter(t => t.toLowerCase() !== title.toLowerCase())
      : [...prev, title]
  ));
  const titlesChanged = useMemo(() => {
    const a = [...(user.implementation_titles || [])].map(t => t.toLowerCase()).sort();
    const b = [...heldTitles].map(t => t.toLowerCase()).sort();
    return a.length !== b.length || a.some((t, i) => t !== b[i]);
  }, [user.implementation_titles, heldTitles]);
  const heldRiskTotal = titleRows.filter(t => heldTitles.some(h => h.toLowerCase() === t.title.toLowerCase())).reduce((n, t) => n + (t.active_count || 0), 0);

  const submit = async () => {
    setSaving(true);
    try {
      // Only include new_upn in the payload if it actually differs.
      const body = { ...form };
      if (body.new_upn === user.upn) delete body.new_upn;
      delete body.upn;
      // Titles: full replacement set, only when changed. The server re-points
      // every risk carrying these titles to this person and emails them.
      if (titlesChanged) body.implementation_titles = heldTitles;
      await apiFetch(`/users/${encodeURIComponent(user.upn)}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      });
      showToast?.('User updated.', 'success');
      onSaved?.();
    } catch (e) {
      showToast?.(`Save failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"/>
      <div onClick={e => e.stopPropagation()} className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl">
        <div className="px-6 py-4 border-b border-stone-200">
          <h3 className="font-serif text-xl text-stone-900">Edit user</h3>
          <div className="text-xs text-stone-500">{user.upn}</div>
        </div>
        <div className="p-6 space-y-3">
          <Field label="UPN (email)">
            <input value={form.new_upn} onChange={e => setForm({ ...form, new_upn: e.target.value })} placeholder="firstname.surname@nymr.co.uk" className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/>
            <div className="text-[10px] text-stone-500 mt-1">Changing the UPN cascades to every risk this user owns or reviews. You can’t rename your own UPN.</div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name"><input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/></Field>
            <Field label="Last name"><input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department">
              <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Role">
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                {Object.keys(ROLE_DESCRIPTIONS).map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Tier register access">
            <div className="flex items-center gap-4 pt-1 flex-wrap">
              <TierCheckbox label="Trust" checked={form.can_see_trust} onChange={v => setForm({ ...form, can_see_trust: v })}/>
              <TierCheckbox label="PLC" checked={form.can_see_plc} onChange={v => setForm({ ...form, can_see_plc: v })}/>
              <TierCheckbox label="Exec" checked={form.can_see_exec} onChange={v => setForm({ ...form, can_see_exec: v })}/>
            </div>
            <div className="text-[10px] text-stone-500 mt-1">Admins implicitly see all tiers regardless of these flags.</div>
          </Field>
          <Field label={`Implementation of Action titles${heldTitles.length ? ` (${heldTitles.length} held · ${heldRiskTotal} active risk${heldRiskTotal === 1 ? '' : 's'})` : ''}`}>
            <div className="text-[10px] text-stone-500 mb-1.5">Tick every title this person carries. Every risk whose “Implementation of action” is one of these titles is then theirs to implement, and they are emailed. A title can only have one holder — ticking one held by someone else takes it over.</div>
            <div className="relative mb-1.5">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-stone-400 pointer-events-none"/>
              <input value={titleFilter} onChange={e => setTitleFilter(e.target.value)} placeholder="Filter titles…" className="w-full text-xs pl-7 pr-2 py-1.5 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/>
            </div>
            <div className="border border-stone-200 rounded-md max-h-52 overflow-y-auto divide-y divide-stone-100">
              {titleRows.length === 0 && <div className="px-3 py-4 text-xs text-stone-500 italic">No titles in use.</div>}
              {titleRows.map(t => {
                const held = heldTitles.some(h => h.toLowerCase() === t.title.toLowerCase());
                const otherHolder = t.holder_upn && t.holder_upn.toLowerCase() !== user.upn.toLowerCase() ? (t.holder_name || t.holder_upn) : null;
                return (
                  <label key={t.title} className={`flex items-start gap-2 px-3 py-1.5 cursor-pointer text-sm ${held ? 'bg-amber-50/60' : 'hover:bg-stone-50'}`}>
                    <input type="checkbox" checked={held} onChange={() => toggleTitle(t.title)} className="mt-0.5 rounded"/>
                    <span className="flex-1 min-w-0">
                      <span className="text-stone-800">{t.title}</span>
                      <span className="text-[11px] text-stone-500"> · {t.active_count ?? t.risk_count ?? 0} active risk{(t.active_count ?? t.risk_count) === 1 ? '' : 's'}</span>
                      {otherHolder && <span className={`text-[10px] ml-1.5 px-1.5 py-0.5 rounded ${held ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-stone-600'}`}>{held ? `taking over from ${otherHolder}` : `held by ${otherHolder}`}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </Field>
        </div>
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="text-sm px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="text-sm px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function UserArchiveModal({ user, allUsers, onClose, onArchived, showToast }) {
  const [reassignTo, setReassignTo] = useState('');
  const [saving, setSaving] = useState(false);
  const candidates = allUsers.filter(u => u.upn !== user.upn && u.status === 'active');
  const heldTitles = user.implementation_titles || [];
  // Risks (owner/reviewer) AND Implementation of Action titles both need a new home.
  const needsTarget = user.risk_count > 0 || heldTitles.length > 0;

  const submit = async () => {
    if (needsTarget && !reassignTo) {
      showToast?.('Pick someone to reassign their risks and titles to.', 'error');
      return;
    }
    setSaving(true);
    try {
      await apiFetch(`/users/${encodeURIComponent(user.upn)}/archive`, {
        method: 'POST',
        body: JSON.stringify({ reassignTo: reassignTo || undefined })
      });
      showToast?.(`User archived${user.risk_count > 0 ? ` and ${user.risk_count} risk${user.risk_count !== 1 ? 's' : ''} reassigned` : ''}.`, 'success');
      onArchived?.();
    } catch (e) {
      showToast?.(`Archive failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"/>
      <div onClick={e => e.stopPropagation()} className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl">
        <div className="px-6 py-4 border-b border-stone-200 flex items-start gap-3">
          <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center flex-shrink-0"><Lock className="w-5 h-5 text-amber-700"/></div>
          <div>
            <h3 className="font-serif text-xl text-stone-900">Archive {user.display_name}?</h3>
            <div className="text-xs text-stone-500">{user.upn}</div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {needsTarget ? (
            <>
              <p className="text-sm text-stone-700">
                {user.risk_count > 0 && <>This user owns or reviews <strong>{user.risk_count}</strong> active risk{user.risk_count !== 1 ? 's' : ''}. </>}
                {heldTitles.length > 0 && <>They hold {heldTitles.length === 1 ? 'the Implementation of Action title' : `${heldTitles.length} Implementation of Action titles`} <strong>{heldTitles.join(', ')}</strong> (every risk carrying {heldTitles.length === 1 ? 'it' : 'them'} moves too). </>}
                Pick someone to take {user.risk_count > 0 && heldTitles.length > 0 ? 'these' : user.risk_count > 0 ? 'them' : heldTitles.length === 1 ? 'it' : 'them'} over before archiving:
              </p>
              <Field label="Reassign risks to">
                <select value={reassignTo} onChange={e => setReassignTo(e.target.value)} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                  <option value="">— Select user —</option>
                  {candidates.map(u => <option key={u.upn} value={u.upn}>{u.display_name} ({u.upn})</option>)}
                </select>
              </Field>
            </>
          ) : (
            <p className="text-sm text-stone-700">This user has no active risks and holds no Implementation of Action titles. They can be archived without reassignment.</p>
          )}
          <div className="text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded-md p-3 leading-relaxed">
            Archive sets the user's status to <code className="font-mono text-[10px] bg-white px-1 rounded">archived</code> (they cannot sign in), reassigns every active risk where they are owner or reviewer, and moves their Implementation of Action titles (and the risks carrying them) to the new person, who is emailed a summary. The change is logged to the audit trail.
          </div>
        </div>
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="text-sm px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md disabled:opacity-50">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || (needsTarget && !reassignTo)}
            className="text-sm px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Lock className="w-3.5 h-3.5"/>}
            Archive user
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// RISK DETAIL PANEL
// ============================================================
// ============================================================
// IMPACT TAXONOMY UI COMPONENTS (Phase 7 Batch C)
// Shared between RiskDetailPanel, AddRiskModal, RisksTable, and Reports.
// ============================================================

/** Two compact RAG chips showing how broadly a risk impacts goals/depts. */
function ImpactRagBadges({ goalsCount, deptsCount, size = 'md' }) {
  const gRag = goalsRag(goalsCount);
  const dRag = impactedDeptsRag(deptsCount);
  const cls = size === 'sm'
    ? 'text-[10px] px-1.5 py-0.5 gap-1'
    : 'text-[11px] px-2 py-0.5 gap-1.5';
  return (
    <div className="flex items-center gap-1.5">
      <span
        title={`${goalsCount || 0}/5 NYMR Goals impacted — ${RAG_LABEL[gRag]}`}
        className={`inline-flex items-center rounded-full border font-medium ${cls} ${RAG_CHIP[gRag]}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${RAG_DOT[gRag]}`}/>
        Goals {goalsCount || 0}/5
      </span>
      <span
        title={`${deptsCount || 0}/8 Departments impacted — ${RAG_LABEL[dRag]}`}
        className={`inline-flex items-center rounded-full border font-medium ${cls} ${RAG_CHIP[dRag]}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${RAG_DOT[dRag]}`}/>
        Depts {deptsCount || 0}/8
      </span>
    </div>
  );
}

/** Two compact RAG dots — for use in dense table rows. */
function ImpactRagDots({ goalsCount, deptsCount }) {
  const gRag = goalsRag(goalsCount);
  const dRag = impactedDeptsRag(deptsCount);
  return (
    <div className="flex items-center gap-1.5" title={`Goals ${goalsCount || 0}/5 · Depts ${deptsCount || 0}/8`}>
      <span className={`inline-block w-2.5 h-2.5 rounded-full ring-1 ring-white ${RAG_DOT[gRag]}`}/>
      <span className={`inline-block w-2.5 h-2.5 rounded-full ring-1 ring-white ${RAG_DOT[dRag]}`}/>
    </div>
  );
}

/** Pill toggle group used for both Goals and Impacted Departments.
 *  In edit mode pills are tappable; in view mode they're rendered identically
 *  but disabled, so the user always sees exactly what's selected.
 */
function PillToggleGroup({ options, value, onChange, accent = 'amber', disabled = false }) {
  const sel = new Set(value || []);
  const baseSelected =
    accent === 'amber' ? 'bg-amber-600 text-white border-amber-700 shadow-sm'
      : accent === 'emerald' ? 'bg-emerald-600 text-white border-emerald-700 shadow-sm'
        : 'bg-stone-900 text-stone-50 border-stone-900 shadow-sm';
  const baseUnselected = disabled
    ? 'bg-stone-50 text-stone-400 border-stone-200'
    : 'bg-white text-stone-700 border-stone-300 hover:border-stone-500 hover:text-stone-900';
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(opt => {
        const isOn = sel.has(opt);
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (disabled || !onChange) return;
              const next = new Set(sel);
              if (next.has(opt)) next.delete(opt); else next.add(opt);
              onChange([...next]);
            }}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1.5 ${
              isOn ? baseSelected : baseUnselected
            } ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
            aria-pressed={isOn}
          >
            {isOn && <CheckCircle2 className="w-3 h-3"/>}
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/** Self-contained editor / viewer for Goals + Impacted Departments.
 *
 *  - In `editable=true` mode users can click pills to toggle.
 *  - In `editable=false` mode pills render the same way but are disabled —
 *    so the visual presentation stays consistent between view and edit.
 *  - Reads canonical lists from `goalsList` / `deptsList` (passed in by
 *    RiskDetailPanel/AddRiskModal from /api/lookups, falling back to the
 *    NYMR_GOALS / IMPACTED_DEPARTMENTS constants).
 */
function ImpactTaxonomyEditor({
  goals, impactedDepartments, onChange,
  editable = false,
  goalsList = NYMR_GOALS,
  deptsList = IMPACTED_DEPARTMENTS
}) {
  const setGoals = (next) => onChange?.({ goals: next, impacted_departments: impactedDepartments || [] });
  const setDepts = (next) => onChange?.({ goals: goals || [], impacted_departments: next });

  return (
    <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-600 font-semibold">Impact Profile</div>
        <ImpactRagBadges goalsCount={(goals||[]).length} deptsCount={(impactedDepartments||[]).length} size="sm"/>
        <span className="text-[10px] text-stone-400 ml-auto">
          {editable ? 'Tap to toggle. Select every goal/dept this risk would affect if it materialised.' : 'Read-only view'}
        </span>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">NYMR Goals</div>
          <span className="text-[10px] text-stone-400">— charitable objects this risk pushes against</span>
        </div>
        <PillToggleGroup
          options={goalsList}
          value={goals}
          onChange={setGoals}
          accent="amber"
          disabled={!editable}
        />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">Impacted Departments</div>
          <span className="text-[10px] text-stone-400">— functional areas affected (NOT the responsible department)</span>
        </div>
        <PillToggleGroup
          options={deptsList}
          value={impactedDepartments}
          onChange={setDepts}
          accent="emerald"
          disabled={!editable}
        />
      </div>
    </div>
  );
}

// ============================================================
// RISK MATRIX VIEWER (May 2026)
//
// Read-only stylised version of the corporate risk matrix grid
// (verbatim text from Risk_Matrix_Only.xlsx / RR_scoring_System.xlsx).
// Triggered from the "Risk Matrix Viewer" button on Risk Edit and
// Add-New-Risk modals so users can study the variances of every goal
// and theme across impact levels 1-5 before choosing a score.
// ============================================================
function RiskMatrixViewerModal({ onClose }) {
  const cellColors = {
    1: 'bg-emerald-50',
    2: 'bg-yellow-50',
    3: 'bg-amber-50',
    4: 'bg-orange-50',
    5: 'bg-red-50'
  };
  const headerColors = {
    1: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    2: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    3: 'bg-amber-100 text-amber-800 border-amber-200',
    4: 'bg-orange-100 text-orange-800 border-orange-200',
    5: 'bg-red-100 text-red-800 border-red-200'
  };
  const impactLabels = { 1: 'Negligible', 2: 'Minor', 3: 'Moderate', 4: 'Major', 5: 'Severe' };
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm"/>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-7xl bg-white rounded-lg shadow-2xl flex flex-col max-h-[92vh]"
      >
        <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-4">
          <div className="w-10 h-10 rounded-md bg-stone-900 text-stone-50 flex items-center justify-center">
            <LayoutGrid className="w-5 h-5"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Reference</div>
            <h3 className="font-serif text-xl text-stone-900">Risk Matrix Viewer</h3>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-xs text-stone-500 hidden md:block max-w-md text-right leading-snug">
              What each impact level means for every NYMR charitable goal and theme — use this to calibrate the Impact score you choose.
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded"
              aria-label="Close matrix viewer"
            >
              <X className="w-4 h-4 text-stone-600"/>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6">
          <div className="border border-stone-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
                <tr>
                  <th className="text-left text-[10px] uppercase tracking-widest text-stone-500 font-medium px-3 py-2 w-56 bg-stone-50 border-r border-stone-200">
                    Goal / Theme
                  </th>
                  {[1,2,3,4,5].map(n => (
                    <th
                      key={n}
                      className={`text-center text-[11px] font-semibold px-3 py-2 border-l border-b ${headerColors[n]}`}
                    >
                      <div className="flex flex-col items-center leading-tight">
                        <span className="text-base">{n}</span>
                        <span className="text-[9px] uppercase tracking-wider opacity-80">{impactLabels[n]}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {NYMR_GOALS.map(goalName => {
                  const def = GOAL_IMPACT_DESCRIPTORS[goalName];
                  if (!def) return null;
                  return (
                    <React.Fragment key={goalName}>
                      <tr className="bg-stone-100 border-b border-stone-200">
                        <td colSpan={6} className="px-3 py-2">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-serif text-sm text-stone-900">{goalName}</span>
                            <span className="text-[10px] text-stone-500 italic">{def.object}</span>
                          </div>
                        </td>
                      </tr>
                      {def.themes.map((t, idx) => (
                        <tr
                          key={goalName + t.theme}
                          className={`align-top ${idx === def.themes.length - 1 ? 'border-b-2 border-stone-200' : 'border-b border-stone-100'}`}
                        >
                          <td className="px-3 py-2.5 font-medium text-stone-700 bg-white border-r border-stone-200 leading-snug">
                            {t.theme}
                          </td>
                          {[1,2,3,4,5].map(n => (
                            <td
                              key={n}
                              className={`px-3 py-2.5 border-l border-stone-200 text-stone-800 leading-snug ${cellColors[n]}`}
                              style={{ minWidth: 160 }}
                            >
                              {t.levels[n] || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center gap-2">
          <div className="text-[11px] text-stone-500">Source: NYMR Risk Scoring System (RR_scoring_System.xlsx). Read-only reference.</div>
          <button
            onClick={onClose}
            className="ml-auto text-sm px-3 py-1.5 border border-stone-300 hover:bg-stone-100 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** Static text-box references to the corporate Risk Matrix scoring system.
 *  For each Goal the risk is tagged against, shows what the current Impact
 *  score (1-5) means for that goal's themes — verbatim from the
 *  RR_scoring_System.xlsx workbook.
 *
 *  If the risk has no Impact score yet, prompts the user to set one.
 */
function ImpactMatrixContext({ goals, impactScore }) {
  if (!goals || goals.length === 0) {
    return (
      <div className="bg-stone-50 border border-dashed border-stone-300 rounded-lg p-4 text-xs text-stone-500">
        Tag this risk with one or more <strong className="text-stone-700">NYMR Goals</strong> to see what its impact score means against each charitable object.
      </div>
    );
  }
  if (!impactScore || impactScore < 1 || impactScore > 5) {
    return (
      <div className="bg-stone-50 border border-dashed border-stone-300 rounded-lg p-4 text-xs text-stone-500">
        Set an Impact score (1–5) above to see what this means for each tagged goal.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">
        Risk Matrix context — what an Impact of <span className="text-stone-900 font-semibold">{impactScore}</span> means for each tagged goal
      </div>
      <div className="grid grid-cols-1 gap-2">
        {goals.map(goalName => {
          const def = GOAL_IMPACT_DESCRIPTORS[goalName];
          if (!def) return null;
          return (
            <div key={goalName} className="bg-white border border-stone-200 rounded-md p-3">
              <div className="flex items-baseline gap-2 mb-2">
                <div className="font-serif text-sm text-stone-900">{goalName}</div>
                <div className="text-[10px] text-stone-500 italic">{def.object}</div>
              </div>
              <div className="space-y-2">
                {def.themes.map(t => (
                  <div key={t.theme} className="flex gap-2 text-xs leading-snug">
                    <div className="w-32 flex-shrink-0 text-stone-500 font-medium">{t.theme}</div>
                    <div className="flex-1 text-stone-800">{t.levels[impactScore]}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RiskDetailPanel({ risk, editMode, setEditMode, onClose, onSave, onArchive, onRestore, onClone, onMerge, saving, canEdit = true, directory = [], departments = DEPARTMENTS, functions = FUNCTIONS, goalsList = NYMR_GOALS, deptsList = IMPACTED_DEPARTMENTS, implTitles = [] }) {
  const [draft, setDraft] = useState(risk);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [showMatrixViewer, setShowMatrixViewer] = useState(false);

  useEffect(() => { setDraft(risk); }, [risk]);
  useEffect(() => {
    if (editMode) {
      // Implementation of action defaults to the owner's name when the risk
      // has none recorded (the user can pick another person or role title).
      setDraft(d => {
        const hasImpl = !!(d.impl || '').trim();
        return { ...d, s: (d.L || 0) * (d.I || 0), rs: (d.rL || 0) * (d.rI || 0), impl: hasImpl ? d.impl : (d.owner || ''), implUpn: hasImpl ? d.implUpn : (d.ownerUpn || null) };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.L, draft.I, draft.rL, draft.rI, editMode]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"/>
      <div onClick={e => e.stopPropagation()} className="relative w-full max-w-3xl bg-white shadow-2xl flex flex-col animate-slide-in">
        <div className="px-6 py-4 border-b border-stone-200 flex items-start gap-3">
          <div className={`w-12 h-12 rounded ${scoreColor(draft.s)} flex flex-col items-center justify-center font-bold flex-shrink-0`}>
            <div className="text-lg leading-none">{draft.s || '—'}</div>
            <div className="text-[8px] uppercase tracking-wider opacity-80">{scoreLabel(draft.s)}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <code className="text-[11px] font-mono bg-stone-100 px-1.5 py-0.5 rounded text-stone-700">#{draft.serial}</code>
              <span className="text-[10px] uppercase tracking-wider text-stone-500">{draft.dept} · {draft.tier}</span>
              {draft.is_archived && <span className="text-[10px] uppercase font-semibold tracking-wider bg-stone-200 text-stone-700 px-1.5 py-0.5 rounded">Archived</span>}
              <ImpactRagBadges goalsCount={(draft.goals||[]).length} deptsCount={(draft.impacted_departments||[]).length} size="sm"/>
            </div>
            <h3 className="font-serif text-lg text-stone-900 leading-tight">Risk Detail</h3>
            {draft.source_register && (
              <div className="text-[10px] text-stone-400 mt-1">Source: {draft.source_register}{draft.source_serial ? ` · ${draft.source_serial}` : ''}</div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!editMode && !draft.is_archived && canEdit && <button onClick={() => setEditMode(true)} className="p-2 hover:bg-stone-100 rounded" title="Edit"><Edit3 className="w-4 h-4 text-stone-600"/></button>}
            {!editMode && !draft.is_archived && canEdit && onClone && <button onClick={() => onClone(draft)} className="p-2 hover:bg-stone-100 rounded" title="Clone this risk"><Copy className="w-4 h-4 text-stone-600"/></button>}
            {!editMode && !draft.is_archived && canEdit && onMerge && <button onClick={() => onMerge(draft)} className="p-2 hover:bg-stone-100 rounded" title="Merge other risks into this one"><GitMerge className="w-4 h-4 text-stone-600"/></button>}
            {!editMode && !draft.is_archived && canEdit && onArchive && <button onClick={() => setConfirmArchive(true)} className="p-2 hover:bg-amber-50 rounded" title="Archive risk"><Lock className="w-4 h-4 text-amber-700"/></button>}
            {!editMode && draft.is_archived && canEdit && onRestore && <button onClick={() => onRestore(draft.id)} className="p-2 hover:bg-emerald-50 rounded text-xs text-emerald-700 font-medium px-2" title="Restore from archive" disabled={saving}>Restore</button>}
            {!editMode && <button onClick={() => navigator.clipboard?.writeText(`${window.location.origin}${window.location.pathname}#risk-${draft.id}`)} className="p-2 hover:bg-stone-100 rounded" title="Copy link"><LinkIcon className="w-4 h-4 text-stone-600"/></button>}
            <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded"><X className="w-4 h-4 text-stone-600"/></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {editMode ? (
            <div className="p-6 space-y-5">
              <Field label="Risk Description">
                <textarea value={draft.desc} onChange={e => setDraft({ ...draft, desc: e.target.value })} rows={4} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Function/Category">
                  <select value={draft.fn || ''} onChange={e => setDraft({ ...draft, fn: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                    <option value="">—</option>
                    {functions.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </Field>
                <Field label="Department">
                  <select value={draft.dept} onChange={e => setDraft({ ...draft, dept: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Owner">
                  <PersonPicker
                    value={draft.ownerUpn}
                    directory={directory}
                    onChange={({ upn, displayName }) => setDraft(d => {
                      // Implementation follows the owner until it has been set to something else.
                      const follow = !(d.impl || '').trim() || (d.impl || '').trim() === (d.owner || '').trim();
                      return { ...d, ownerUpn: upn, owner: displayName, impl: follow ? displayName : d.impl, implUpn: follow ? upn : d.implUpn };
                    })}
                  />
                </Field>
                <Field label="Reviewer">
                  <PersonPicker
                    value={draft.reviewerUpn}
                    directory={directory}
                    onChange={({ upn, displayName }) => setDraft({ ...draft, reviewerUpn: upn, reviewer: displayName })}
                  />
                </Field>
                <Field label="Implementation of Action">
                  <ImplementationPicker
                    value={draft.impl}
                    valueUpn={draft.implUpn}
                    directory={directory}
                    titles={implTitles}
                    onChange={({ text, upn }) => setDraft({ ...draft, impl: text, implUpn: upn })}
                  />
                  {draft.impl && draft.implName && draft.implName !== draft.impl && (
                    <div className="text-[10px] text-stone-500 mt-1">Held by {draft.implName}</div>
                  )}
                </Field>
                <Field label="Tier">
                  <select value={draft.tier} onChange={e => setDraft({ ...draft, tier: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                    {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Target Date">
                  <TargetDatePicker
                    value={draft.td}
                    status={draft.tStatus}
                    residualScore={draft.rs}
                    onChange={({ td, tStatus }) => setDraft({ ...draft, td, tStatus })}
                  />
                </Field>
              </div>

              <div className="flex items-center justify-between bg-stone-100 border border-stone-200 rounded-lg px-4 py-2">
                <div className="text-xs text-stone-600">
                  <span className="text-stone-900 font-medium">Need help choosing a score?</span> Open the matrix to compare impact levels across all goals.
                </div>
                <button
                  type="button"
                  onClick={() => setShowMatrixViewer(true)}
                  className="text-xs px-3 py-1.5 bg-white border border-stone-300 hover:border-amber-600 hover:bg-amber-50 rounded-md flex items-center gap-1.5 text-stone-700 font-medium"
                >
                  <LayoutGrid className="w-3.5 h-3.5"/>
                  Risk Matrix Viewer
                </button>
              </div>

              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mb-3">Initial Risk (before mitigation)</div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Likelihood (1-5)"><ScoreSelector value={draft.L} onChange={v => setDraft({ ...draft, L: v })}/></Field>
                  <Field label="Impact (1-5)"><ScoreSelector value={draft.I} onChange={v => setDraft({ ...draft, I: v })}/></Field>
                  <Field label="Score (auto)">
                    <div className={`text-center px-3 py-2 rounded-md font-bold text-xl ${scoreColor(draft.s)}`}>{draft.s || 0}</div>
                  </Field>
                </div>
              </div>

              {/* Impact Profile editor — Phase 7 Batch C */}
              <ImpactTaxonomyEditor
                goals={draft.goals || []}
                impactedDepartments={draft.impacted_departments || []}
                onChange={({ goals, impacted_departments }) =>
                  setDraft(d => ({ ...d, goals, impacted_departments }))
                }
                editable
                goalsList={goalsList}
                deptsList={deptsList}
              />

              {(draft.goals?.length > 0) && draft.I && (
                <ImpactMatrixContext goals={draft.goals} impactScore={draft.I}/>
              )}

              <Field label="Controls / Mitigations Already in Place">
                <textarea value={draft.ctrl} onChange={e => setDraft({ ...draft, ctrl: e.target.value })} rows={4} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/>
              </Field>
              <Field label="Gaps in Controls / Required Actions">
                <textarea value={draft.gaps} onChange={e => setDraft({ ...draft, gaps: e.target.value })} rows={4} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/>
              </Field>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-medium mb-3">Residual Risk (after mitigation)</div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Likelihood"><ScoreSelector value={draft.rL} onChange={v => setDraft({ ...draft, rL: v })}/></Field>
                  <Field label="Impact"><ScoreSelector value={draft.rI} onChange={v => setDraft({ ...draft, rI: v })}/></Field>
                  <Field label="Score (auto)">
                    <div className={`text-center px-3 py-2 rounded-md font-bold text-xl ${scoreColor(draft.rs)}`}>{draft.rs || 0}</div>
                  </Field>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-5">
              <Section label="Risk Description"><p className="text-sm text-stone-800 leading-relaxed whitespace-pre-line">{draft.desc || '—'}</p></Section>
              <div className="grid grid-cols-2 gap-4">
                <Section label="Function"><p className="text-sm text-stone-800">{draft.fn || '—'}</p></Section>
                <Section label="Department"><p className="text-sm text-stone-800">{draft.dept || '—'}</p></Section>
                <Section label="Owner"><p className="text-sm text-stone-800 flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-stone-500"/>{draft.owner || '—'}</p>{draft.ownerUpn && <div className="text-[10px] text-stone-500 mt-0.5 ml-5">{draft.ownerUpn}</div>}</Section>
                <Section label="Reviewer"><p className="text-sm text-stone-800 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5 text-stone-500"/>{draft.reviewer || '—'}</p>{draft.reviewerUpn && <div className="text-[10px] text-stone-500 mt-0.5 ml-5">{draft.reviewerUpn}</div>}</Section>
                <Section label="Implementation of Action">
                  <p className="text-sm text-stone-800 flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5 text-stone-500"/>{draft.impl || '—'}</p>
                  {draft.implUpn && <div className="text-[10px] text-stone-500 mt-0.5 ml-5">{draft.implName && draft.implName !== draft.impl ? `${draft.implName} · ` : ''}{draft.implUpn}</div>}
                </Section>
                <Section label="Tier"><p className="text-sm text-stone-800">{draft.tier}</p></Section>
                <Section label="Target Date">
                  <p className={`text-sm flex items-center gap-1.5 flex-wrap ${isOverdue(draft.td) ? 'text-red-700 font-medium' : 'text-stone-800'}`}>
                    <Calendar className="w-3.5 h-3.5"/>
                    {draft.tStatus === 'ongoing_mitigated' ? 'Ongoing & Mitigated' : fmtDate(draft.td)}
                    {isOverdue(draft.td) && <span className="text-[10px] uppercase font-semibold tracking-wider bg-red-100 text-red-700 px-1.5 py-0.5 rounded ml-1">Overdue</span>}
                    {draft.tStatus === 'ongoing_mitigated' && <span className="text-[10px] uppercase font-semibold tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded ml-1">Mitigated</span>}
                  </p>
                </Section>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Initial Score</div>
                  <div className={`text-center inline-flex flex-col items-center justify-center w-20 h-20 rounded ${scoreColor(draft.s)} font-bold`}>
                    <div className="text-2xl leading-none">{draft.s || '—'}</div>
                    <div className="text-[10px] uppercase tracking-wider opacity-80">{scoreLabel(draft.s)}</div>
                  </div>
                  <div className="text-xs text-stone-600 mt-2">L {draft.L || '—'} × I {draft.I || '—'}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-2">Residual Score</div>
                  <div className={`text-center inline-flex flex-col items-center justify-center w-20 h-20 rounded ${scoreColor(draft.rs)} font-bold`}>
                    <div className="text-2xl leading-none">{draft.rs || '—'}</div>
                    <div className="text-[10px] uppercase tracking-wider opacity-80">{scoreLabel(draft.rs)}</div>
                  </div>
                  <div className="text-xs text-emerald-700 mt-2">L {draft.rL || '—'} × I {draft.rI || '—'}</div>
                </div>
              </div>

              {/* Impact Profile (read-only view) — Phase 7 Batch C */}
              <ImpactTaxonomyEditor
                goals={draft.goals || []}
                impactedDepartments={draft.impacted_departments || []}
                editable={false}
                goalsList={goalsList}
                deptsList={deptsList}
              />

              {(draft.goals?.length > 0) && draft.I && (
                <ImpactMatrixContext goals={draft.goals} impactScore={draft.I}/>
              )}

              <Section label="Controls / Mitigations in Place">
                <p className="text-sm text-stone-800 leading-relaxed bg-stone-50 border-l-2 border-emerald-500 pl-3 py-2 whitespace-pre-line">{draft.ctrl || '—'}</p>
              </Section>
              <Section label="Gaps in Controls / Required Actions">
                <p className="text-sm text-stone-800 leading-relaxed bg-stone-50 border-l-2 border-amber-500 pl-3 py-2 whitespace-pre-line">{draft.gaps || '—'}</p>
              </Section>

              <Section label="Status Updates / Journal">
                <RiskJournal riskId={draft.id} canEdit={canEdit && !draft.is_archived} reviewerUpn={draft.reviewerUpn}/>
              </Section>

              <Section label="Amendment Record / Audit Notes">
                <RiskAuditNotes riskId={draft.id} canEdit={canEdit && !draft.is_archived}/>
              </Section>
            </div>
          )}
        </div>

        {editMode && (
          <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center gap-2">
            <div className="text-xs text-stone-500 mr-auto">Changes will be logged to the audit trail</div>
            {onArchive && !draft.is_archived && (
              <button onClick={() => setConfirmArchive(true)} disabled={saving} className="text-sm px-3 py-1.5 border border-amber-300 text-amber-800 hover:bg-amber-50 rounded-md flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5"/>Archive
              </button>
            )}
            <button onClick={() => { setDraft(risk); setEditMode(false); }} className="text-sm px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md" disabled={saving}>Cancel</button>
            <button onClick={() => onSave(draft, risk)} disabled={saving} className="text-sm px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5 hover:bg-stone-800 disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}

        {confirmArchive && (
          <div className="absolute inset-0 bg-stone-900/40 flex items-center justify-center p-6 z-10" onClick={() => setConfirmArchive(false)}>
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center flex-shrink-0"><Lock className="w-5 h-5 text-amber-700"/></div>
                <div>
                  <h3 className="font-serif text-lg text-stone-900">Archive risk #{draft.serial}?</h3>
                  <p className="text-sm text-stone-600 mt-1">It will move to the Archive view and stop contributing to active scores, dashboards, and reports. You can restore it later. The action is logged to the audit trail.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => setConfirmArchive(false)} className="text-sm px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md" disabled={saving}>Cancel</button>
                <button onClick={() => { setConfirmArchive(false); onArchive(draft.id); }} className="text-sm px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center gap-1.5" disabled={saving}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Lock className="w-3.5 h-3.5"/>}
                  Archive
                </button>
              </div>
            </div>
          </div>
        )}
        {showMatrixViewer && <RiskMatrixViewerModal onClose={() => setShowMatrixViewer(false)}/>}
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mb-1.5">{label}</div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mb-1.5">{label}</div>
      {children}
    </div>
  );
}
// Risk journal: list + post status updates for a risk. Self-contained
// component fetched lazily when shown. Posts go through POST
// /api/risks/{id}/status-updates which is INSERT-only on the server side
// so once posted an entry cannot be edited or deleted.
function RiskJournal({ riskId, canEdit, reviewerUpn, onUpdated }) {
  const [updates, setUpdates] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [content, setContent] = useState('');
  const [notify, setNotify] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/risks/${riskId}/status-updates`);
      setUpdates(data.updates || []);
    } catch (e) {
      setError(e.message);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  }, [riskId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!content.trim()) return;
    setPosting(true);
    setError(null);
    try {
      await apiFetch(`/risks/${riskId}/status-updates`, {
        method: 'POST',
        body: JSON.stringify({ content: content.trim(), notify: notify && !!reviewerUpn })
      });
      setContent('');
      setNotify(false);
      await load();
      onUpdated?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="bg-white border border-stone-200 rounded-lg p-3">
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Add a status update or progress note… (e.g. 'Contractor confirmed 2026-05-12 delivery')"
            rows={2}
            className="w-full text-sm px-2 py-1.5 border-0 focus:outline-none resize-none"
          />
          <div className="flex items-center gap-2 mt-1">
            <div className="text-[10px] text-stone-500">Once posted, an update can’t be edited or deleted — the journal is immutable.</div>
            <button
              type="button"
              onClick={() => reviewerUpn && setNotify(v => !v)}
              disabled={!reviewerUpn}
              title={reviewerUpn ? `Tick to send a notification to the reviewer (${reviewerUpn})` : 'No reviewer set on this risk - notification disabled.'}
              className={`text-xs px-2.5 py-1 rounded-md border transition-colors flex items-center gap-1.5 ${
                !reviewerUpn ? 'bg-stone-50 text-stone-400 border-stone-200 cursor-not-allowed'
                  : notify ? 'bg-amber-100 text-amber-800 border-amber-300'
                  : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
              }`}
            >
              <Bell className="w-3 h-3"/>
              {notify ? 'Notify reviewer ✓' : 'Notify reviewer'}
            </button>
            <button
              onClick={submit}
              disabled={posting || !content.trim()}
              className="ml-auto text-xs px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5 hover:bg-stone-800 disabled:opacity-50"
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
              Post update
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>
      )}

      {loading && updates === null ? (
        <div className="text-xs text-stone-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin"/>Loading journal…</div>
      ) : (updates || []).length === 0 ? (
        <div className="text-xs text-stone-500 italic px-1">No status updates yet.</div>
      ) : (
        <div className="space-y-2">
          {updates.map(u => (
            <div key={u.id} className="bg-white border border-stone-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1 text-[11px] text-stone-500">
                <User className="w-3 h-3"/>
                <span className="text-stone-700 font-medium">{u.created_by_name || u.created_by_upn}</span>
                <span>·</span>
                <span>{new Date(u.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="text-sm text-stone-800 whitespace-pre-line leading-snug">{u.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Risk audit notes: list + post discussion-level audit_log entries for a risk.
// Modern equivalent of the corporate-register Amendment record tab.
// Reads via GET /api/audit?risk_id={id}, posts via POST /api/risks/{id}/audit-note.
// Once posted, an entry cannot be edited or deleted (audit_log is append-only).
function RiskAuditNotes({ riskId, canEdit }) {
  const AUDIT_NOTE_TYPES = ['Change', 'Pending', 'Comment', 'New'];
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [changeType, setChangeType] = useState('Change');
  const [outline, setOutline] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/audit?risk_id=${riskId}&limit=500`);
      setEntries(data.entries || []);
    } catch (e) {
      setError(e.message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [riskId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!outline.trim()) return;
    setPosting(true);
    setError(null);
    try {
      await apiFetch(`/risks/${riskId}/audit-note`, {
        method: 'POST',
        body: JSON.stringify({
          change_type: changeType,
          outline: outline.trim(),
          action_taken: actionTaken.trim() || null
        })
      });
      setOutline('');
      setActionTaken('');
      setChangeType('Change');
      setShowForm(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-3">
      {canEdit && !showForm && (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="text-xs px-3 py-1.5 border border-stone-300 hover:border-stone-500 hover:bg-stone-50 rounded-md flex items-center gap-1.5 text-stone-700"
        >
          <Plus className="w-3.5 h-3.5"/>Add audit note
        </button>
      )}

      {canEdit && showForm && (
        <div className="bg-white border border-stone-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium">Type</div>
            <div className="flex gap-1">
              {AUDIT_NOTE_TYPES.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setChangeType(t)}
                  className={`text-[11px] uppercase font-semibold tracking-wider px-2 py-1 rounded-full border transition ${
                    changeType === t
                      ? (t === 'New' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                        : t === 'Pending' ? 'bg-amber-100 text-amber-800 border-amber-300'
                        : t === 'Comment' ? 'bg-stone-200 text-stone-800 border-stone-400'
                        : 'bg-stone-900 text-stone-50 border-stone-900')
                      : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mb-1">Outline <span className="text-red-500">*</span></div>
            <textarea
              value={outline}
              onChange={e => setOutline(e.target.value)}
              placeholder="What was raised or decided… (e.g. 'CEO suggested wording change to reflect cost-of-living impact')"
              rows={2}
              maxLength={500}
              className="w-full text-sm px-2 py-1.5 border border-stone-200 rounded focus:outline-none focus:border-amber-600 resize-none"
            />
            <div className="text-[10px] text-stone-400 text-right">{outline.length}/500</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mb-1">Action Taken</div>
            <textarea
              value={actionTaken}
              onChange={e => setActionTaken(e.target.value)}
              placeholder="What was done in response… (e.g. 'Risk reworded; reallocated to CEO')"
              rows={2}
              className="w-full text-sm px-2 py-1.5 border border-stone-200 rounded focus:outline-none focus:border-amber-600 resize-none"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <div className="text-[10px] text-stone-500">Once posted, entries cannot be edited or deleted — audit log is immutable.</div>
            <button
              type="button"
              onClick={() => { setShowForm(false); setOutline(''); setActionTaken(''); setChangeType('Change'); setError(null); }}
              className="ml-auto text-xs px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md"
              disabled={posting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={posting || !outline.trim()}
              className="text-xs px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5 hover:bg-stone-800 disabled:opacity-50"
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
              Post note
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>
      )}

      {loading && entries === null ? (
        <div className="text-xs text-stone-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin"/>Loading audit notes…</div>
      ) : (entries || []).length === 0 ? (
        <div className="text-xs text-stone-500 italic px-1">No audit entries for this risk yet.</div>
      ) : (
        <div className="space-y-2">
          {entries.map(a => (
            <div key={a.id} className="bg-white border border-stone-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1 text-[11px] text-stone-500 flex-wrap">
                <span className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full ${
                  a.change_type === 'New' ? 'bg-emerald-100 text-emerald-700'
                    : a.change_type === 'Pending' ? 'bg-amber-100 text-amber-700'
                    : a.change_type === 'Archive' || a.change_type === 'Restore' ? 'bg-orange-100 text-orange-700'
                    : 'bg-stone-100 text-stone-700'
                }`}>{a.change_type}</span>
                <User className="w-3 h-3"/>
                <span className="text-stone-700 font-medium">{a.user_name || a.user_upn}</span>
                <span>·</span>
                <span>{a.changed_at ? new Date(a.changed_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
              </div>
              {a.outline && <div className="text-sm text-stone-800 whitespace-pre-line leading-snug">{a.outline}</div>}
              {a.action_taken && (
                <div className="mt-1.5 pt-1.5 border-t border-stone-100">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mb-0.5">Action Taken</div>
                  <div className="text-xs text-stone-700 whitespace-pre-line leading-snug">{a.action_taken}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreSelector({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={`flex-1 py-2 rounded text-sm font-semibold border transition ${value === n ? 'bg-stone-900 text-stone-50 border-stone-900' : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'}`}
          type="button"
        >
          {n}
        </button>
      ))}
    </div>
  );
}

// Owner / Reviewer dropdown sourced from /api/directory (active users only).
// onChange receives { upn, displayName }; clearing the select sends {upn:null,displayName:''}.
/** "Implementation of action" — who carries out the mitigation. Stored as text
 *  (dbo.risks.implementation_text, column S of the corporate register) so the
 *  legacy role titles ("Trust Board", "MPD Manager", "CEO"…) keep working
 *  alongside named system users. Two groups: people from the directory, then a
 *  divider, then every title already in use. A stored value that matches
 *  neither is kept selectable under "Current value" so it is never lost. */
function ImplementationPicker({ value, valueUpn, onChange, directory = [], titles = [] }) {
  const nameOf = (u) => (u.display_name || `${u.first_name || ''} ${u.last_name || ''}`).trim();
  const people = directory.filter(u => u.upn && nameOf(u));
  const peopleNames = new Set(people.map(u => nameOf(u).toLowerCase()));
  // Titles = rows from /api/lookups ({ title, active_count, holder_name, is_person_name }) or plain strings.
  const rows = titles.map(t => (typeof t === 'string' ? { title: t } : t))
    .filter(t => t.title && !t.is_person_name && !peopleNames.has(t.title.toLowerCase()));
  const current = (value || '').trim();
  const curUpn = (valueUpn || '').toLowerCase();

  // Work out which option represents the stored value.
  const personByUpn = curUpn ? people.find(u => u.upn.toLowerCase() === curUpn) : null;
  const personByName = !personByUpn && current ? people.find(u => nameOf(u).toLowerCase() === current.toLowerCase()) : null;
  const person = personByUpn || personByName;
  const titleRow = !person && current ? rows.find(t => t.title.toLowerCase() === current.toLowerCase()) : null;
  const selected = person ? `p:${person.upn}` : titleRow ? `t:${titleRow.title}` : current ? `c:${current}` : '';

  const handle = (e) => {
    const v = e.target.value;
    if (!v) return onChange({ text: '', upn: null });
    if (v.startsWith('p:')) {
      const u = people.find(x => x.upn === v.slice(2));
      return onChange({ text: u ? nameOf(u) : '', upn: u ? u.upn : null });
    }
    if (v.startsWith('t:')) return onChange({ text: v.slice(2), upn: undefined }); // server resolves the title's holder
    return onChange({ text: v.slice(2), upn: undefined });
  };

  return (
    <select
      value={selected}
      onChange={handle}
      className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"
    >
      <option value="">— None —</option>
      {selected.startsWith('c:') && <optgroup label="Current value"><option value={selected}>{current}</option></optgroup>}
      <optgroup label="People (system users)">
        {people.map(u => <option key={`p-${u.upn}`} value={`p:${u.upn}`}>{nameOf(u)}{u.department ? ` — ${u.department}` : ''}</option>)}
      </optgroup>
      {rows.length > 0 && (
        <optgroup label="Roles / titles already in use">
          {rows.map(t => (
            <option key={`t-${t.title}`} value={`t:${t.title}`}>
              {t.title}
              {t.holder_name ? ` — ${t.holder_name}` : ''}
              {t.active_count != null ? ` (${t.active_count})` : ''}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

function PersonPicker({ value, onChange, directory = [], placeholder = '— None —' }) {
  return (
    <select
      value={value || ''}
      onChange={e => {
        const upn = e.target.value || null;
        const u = directory.find(x => x.upn === upn);
        onChange({
          upn,
          displayName: u ? (u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim()) : ''
        });
      }}
      className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"
    >
      <option value="">{placeholder}</option>
      {directory.map(u => (
        <option key={u.upn} value={u.upn}>
          {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim()}{u.department ? ` — ${u.department}` : ''}
        </option>
      ))}
    </select>
  );
}

// Target Date picker: HTML5 date input + an "Ongoing" toggle pill.
// When ongoing, the date input is disabled and shows blank. Clicking the pill
// flips between modes; entering a date when ongoing flips back automatically.
function ConcentrationColumn({ label, rows, maxOverall, onRowClick }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mb-2">{label}</div>
      <div className="space-y-1.5">
        {rows.map(row => {
          const widthPct = (row.total / maxOverall) * 100;
          const critPct = row.total > 0 ? (row.critical / row.total) * 100 : 0;
          const empty = row.total === 0;
          return (
            <button
              key={row.name}
              type="button"
              onClick={() => !empty && onRowClick?.(row.name)}
              disabled={empty}
              className={`w-full text-left flex items-center gap-3 px-1 py-1.5 rounded transition-colors ${empty ? 'opacity-50 cursor-default' : 'hover:bg-stone-50 cursor-pointer'}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs text-stone-800 truncate mb-1">{row.name}</div>
                <div className="h-2 bg-stone-100 rounded-sm overflow-hidden flex" style={{ width: `${Math.max(2, widthPct)}%` }}>
                  {row.critical > 0 && <div className="bg-red-600 h-full" style={{ width: `${critPct}%` }}/>}
                  {row.high > 0 && <div className="bg-orange-500 h-full" style={{ width: `${100 - critPct}%` }}/>}
                </div>
              </div>
              <div className="text-right flex-shrink-0 w-16">
                {empty ? (
                  <span className="text-[10px] text-stone-400 italic">none</span>
                ) : (
                  <>
                    <span className="font-serif text-base text-stone-900">{row.total}</span>
                    <span className="text-[10px] text-stone-500 ml-1">{row.critical}c &middot; {row.high}h</span>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TargetDatePicker({ value, status, residualScore, onChange }) {
  const today = new Date().toISOString().slice(0, 10);
  // effective status: prefer explicit `status`, otherwise infer from value
  const eff = status || (value && value !== 'Ongoing' ? 'date' : 'ongoing');
  const mitigatedAllowed = residualScore != null && residualScore >= 1 && residualScore <= 2;

  const setStatus = (next) => {
    if (next === 'date') {
      const td = value && value !== 'Ongoing' ? value : today;
      onChange({ td, tStatus: 'date' });
    } else if (next === 'ongoing') {
      onChange({ td: 'Ongoing', tStatus: 'ongoing' });
    } else if (next === 'ongoing_mitigated') {
      if (!mitigatedAllowed) return;
      onChange({ td: 'Ongoing', tStatus: 'ongoing_mitigated' });
    }
  };

  const pillBase = 'text-xs px-3 py-1.5 rounded-md border transition-colors whitespace-nowrap';
  const pill = (active, allowed = true, accent = 'stone') => {
    if (active) {
      return accent === 'emerald'
        ? `${pillBase} bg-emerald-700 text-emerald-50 border-emerald-700`
        : `${pillBase} bg-stone-900 text-stone-50 border-stone-900`;
    }
    if (!allowed) return `${pillBase} bg-stone-100 text-stone-400 border-stone-200 cursor-not-allowed`;
    return accent === 'emerald'
      ? `${pillBase} bg-white text-stone-700 border-stone-200 hover:border-emerald-500 hover:text-emerald-700`
      : `${pillBase} bg-white text-stone-700 border-stone-200 hover:border-stone-400`;
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setStatus('date')} className={pill(eff === 'date')}>
          On a date
        </button>
        <button type="button" onClick={() => setStatus('ongoing')} className={pill(eff === 'ongoing')}>
          Ongoing
        </button>
        <button
          type="button"
          onClick={() => setStatus('ongoing_mitigated')}
          disabled={!mitigatedAllowed && eff !== 'ongoing_mitigated'}
          title={mitigatedAllowed
            ? 'Risk is rolling and the residual is acceptable.'
            : 'Available only when residual score is 1 or 2.'}
          className={pill(eff === 'ongoing_mitigated', mitigatedAllowed || eff === 'ongoing_mitigated', 'emerald')}
        >
          <CheckCircle2 className="w-3 h-3 inline -mt-px mr-1"/>Ongoing & Mitigated
        </button>
      </div>
      {eff === 'date' && (
        <input
          type="date"
          value={value && value !== 'Ongoing' ? value : ''}
          onChange={e => onChange({ td: e.target.value || 'Ongoing', tStatus: e.target.value ? 'date' : 'ongoing' })}
          className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"
        />
      )}
      {eff === 'ongoing_mitigated' && mitigatedAllowed && (
        <div className="text-[11px] text-emerald-700 italic flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3"/> Residual {residualScore} - risk is contained.
        </div>
      )}
      {eff === 'ongoing_mitigated' && !mitigatedAllowed && (
        <div className="text-[11px] text-red-700 italic flex items-center gap-1">
          <AlertTriangle className="w-3 h-3"/> Residual {residualScore || 'not set'} no longer qualifies as mitigated. Switch to Ongoing or reassess.
        </div>
      )}
    </div>
  );
}

// ============================================================
// ADD RISK MODAL
// ============================================================
function MergeRisksModal({ target, allRisks, currentUser, onClose, onMerge, saving }) {
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');

  // Candidate pool: my active risks (where I'm owner or reviewer), excluding
  // the target itself. Archived risks are not eligible because the merge
  // workflow archives sources at the end.
  const candidates = useMemo(() => {
    if (!target) return [];
    const mine = (allRisks || []).filter(r =>
      !r.is_archived
      && r.id !== target.id
      && (r.ownerUpn === currentUser.upn || r.reviewerUpn === currentUser.upn)
    );
    if (!search.trim()) return mine;
    const q = search.toLowerCase();
    return mine.filter(r => `${r.serial} ${r.desc} ${r.dept} ${r.fn}`.toLowerCase().includes(q));
  }, [target, allRisks, currentUser, search]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(candidates.map(r => r.id)));
  const clearAll = () => setSelected(new Set());

  // Pre-compute the projected new scores so the user sees the impact of
  // the merge before clicking. Mirrors sp_merge_risks's MAX logic.
  const projected = useMemo(() => {
    if (!target) return null;
    const all = [target, ...candidates.filter(r => selected.has(r.id))];
    return {
      L: Math.max(...all.map(r => r.L || 0)),
      I: Math.max(...all.map(r => r.I || 0)),
      rL: Math.max(...all.map(r => r.rL || 0)),
      rI: Math.max(...all.map(r => r.rI || 0))
    };
  }, [target, candidates, selected]);

  const projS = projected ? projected.L * projected.I : 0;
  const projRs = projected ? projected.rL * projected.rI : 0;

  if (!target) return null;

  const handleSubmit = () => {
    if (selected.size === 0) return;
    onMerge(target.id, Array.from(selected));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"/>
      <div onClick={e => e.stopPropagation()} className="relative w-full max-w-3xl bg-white rounded-lg shadow-2xl flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-stone-200 flex items-start gap-3">
          <div className="w-10 h-10 rounded bg-stone-900 text-amber-400 flex items-center justify-center flex-shrink-0">
            <GitMerge className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Risk Register / Merge</div>
            <h3 className="font-serif text-xl text-stone-900">Merge into #{target.serial}</h3>
            <div className="text-xs text-stone-500 mt-0.5 line-clamp-1">{target.desc}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded"><X className="w-4 h-4 text-stone-600"/></button>
        </div>

        <div className="px-6 py-3 border-b border-stone-100 bg-amber-50/50">
          <div className="text-xs text-stone-700 leading-relaxed">
            <strong>What merge does:</strong> Each selected source gets archived. Their goals and impacted departments get unioned onto this risk. The likelihood and impact (initial and residual) take the highest value across this risk plus all sources. A "MERGED DATA <em>datetime</em>" entry is added to this risk's journal containing the source serials and descriptions. <strong>This risk's description is not changed.</strong>
          </div>
        </div>

        <div className="px-6 py-3 border-b border-stone-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"/>
            <input
              type="text"
              placeholder="Filter my risks…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-stone-50 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600 focus:bg-white"
            />
          </div>
          <div className="text-xs text-stone-500 whitespace-nowrap">{candidates.length} candidate{candidates.length === 1 ? '' : 's'} · {selected.size} selected</div>
          <button onClick={selectAll} className="text-xs text-amber-700 hover:text-amber-800 font-medium">Select all</button>
          <button onClick={clearAll} className="text-xs text-stone-500 hover:text-stone-700">Clear</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {candidates.length === 0 ? (
            <div className="p-12 text-center text-sm text-stone-500">
              No candidate risks. Merge requires risks where you are owner or reviewer (excluding archived and this risk).
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {candidates.map(r => {
                const isSelected = selected.has(r.id);
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => toggle(r.id)}
                    className={`w-full text-left px-6 py-3 flex items-start gap-3 hover:bg-stone-50 transition-colors ${isSelected ? 'bg-amber-50/60' : ''}`}
                  >
                    <div className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'bg-amber-600 border-amber-600' : 'bg-white border-stone-300'
                    }`}>
                      {isSelected && <CheckCircle2 className="w-3 h-3 text-white"/>}
                    </div>
                    <div className={`w-9 h-9 rounded ${scoreColor(r.s)} flex items-center justify-center font-bold text-xs flex-shrink-0`}>{r.s || '—'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <code className="text-[10px] font-mono bg-stone-100 px-1.5 py-0.5 rounded text-stone-700">#{r.serial}</code>
                        <span className="text-[10px] uppercase tracking-wider text-stone-500">{r.dept}</span>
                        <span className="text-[10px] text-stone-400">·</span>
                        <span className="text-[10px] text-stone-500">{r.tier}</span>
                        {r.rs ? <span className="text-[10px] text-stone-500">· residual {r.rs}</span> : null}
                      </div>
                      <div className="text-xs text-stone-800 line-clamp-2 leading-snug">{r.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center gap-3 flex-wrap">
          {selected.size > 0 && (
            <div className="flex items-center gap-2 text-[11px] text-stone-700">
              <span className="uppercase tracking-widest text-stone-500">Projected:</span>
              <span>L {projected.L} × I {projected.I} = <span className={`px-1.5 py-0.5 rounded font-semibold ${scoreColor(projS)}`}>{projS}</span></span>
              <span className="text-stone-400">·</span>
              <span>residual <span className={`px-1.5 py-0.5 rounded font-semibold ${scoreColor(projRs)}`}>{projRs}</span></span>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} className="text-sm px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={selected.size === 0 || saving}
              className="text-sm px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5 hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <GitMerge className="w-3.5 h-3.5"/>}
              Merge {selected.size > 0 ? `${selected.size} risk${selected.size === 1 ? '' : 's'}` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddRiskModal({ cloneFrom, onClose, onCreate, currentUser, existingRisks = [], saving, directory = [], departments = DEPARTMENTS, functions = FUNCTIONS, goalsList = NYMR_GOALS, deptsList = IMPACTED_DEPARTMENTS, implTitles = [] }) {
  const suggested = useMemo(() => nextSerial(existingRisks), [existingRisks]);
  const isClone = !!cloneFrom;
  const [showMatrixViewer, setShowMatrixViewer] = useState(false);
  const [draft, setDraft] = useState(() => {
    if (cloneFrom) {
      // Inherit everything from the source EXCEPT serial (auto-suggested),
      // target date / status (must be reselected), and the description
      // gets a {cloned} marker so the user can see at a glance it's a copy.
      return {
        ...cloneFrom,
        id: undefined,
        serial: suggested,
        desc: ((cloneFrom.desc || '') + ' {cloned}').trim(),
        td: 'Ongoing',
        tStatus: 'ongoing',
        is_archived: false,
        // The source array refs are safe to keep but ensure they're real arrays:
        goals: Array.isArray(cloneFrom.goals) ? [...cloneFrom.goals] : [],
        impacted_departments: Array.isArray(cloneFrom.impacted_departments) ? [...cloneFrom.impacted_departments] : []
      };
    }
    return {
      serial: suggested,
      fn: 'Operation of railway',
      dept: currentUser.department || 'Corporate',
      desc: '',
      owner: currentUser.displayName || '',
      reviewer: '',
      // Implementation of action pre-fills with the owner (name + person).
      impl: currentUser.displayName || '',
      implUpn: currentUser.upn || null,
      ownerUpn: currentUser.upn || null,
      reviewerUpn: null,
      L: 3, I: 3, s: 9,
      rL: 3, rI: 3, rs: 9,
      ctrl: '', gaps: '', td: 'Ongoing', tStatus: 'ongoing',
      tier: 'PLC',
      goals: [],
      impacted_departments: []
    };
  });

  useEffect(() => {
    setDraft(d => ({ ...d, s: (d.L || 0) * (d.I || 0), rs: (d.rL || 0) * (d.rI || 0) }));
  }, [draft.L, draft.I, draft.rL, draft.rI]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"/>
      <div onClick={e => e.stopPropagation()} className="relative w-full max-w-3xl bg-white rounded-lg shadow-2xl flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{isClone ? `Cloning #${cloneFrom?.serial || ''}` : 'Risk Register'}</div>
            <h3 className="font-serif text-xl text-stone-900">{isClone ? 'Clone Risk' : 'Add New Risk'}</h3>
          </div>
          <button onClick={onClose} className="ml-auto p-2 hover:bg-stone-100 rounded"><X className="w-4 h-4 text-stone-600"/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Serial Number">
              <input value={draft.serial} onChange={e => setDraft({ ...draft, serial: e.target.value })} placeholder={`Suggested: ${suggested}`} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/>
              <div className="text-[10px] text-stone-500 mt-1">Auto-filled to the next sequential number. Override (e.g. “{suggested}A”) for sub-risks.</div>
            </Field>
            <Field label="Tier">
              <select value={draft.tier} onChange={e => setDraft({ ...draft, tier: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Department">
              <select value={draft.dept} onChange={e => setDraft({ ...draft, dept: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Risk Description">
            <textarea value={draft.desc} onChange={e => setDraft({ ...draft, desc: e.target.value })} rows={3} placeholder="Describe what the risk is..." className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Function"><select value={draft.fn} onChange={e => setDraft({ ...draft, fn: e.target.value })} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600">{functions.map(f => <option key={f} value={f}>{f}</option>)}</select></Field>
            <Field label="Target Date">
              <TargetDatePicker
                    value={draft.td}
                    status={draft.tStatus}
                    residualScore={draft.rs}
                    onChange={({ td, tStatus }) => setDraft({ ...draft, td, tStatus })}
                  />
            </Field>
            <Field label="Owner">
              <PersonPicker
                value={draft.ownerUpn}
                directory={directory}
                onChange={({ upn, displayName }) => setDraft(d => {
                  // Implementation follows the owner until it has been set to something else.
                  const follow = !(d.impl || '').trim() || (d.impl || '').trim() === (d.owner || '').trim();
                  return { ...d, ownerUpn: upn, owner: displayName, impl: follow ? displayName : d.impl, implUpn: follow ? upn : d.implUpn };
                })}
              />
            </Field>
            <Field label="Reviewer">
              <PersonPicker
                value={draft.reviewerUpn}
                directory={directory}
                onChange={({ upn, displayName }) => setDraft({ ...draft, reviewerUpn: upn, reviewer: displayName })}
              />
            </Field>
            <Field label="Implementation of Action">
              <ImplementationPicker
                value={draft.impl}
                valueUpn={draft.implUpn}
                directory={directory}
                titles={implTitles}
                onChange={({ text, upn }) => setDraft({ ...draft, impl: text, implUpn: upn })}
              />
              <div className="text-[10px] text-stone-500 mt-1">Who carries out the mitigation. Defaults to the owner; pick a named user or an existing role title.</div>
            </Field>
          </div>
          <div className="flex items-center justify-between bg-stone-100 border border-stone-200 rounded-lg px-4 py-2">
            <div className="text-xs text-stone-600">
              <span className="text-stone-900 font-medium">Need help choosing a score?</span> Open the matrix to compare impact levels across all goals.
            </div>
            <button
              type="button"
              onClick={() => setShowMatrixViewer(true)}
              className="text-xs px-3 py-1.5 bg-white border border-stone-300 hover:border-amber-600 hover:bg-amber-50 rounded-md flex items-center gap-1.5 text-stone-700 font-medium"
            >
              <LayoutGrid className="w-3.5 h-3.5"/>
              Risk Matrix Viewer
            </button>
          </div>
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-medium mb-3">Initial Risk Score</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Likelihood"><ScoreSelector value={draft.L} onChange={v => setDraft({ ...draft, L: v })}/></Field>
              <Field label="Impact"><ScoreSelector value={draft.I} onChange={v => setDraft({ ...draft, I: v })}/></Field>
              <Field label="Score"><div className={`text-center px-3 py-2 rounded-md font-bold text-xl ${scoreColor(draft.s)}`}>{draft.s}</div></Field>
            </div>
          </div>
          {/* Impact Profile editor — Phase 7 Batch C */}
          <ImpactTaxonomyEditor
            goals={draft.goals}
            impactedDepartments={draft.impacted_departments}
            onChange={({ goals, impacted_departments }) =>
              setDraft(d => ({ ...d, goals, impacted_departments }))
            }
            editable
            goalsList={goalsList}
            deptsList={deptsList}
          />
          {(draft.goals?.length > 0) && draft.I && (
            <ImpactMatrixContext goals={draft.goals} impactScore={draft.I}/>
          )}
          <Field label="Controls / Mitigations in Place"><textarea value={draft.ctrl} onChange={e => setDraft({ ...draft, ctrl: e.target.value })} rows={3} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/></Field>
          <Field label="Gaps in Controls / Required Actions"><textarea value={draft.gaps} onChange={e => setDraft({ ...draft, gaps: e.target.value })} rows={3} className="w-full text-sm px-3 py-2 border border-stone-200 rounded-md focus:outline-none focus:border-amber-600"/></Field>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-medium mb-3">Residual Risk Score (after mitigation)</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Likelihood"><ScoreSelector value={draft.rL} onChange={v => setDraft({ ...draft, rL: v })}/></Field>
              <Field label="Impact"><ScoreSelector value={draft.rI} onChange={v => setDraft({ ...draft, rI: v })}/></Field>
              <Field label="Score"><div className={`text-center px-3 py-2 rounded-md font-bold text-xl ${scoreColor(draft.rs)}`}>{draft.rs}</div></Field>
            </div>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center gap-2">
          <div className="text-xs text-stone-500 mr-auto">New risk will be logged to the audit trail</div>
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-stone-200 hover:bg-stone-100 rounded-md" disabled={saving}>Cancel</button>
          <button
            onClick={() => onCreate(draft)}
            disabled={!draft.serial || !draft.desc || saving}
            className="text-sm px-3 py-1.5 bg-stone-900 text-stone-50 rounded-md flex items-center gap-1.5 hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>}
            {saving ? 'Creating…' : 'Create Risk'}
          </button>
        </div>
        {showMatrixViewer && <RiskMatrixViewerModal onClose={() => setShowMatrixViewer(false)}/>}
      </div>
    </div>
  );
}
